import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { SvgComponent, logoConfig, Header, InlineButton, DropdownList, FloatingButton, IconButton, BottomSheet, SelectableListMenu, Modal, Wrapper, Spinner, Tooltip2, Theme, AccessGuard, ToggleButtonGroup } from '@platform/ui';
import cn from 'classnames';
import * as React from 'react';
import React__default, { useState, useEffect, useRef, createContext, useContext, useMemo, useCallback, Fragment as Fragment$1, Children, memo, isValidElement, createRef } from 'react';
import axios from 'axios';
import { useDispatch, useSelector, ReactReduxContext, Provider } from 'react-redux';
import { __awaiter, __rest } from 'tslib';
import { createApi } from '@reduxjs/toolkit/query/react';
import { createSelector } from 'reselect';
import { createSlice, createSelector as createSelector$1, configureStore, createDraftSafeSelector } from '@reduxjs/toolkit';
import { createPortal } from 'react-dom';

function styleInject(css, ref) {
  if ( ref === void 0 ) ref = {};
  var insertAt = ref.insertAt;

  if (!css || typeof document === 'undefined') { return; }

  var head = document.head || document.getElementsByTagName('head')[0];
  var style = document.createElement('style');
  style.type = 'text/css';

  if (insertAt === 'top') {
    if (head.firstChild) {
      head.insertBefore(style, head.firstChild);
    } else {
      head.appendChild(style);
    }
  } else {
    head.appendChild(style);
  }

  if (style.styleSheet) {
    style.styleSheet.cssText = css;
  } else {
    style.appendChild(document.createTextNode(css));
  }
}

var css_248z$H = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */";
var variables = {"bpDesktop":"10000px","bpTablet":"1279px","bpMobile":"712px"};
styleInject(css_248z$H);

function on(obj, ...args) {
    if (obj && obj.addEventListener) {
        obj.addEventListener(...args);
    }
}
function off(obj, ...args) {
    if (obj && obj.removeEventListener) {
        obj.removeEventListener(...args);
    }
}
const isBrowser = typeof window !== 'undefined';

const getInitialState = (query, defaultState) => {
    if (isBrowser) {
        return window.matchMedia(query).matches;
    }
    // A default value has not been provided, and you are rendering on the server, warn of a possible hydration mismatch when defaulting to false.
    if (process.env.NODE_ENV !== 'production') {
        console.warn('`useMedia` When server side rendering, defaultState should be defined to prevent a hydration mismatches.');
    }
    return false;
};
const useMedia = (query, defaultState) => {
    const [state, setState] = useState(getInitialState(query));
    useEffect(() => {
        let mounted = true;
        const mql = window.matchMedia(query);
        const onChange = () => {
            if (!mounted) {
                return;
            }
            setState(!!mql.matches);
        };
        mql.addListener(onChange);
        setState(mql.matches);
        return () => {
            mounted = false;
            mql.removeListener(onChange);
        };
    }, [query]);
    return state;
};

const defaultEvents = ['mousedown', 'touchstart'];
const useClickAway = (ref, onClickAway, events = defaultEvents) => {
    const savedCallback = useRef(onClickAway);
    useEffect(() => {
        savedCallback.current = onClickAway;
    }, [onClickAway]);
    useEffect(() => {
        const handler = (event) => {
            const { current: el } = ref;
            el && !el.contains(event.target) && savedCallback.current(event);
        };
        for (const eventName of events) {
            on(document, eventName, handler);
        }
        return () => {
            for (const eventName of events) {
                off(document, eventName, handler);
            }
        };
    }, [events, ref]);
};

function getClosestBody(el) {
    if (!el) {
        return null;
    }
    else if (el.tagName === 'BODY') {
        return el;
    }
    else if (el.tagName === 'IFRAME') {
        const document = el.contentDocument;
        return document ? document.body : null;
    }
    else if (!el.offsetParent) {
        return null;
    }
    return getClosestBody(el.offsetParent);
}
function preventDefault(rawEvent) {
    const e = rawEvent || window.event;
    // Do not prevent if the event has more than one touch (usually meaning this is a multi touch gesture like pinch to zoom).
    if (e.touches.length > 1)
        return true;
    if (e.preventDefault)
        e.preventDefault();
    return false;
}
const isIosDevice = isBrowser &&
    window.navigator &&
    window.navigator.platform &&
    /iP(ad|hone|od)/.test(window.navigator.platform);
const bodies = new Map();
const doc = typeof document === 'object' ? document : undefined;
let documentListenerAdded = false;
const useLockBodyScroll = !doc
    ? function useLockBodyMock(_locked = true, _elementRef) { }
    : function useLockBody(locked = true, elementRef) {
        const bodyRef = useRef(doc.body);
        elementRef = elementRef || bodyRef;
        const lock = (body) => {
            const bodyInfo = bodies.get(body);
            if (!bodyInfo) {
                bodies.set(body, { counter: 1, initialOverflow: body.style.overflow });
                if (isIosDevice) {
                    if (!documentListenerAdded) {
                        on(document, 'touchmove', preventDefault, { passive: false });
                        documentListenerAdded = true;
                    }
                }
                else {
                    body.style.overflow = 'hidden';
                }
            }
            else {
                bodies.set(body, {
                    counter: bodyInfo.counter + 1,
                    initialOverflow: bodyInfo.initialOverflow,
                });
            }
        };
        const unlock = (body) => {
            const bodyInfo = bodies.get(body);
            if (bodyInfo) {
                if (bodyInfo.counter === 1) {
                    bodies.delete(body);
                    if (isIosDevice) {
                        body.ontouchmove = null;
                        if (documentListenerAdded) {
                            off(document, 'touchmove', preventDefault);
                            documentListenerAdded = false;
                        }
                    }
                    else {
                        body.style.overflow = bodyInfo.initialOverflow;
                    }
                }
                else {
                    bodies.set(body, {
                        counter: bodyInfo.counter - 1,
                        initialOverflow: bodyInfo.initialOverflow,
                    });
                }
            }
        };
        useEffect(() => {
            const body = getClosestBody(elementRef.current);
            if (!body) {
                return;
            }
            if (locked) {
                lock(body);
            }
            else {
                unlock(body);
            }
        }, [locked, elementRef]);
        // clean up, on un-mount
        useEffect(() => {
            const body = getClosestBody(elementRef.current);
            if (!body) {
                return;
            }
            return () => {
                unlock(body);
            };
        }, [elementRef]);
    };

const bpMobile = parseInt(variables.bpMobile);
const bpTablet = parseInt(variables.bpTablet);
const useBreakpointsCreate = () => {
    const isMobile = useMedia(`(max-width: ${variables.bpMobile})`);
    const isTablet = useMedia(`(max-width: ${variables.bpTablet})`);
    const isDesktop = useMedia(`(min-width: ${variables.bpDesktop})`);
    const fromTablet = useMedia(`(min-width: ${bpMobile + 1}px)`);
    const fromDesktop = useMedia(`(min-width: ${bpTablet + 1}px)`);
    return {
        isMobile,
        isTablet,
        fromTablet,
        isCompact: isMobile || isTablet,
        isDesktop,
        fromDesktop,
    };
};

const BreakpointsContext = createContext(null);
const ProvideBreakpoints = ({ children }) => {
    const prevContext = useContext(BreakpointsContext);
    if (prevContext !== null) {
        return jsx(Fragment, { children: children });
    }
    return (jsx(ProvideNewContext, { children: children }));
};
const ProvideNewContext = ({ children }) => {
    const context = useBreakpointsCreate();
    return (jsx(BreakpointsContext.Provider, Object.assign({ value: context }, { children: children })));
};

const j = "datalake_session";
const I = (e) => Number(e) ? new Date(Number(e)).getTime() > 0 : false, x = () => {
  const e = localStorage.getItem(j);
  if (!e)
    return null;
  try {
    const t = JSON.parse(e);
    return !["id", "lastActivity", "startTime"].every((o) => Object.hasOwn(t, o)) || !I(t.startTime) || !I(t.lastActivity) || t.lastActivity <= Date.now() - 18e5 ? null : t;
  } catch {
    return null;
  }
}, dt = () => {
  const e = x();
  return e == null ? void 0 : e.id;
};
var m = typeof globalThis < "u" ? globalThis : typeof window < "u" ? window : typeof global < "u" ? global : typeof self < "u" ? self : {};
var Q = typeof m == "object" && m && m.Object === Object && m, K = typeof self == "object" && self && self.Object === Object && self; Q || K || Function("return this")();

const LOCALE_DEFAULT = 'en-US';
const LOCALE_LABELS = {
    [LOCALE_DEFAULT]: 'English',
    'ar-AR': 'اَلْعَرَبِيَّةُ',
    'bg-BG': 'Български',
    'bn-BD': 'বাংলা',
    'cs-CZ': 'Čeština',
    'da-DK': 'Dansk',
    'de-CH': 'Deutsch (CH)',
    'de-DE': 'Deutsch',
    'es-ES': 'Español',
    'fi-FI': 'Suomi',
    'fr-FR': 'Français',
    'hi-IN': 'हिंदी',
    'hu-HU': 'Magyar',
    'id-ID': 'Bahasa Indonesia',
    'it-IT': 'Italiano',
    'ja-JP': '日本語',
    'ko-KR': '한국어',
    'ms-MY': 'Bahasa Melayu',
    'nb-NO': 'Norwegian Bokmål',
    'nl-NL': 'Nederlands',
    'pl-PL': 'Polski',
    'pt-BR': 'Português brasileiro',
    'pt-PT': 'Português',
    'ro-RO': 'Română',
    'ru-RU': 'Русский',
    'sr-Cyrl-RS': 'Српски (ћир.)',
    'sr-Latn-RS': 'Srpski (lat.)',
    'sv-SE': 'Svenska',
    'th-TH': 'ไทย',
    'tr-TR': 'Türkçe',
    'uk-UA': 'Українська',
    'vi-VN': 'Tiếng Việt',
    'zh-CN': '中文(中华人民共和国)',
    'zh-TW': '中文(台灣)',
};
const DEVICE_LOCALE = window.navigator.language;
const LOCALE_CLIENT_VAR_NAME = 'locale';

const NAVIGATION_STRUCTURE_ENDPOINT = '/navigation/structure';

const initialState$5 = {
    locale: localStorage.getItem(LOCALE_CLIENT_VAR_NAME),
    supportedLocales: [],
};
const localSlice = createSlice({
    name: 'navigationLocale',
    initialState: initialState$5,
    reducers: {
        setLocale: (state, action) => {
            state.locale = action.payload;
        },
        setSupportedLocales: (state, action) => {
            state.supportedLocales = action.payload;
        },
    },
});
const selectLocaleRoot = (state) => state.navigationLocale;
const { setLocale, setSupportedLocales } = localSlice.actions;

const selectCurrentStoreLocale = createSelector(selectLocaleRoot, (data) => {
    return data.locale;
});

let globalAxiosErrorHandler = (error) => __awaiter(void 0, void 0, void 0, function* () {
    throw error;
});
const setGlobalAxiosErrorHandler = (handler) => {
    globalAxiosErrorHandler = handler;
};
const errorInterceptor = (client) => {
    client.interceptors.response.use((request) => request, (error) => {
        return globalAxiosErrorHandler(error);
    });
};

const createClient$1 = (baseURL) => {
    const client = axios.create({
        baseURL,
        headers: {
            'X-Internal-Request': 'true',
            'x-session-id': dt(),
            // 'X-Device-Id': getDeviceId(),
        },
    });
    errorInterceptor(client);
    // authorizationHeaderInterceptor(client)
    // flattenQueryParamsArraysInterceptor(client)
    // unauthorizedResponseInterceptor(client)
    // TODO: current locale header interceptor over redux slice
    return client;
};

const PLATFORM_URL$1 = '/api/platform/v1';
const platformClient = createClient$1(PLATFORM_URL$1);

const navigationPlatformApi = createApi({
    reducerPath: 'navigationPlatformApi',
    baseQuery: ({ url, method = 'get', body, params }, { getState }, extraOptions = {}) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        const currentLocale = selectCurrentStoreLocale(getState());
        const headers = {
            'Accept-Language': currentLocale,
        };
        try {
            const result = yield platformClient({ url, method, data: body, params, headers });
            return { data: result.data };
        }
        catch (axiosError) {
            const err = axiosError;
            return {
                error: Object.assign({ status: (_a = err.response) === null || _a === void 0 ? void 0 : _a.status, data: (_c = (_b = err.response) === null || _b === void 0 ? void 0 : _b.data) !== null && _c !== void 0 ? _c : err.message }, (extraOptions.ignoreError !== undefined && { ignoreError: extraOptions.ignoreError })),
            };
        }
    }),
    endpoints: () => ({}),
});

const navigationStructureApi = navigationPlatformApi
    .enhanceEndpoints({
    addTagTypes: ['NavigationStructure'],
})
    .injectEndpoints({
    endpoints: (builder) => ({
        getNavigationStructure: builder.query({
            query: () => ({
                url: NAVIGATION_STRUCTURE_ENDPOINT,
            }),
            providesTags: ['NavigationStructure'],
        }),
    }),
    overrideExisting: false,
});
const { useGetNavigationStructureQuery, useLazyGetNavigationStructureQuery } = navigationStructureApi;

const useSubEvent = () => {
    const [, update] = useState({});
    const listeners = useRef(new Set());
    const emit = useMemo(() => (param) => {
        const cancel = Array.from(listeners.current).some((listener) => {
            const result = listener(param);
            return result === true;
        });
        update({});
        return cancel;
    }, []);
    const on = useMemo(() => (listener) => {
        listeners.current.add(listener);
        update({});
        return {
            cancel: () => {
                listeners.current.delete(listener);
                update({});
            },
        };
    }, []);
    useEffect(() => {
        return () => {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            listeners.current.clear();
        };
    }, []);
    return useMemo(() => ({ on, emit }), [on, emit]);
};

const useMutableRef = (value) => {
    const ref = useRef(value);
    ref.current = value;
    return ref;
};

function useSyncLocalStorage(currentStoreLocale, setLocale) {
    const setLocaleRef = useMutableRef(setLocale);
    // save to `localStorage` on every `currentStoreLocale` state change
    useEffect(() => {
        if (!currentStoreLocale) {
            localStorage.removeItem(LOCALE_CLIENT_VAR_NAME);
            return;
        }
        localStorage.setItem(LOCALE_CLIENT_VAR_NAME, currentStoreLocale);
    }, [currentStoreLocale]);
    // careful handle changes from other instances
    useEffect(() => {
        const handler = (ev) => {
            if (ev.key !== LOCALE_CLIENT_VAR_NAME) {
                return;
            }
            const newValue = ev.newValue;
            if (!newValue) {
                setLocaleRef.current(null);
                return;
            }
            setLocaleRef.current(newValue);
        };
        window.addEventListener('storage', handler);
        return () => {
            window.removeEventListener('storage', handler);
        };
    }, [setLocaleRef]);
}

/**
 * Due to backward incompatibility of the reselect versions, I had to inline the lruMemoize function (formerly defaultMemoize).
 * Otherwise, I would have had to update all applications. The original source code is here.
 */
const NOT_FOUND = 'NOT_FOUND';
function createSingletonCache(equals) {
    let entry;
    return {
        get(key) {
            if (entry && equals(entry.key, key)) {
                return entry.value;
            }
            return NOT_FOUND;
        },
        put(key, value) {
            entry = { key, value };
        },
        getEntries() {
            return entry ? [entry] : [];
        },
        clear() {
            entry = undefined;
        },
    };
}
function createLruCache(maxSize, equals) {
    let entries = [];
    function get(key) {
        const cacheIndex = entries.findIndex((entry) => equals(key, entry.key));
        // We found a cached entry
        if (cacheIndex > -1) {
            const entry = entries[cacheIndex];
            // Cached entry not at top of cache, move it to the top
            if (cacheIndex > 0) {
                entries.splice(cacheIndex, 1);
                entries.unshift(entry);
            }
            return entry.value;
        }
        // No entry found in cache, return sentinel
        return NOT_FOUND;
    }
    function put(key, value) {
        if (get(key) === NOT_FOUND) {
            // TODO Is unshift slow?
            entries.unshift({ key, value });
            if (entries.length > maxSize) {
                entries.pop();
            }
        }
    }
    function getEntries() {
        return entries;
    }
    function clear() {
        entries = [];
    }
    return { get, put, getEntries, clear };
}
/**
 * Runs a simple reference equality check.
 * What {@linkcode lruMemoize lruMemoize} uses by default.
 *
 * **Note**: This function was previously known as `defaultEqualityCheck`.
 *
 * @public
 */
const referenceEqualityCheck = (a, b) => a === b;
function createCacheKeyComparator(equalityCheck) {
    return function areArgumentsShallowlyEqual(prev, next) {
        if (prev === null || next === null || prev.length !== next.length) {
            return false;
        }
        // Do this in a for loop (and not a `forEach` or an `every`) so we can determine equality as fast as possible.
        const { length } = prev;
        for (let i = 0; i < length; i++) {
            if (!equalityCheck(prev[i], next[i])) {
                return false;
            }
        }
        return true;
    };
}
/**
 * Creates a memoized version of a function with an optional
 * LRU (Least Recently Used) cache. The memoized function uses a cache to
 * store computed values. Depending on the `maxSize` option, it will use
 * either a singleton cache (for a single entry) or an
 * LRU cache (for multiple entries).
 *
 * **Note**: This function was previously known as `defaultMemoize`.
 *
 * @param func - The function to be memoized.
 * @param equalityCheckOrOptions - Either an equality check function or an options object.
 * @returns A memoized function with a `.clearCache()` method attached.
 *
 * @template Func - The type of the function that is memoized.
 *
 * @see {@link https://reselect.js.org/api/lruMemoize `lruMemoize`}
 *
 * @public
 */
function lruMemoize(func, equalityCheckOrOptions) {
    const providedOptions = { equalityCheck: equalityCheckOrOptions };
    const { equalityCheck = referenceEqualityCheck, maxSize = 1, resultEqualityCheck } = providedOptions;
    const comparator = createCacheKeyComparator(equalityCheck);
    let resultsCount = 0;
    const cache = maxSize <= 1 ? createSingletonCache(comparator) : createLruCache(maxSize, comparator);
    function memoized() {
        let value = cache.get(arguments);
        if (value === NOT_FOUND) {
            // apply arguments instead of spreading for performance.
            // @ts-ignore
            value = func.apply(null, arguments);
            resultsCount++;
            if (resultEqualityCheck) {
                const entries = cache.getEntries();
                const matchingEntry = entries.find((entry) => resultEqualityCheck(entry.value, value));
                if (matchingEntry) {
                    value = matchingEntry.value;
                    resultsCount !== 0 && resultsCount--;
                }
            }
            cache.put(arguments, value);
        }
        return value;
    }
    memoized.clearCache = () => {
        cache.clear();
        memoized.resetResultsCount();
    };
    memoized.resultsCount = () => resultsCount;
    memoized.resetResultsCount = () => {
        resultsCount = 0;
    };
    return memoized;
}

const intersection = (mySet1, mySet2) => {
    return new Set([...mySet1].filter((x) => mySet2.has(x)));
};

const getAvailableLocales = lruMemoize((supportedSet, tenantLocalesSet) => {
    return intersection(supportedSet, tenantLocalesSet);
});

function makeLocaleMenu(supportedSet, tenantLocalesSet, currentLocale) {
    const availableLocales = getAvailableLocales(supportedSet, tenantLocalesSet);
    const items = Array.from(availableLocales).map((code) => ({
        key: code,
        disabled: false,
        label: LOCALE_LABELS[code],
    }));
    // let's add unsupported language in menu, if it's supported by tenant
    if (currentLocale && !supportedSet.has(currentLocale) && tenantLocalesSet.has(currentLocale)) {
        items.push({
            key: currentLocale,
            disabled: true,
            label: LOCALE_LABELS[currentLocale],
        });
    }
    const rootLabel = currentLocale ? `Language: ${LOCALE_LABELS[currentLocale]}` : 'Language';
    return {
        label: rootLabel,
        key: 'language',
        items,
    };
}

/**
 * Usually locales set passed as inline array on consumer side
 * Os every time it will be new array, so we need to implement more accurate comparison
 */
const useLocalesMemorize = (array) => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => new Set(array), [array.join(',')]);
};

/**
 * `supportedLocales` could be passed as inline array on consumer side
 * Every time it will be new array
 * Do not use `supportedLocales` without memoization
 */
// DO NOT USE IT DIRECTLY
const useLocale = (supportedLocales, tenantSupportedLocales, tenantDefaultLocale) => {
    const [currentStoreLocale, setCurrentStoreLocale] = useState(() => {
        var _a;
        return (_a = localStorage.getItem(LOCALE_CLIENT_VAR_NAME)) !== null && _a !== void 0 ? _a : null;
    });
    useSyncLocalStorage(currentStoreLocale, setCurrentStoreLocale);
    const supportedLocalesSet = useLocalesMemorize(supportedLocales);
    const tenantSupportedLocalesSet = useLocalesMemorize(tenantSupportedLocales !== null && tenantSupportedLocales !== void 0 ? tenantSupportedLocales : supportedLocales);
    const selectedLocale = useMemo(() => currentStoreLocale || tenantDefaultLocale, [currentStoreLocale, tenantDefaultLocale]);
    const availableLocales = getAvailableLocales(supportedLocalesSet, tenantSupportedLocalesSet);
    const currentLocale = useMemo(() => {
        const defaultAvailableLocale = availableLocales.has(DEVICE_LOCALE) ? DEVICE_LOCALE : LOCALE_DEFAULT;
        // in case of no locale selected we choose locale from browser, if it supported
        if (!selectedLocale) {
            return defaultAvailableLocale;
        }
        // locale setting can comes from other app with different set of locales
        // in this case we will switches to default locale
        if (!availableLocales.has(selectedLocale)) {
            return defaultAvailableLocale;
        }
        return selectedLocale;
    }, [availableLocales, selectedLocale]);
    const localeChangeEvent = useSubEvent();
    // emit `localeChangeEvent` on every `currentLocale` change
    useEffect(() => {
        if (!currentLocale) {
            return;
        }
        localeChangeEvent.emit(currentLocale);
    }, [currentLocale, localeChangeEvent]);
    const localeMenu = useMemo(() => makeLocaleMenu(supportedLocalesSet, tenantSupportedLocalesSet, selectedLocale), [selectedLocale, supportedLocalesSet, tenantSupportedLocalesSet]);
    return useMemo(() => ({
        localeMenu,
        currentLocale,
        selectedLocale,
        availableLocales,
        localeChangeEvent,
        setLocale: setCurrentStoreLocale,
    }), [localeMenu, currentLocale, selectedLocale, availableLocales, localeChangeEvent]);
};

const useLocaleContext = (supportedLocales, tenantSupportedLocales, tenantDefaultLocale, isPublic = false) => {
    const context = useLocale(supportedLocales, tenantSupportedLocales, tenantDefaultLocale);
    const { currentLocale } = context;
    const dispatch = useDispatch();
    useEffect(() => {
        if (!currentLocale) {
            return;
        }
        dispatch(setLocale(currentLocale));
        if (isPublic) {
            return;
        }
        dispatch(navigationStructureApi.util.invalidateTags(['NavigationStructure']));
    }, [currentLocale, dispatch, isPublic]);
    return context;
};

const TENANT_SUPPORTED_LOCALES = 'platform.common.supported_locales';
const TENANT_COMMON_NAVIGATION = 'ui.common.navigation';
const TENANT_LOCALE = 'platform.common.locale';
const TENANT_SETTINGS_ENDPOINT = '/tenants/current/settings';
const PUBLIC_TENANT_SETTINGS_ENDPOINT = '/public/tenant-settings';

const tenantSettingsApi = navigationPlatformApi.injectEndpoints({
    endpoints: (builder) => ({
        getTenantSettings: builder.query({
            query: () => ({
                url: TENANT_SETTINGS_ENDPOINT,
            }),
        }),
    }),
    overrideExisting: false,
});
const { useGetTenantSettingsQuery, useLazyGetTenantSettingsQuery } = tenantSettingsApi;

const getTenantSettingsMap = (data) => {
    const result = new Map();
    if (!data) {
        return result;
    }
    return data.items.reduce((acc, item) => {
        acc.set(item.name, item);
        return acc;
    }, result);
};
const selectTenantSettingsMap = createSelector(tenantSettingsApi.endpoints.getTenantSettings.select(), ({ data }) => {
    return getTenantSettingsMap(data !== null && data !== void 0 ? data : null);
});

const selectTenantDefaultLocale = createSelector(selectTenantSettingsMap, (map) => {
    var _a, _b;
    // TODO: replace type assertion with custom type guard with runtime check
    return ((_b = (_a = map.get(TENANT_LOCALE)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : null);
});

const selectTenantLocales = createSelector$1(selectTenantSettingsMap, (map) => {
    var _a, _b;
    const rawValue = (_b = (_a = map.get(TENANT_SUPPORTED_LOCALES)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : null;
    if (!rawValue) {
        return [];
    }
    return rawValue.split(',').reduce((acc, item) => {
        const localeCode = item.trim();
        if (!LOCALE_LABELS[localeCode]) {
            return acc;
        }
        return [...acc, localeCode];
    }, []);
});

/**
 * @deprecated use selectTenantDefaultLocale instead
 */
function selectCurrentLocale() {
    throw new Error('"selectCurrentLocale" deprecated, use selectTenantDefaultLocale instead');
}

let client;
const createClient = (props) => {
    const { locale, apiPrefix, handleAxiosError } = props;
    const clientLocal = axios.create({
        baseURL: `${apiPrefix}v1`,
        headers: Object.assign({ 'Content-Type': 'application/json', 'x-session-id': dt() }, (!!locale ? { 'Accept-Language': `${locale};q=0.9,${LOCALE_DEFAULT};q=0.8` } : {})),
    });
    clientLocal.interceptors.request.use((config) => {
        config = Object.assign(Object.assign({}, config), { headers: Object.assign({}, config.headers) });
        return config;
    });
    clientLocal.interceptors.response.use((request) => request, handleAxiosError);
    client = clientLocal;
};

const PLATFORM_FEATURES = {
    loadNavigationStructure: true,
    loadUserData: true,
    loadTenantsSettings: true,
    hamburgerVisible: true,
    hideHamburgerSections: false,
    isHamburgerExtendable: false,
    isSidebarOptional: false,
    isSwitchToLearnerAvailable: true,
    isSwitchToPlatformAvailable: false,
    isLinksWithPrefix: true,
    hasPlatformProfile: true,
};
const FeaturesMap = {
    platform: PLATFORM_FEATURES,
    'LMS-1': {
        loadNavigationStructure: true,
        loadUserData: true,
        loadTenantsSettings: true,
        hamburgerVisible: true,
        hideHamburgerSections: true,
        isHamburgerExtendable: false,
        isSidebarOptional: false,
        isSwitchToLearnerAvailable: false,
        isSwitchToPlatformAvailable: false,
        isLinksWithPrefix: true,
        hasPlatformProfile: true,
    },
    learner: {
        loadNavigationStructure: true,
        loadTenantsSettings: true,
        loadUserData: true,
        hamburgerVisible: false,
        hideHamburgerSections: false,
        isHamburgerExtendable: true,
        isSidebarOptional: true,
        isSwitchToLearnerAvailable: false,
        isSwitchToPlatformAvailable: true,
        isLinksWithPrefix: true,
        hasPlatformProfile: false,
    },
    standalone: {
        loadNavigationStructure: false,
        loadTenantsSettings: false,
        loadUserData: false,
        hamburgerVisible: true,
        hideHamburgerSections: false,
        isHamburgerExtendable: true,
        isSidebarOptional: true,
        isSwitchToLearnerAvailable: false,
        isSwitchToPlatformAvailable: false,
        isLinksWithPrefix: false,
        hasPlatformProfile: false,
    },
    schools: {
        loadNavigationStructure: false,
        loadTenantsSettings: false,
        loadUserData: false,
        hamburgerVisible: true,
        hideHamburgerSections: false,
        isHamburgerExtendable: true,
        isSidebarOptional: true,
        isSwitchToLearnerAvailable: false,
        isSwitchToPlatformAvailable: false,
        isLinksWithPrefix: false,
        hasPlatformProfile: false,
    },
};
const DEFAULT_MODE = 'platform';

const initialState$4 = {
    collection: [],
};
const breadcrumbsSlice = createSlice({
    name: 'navigationBreadcrumbs',
    initialState: initialState$4,
    reducers: {
        setBreadcrumbs: (state, action) => {
            state.collection = action.payload;
        },
        pushBreadcrumbs: (state, action) => {
            state.collection.push(action.payload);
        },
        popBreadcrumbs: (state) => {
            state.collection.pop();
        },
    },
});
const { setBreadcrumbs, pushBreadcrumbs, popBreadcrumbs } = breadcrumbsSlice.actions;
const selectBreadcrumbsRoot = (state) => state.navigationBreadcrumbs;
const createBreadcrumbsActions = (dispatch) => {
    return {
        push: (item) => {
            dispatch(breadcrumbsSlice.actions.pushBreadcrumbs(item));
        },
        pop: () => {
            dispatch(breadcrumbsSlice.actions.popBreadcrumbs());
        },
        set: (items) => {
            dispatch(breadcrumbsSlice.actions.setBreadcrumbs(items));
        },
        reset: () => {
            dispatch(breadcrumbsSlice.actions.setBreadcrumbs([]));
        },
        /**
         * @deprecated
         * use `selectLastBreadcrumb` instead
         */
        last: () => {
            throw new Error('use `selectLastBreadcrumb` instead');
        },
    };
};

const initialState$3 = {
    currentLocale: LOCALE_DEFAULT,
    isHamburgerOpen: false,
    isNavOpen: false,
    activeSlug: null,
    mode: DEFAULT_MODE,
};
const navigationSlice = createSlice({
    name: 'navigationSlice',
    initialState: initialState$3,
    reducers: {
        setHamburgerOpen: (state, action) => {
            state.isHamburgerOpen = action.payload;
        },
        setNavOpen: (state, action) => {
            state.isNavOpen = action.payload;
        },
        setActiveSlug: (state, action) => {
            state.activeSlug = action.payload;
        },
        setMode: (state, action) => {
            state.mode = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addMatcher(tenantSettingsApi.endpoints.getTenantSettings.matchFulfilled, (state, action) => {
            var _a, _b;
            if (action.meta.requestStatus !== 'fulfilled') {
                return state;
            }
            const map = getTenantSettingsMap(action.payload);
            state.currentLocale = (_b = (_a = map.get(TENANT_LOCALE)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : LOCALE_DEFAULT;
        });
    },
});
const { setHamburgerOpen, setActiveSlug, setMode, setNavOpen } = navigationSlice.actions;

const initialState$2 = {
    collection: [],
};
const sidebarSlice = createSlice({
    name: 'navigationSidebar',
    initialState: initialState$2,
    reducers: {
        setSidebar: (state, action) => {
            state.collection = action.payload;
        },
        pushSidebar: (state, action) => {
            state.collection.push(action.payload);
        },
        popSidebar: (state) => {
            state.collection.pop();
        },
    },
});
const { setSidebar, popSidebar, pushSidebar } = sidebarSlice.actions;
const selectSidebarRoot = (state) => state.navigationSidebar;
const createSidebarActions = (dispatch) => {
    return {
        push: (item) => {
            dispatch(sidebarSlice.actions.pushSidebar(item));
        },
        pop: () => {
            dispatch(sidebarSlice.actions.popSidebar());
        },
        set: (items) => {
            dispatch(sidebarSlice.actions.setSidebar(items));
        },
        reset: () => {
            dispatch(sidebarSlice.actions.setSidebar([]));
        },
        /**
         * @deprecated
         */
        last: () => {
            throw new Error('select it from navigationSidebar');
        },
    };
};

const initialState$1 = {
    collection: [],
};
const customElementsSlice = createSlice({
    name: 'navigationCustomElements',
    initialState: initialState$1,
    reducers: {
        setCustomElements: (state, action) => {
            state.collection = action.payload;
        },
        pushCustomElements: (state, action) => {
            state.collection.push(action.payload);
        },
        popCustomElements: (state) => {
            state.collection.pop();
        },
    },
});
const selectCustomElementsRoot = (state) => state.navigationCustomElements;
const createCustomElementsActions = (dispatch) => {
    return {
        push: (item) => {
            dispatch(customElementsSlice.actions.pushCustomElements(item));
        },
        pop: () => {
            dispatch(customElementsSlice.actions.popCustomElements());
        },
        set: (items) => {
            dispatch(customElementsSlice.actions.setCustomElements(items));
        },
        reset: () => {
            dispatch(customElementsSlice.actions.setCustomElements([]));
        },
        /**
         * @deprecated
         * use `selectLastCustomElements` instead
         */
        last: () => {
            throw new Error('use `selectLastCustomElements` instead');
        },
    };
};

const DEFAULT_ACTIONS_LIMIT = 2; // Default value on ui-kit
const initialState = {
    collection: [],
    actionsLimit: DEFAULT_ACTIONS_LIMIT,
};
const actionsSlice = createSlice({
    name: 'navigationActions',
    initialState,
    reducers: {
        setActions: (state, action) => {
            state.collection = action.payload;
        },
        pushActions: (state, action) => {
            state.collection.push(action.payload);
        },
        popActions: (state) => {
            state.collection.pop();
        },
        setActionsLimit: (state, action) => {
            state.actionsLimit = action.payload;
        },
    },
});
const { setActions, popActions, pushActions, setActionsLimit } = actionsSlice.actions;
const selectActionsRoot = (state) => state.navigationActions;
const createActionsActions = (dispatch) => {
    return {
        push: (item) => {
            dispatch(actionsSlice.actions.pushActions(item));
        },
        pop: () => {
            dispatch(actionsSlice.actions.popActions());
        },
        set: (items) => {
            dispatch(actionsSlice.actions.setActions(items));
        },
        reset: () => {
            dispatch(actionsSlice.actions.setActions([]));
        },
        /**
         * @deprecated
         */
        last: () => {
            throw new Error('use `selectLastActions` instead');
        },
    };
};

const navigationReducer = {
    [navigationPlatformApi.reducerPath]: navigationPlatformApi.reducer,
    [navigationSlice.name]: navigationSlice.reducer,
    [breadcrumbsSlice.name]: breadcrumbsSlice.reducer,
    [sidebarSlice.name]: sidebarSlice.reducer,
    [customElementsSlice.name]: customElementsSlice.reducer,
    [actionsSlice.name]: actionsSlice.reducer,
    [localSlice.name]: localSlice.reducer,
};
const navigationStore = configureStore({
    reducer: navigationReducer,
});

const selectNavigationRoot = (state) => state.navigationSlice;

const selectIsHamburgerOpen = createSelector(selectNavigationRoot, (navigationState) => navigationState.isHamburgerOpen);

const enumLevel = (tree, itemsIndex) => {
    return Array.from(tree.values()).reduce((acc, item) => {
        itemsIndex.set(item.slug, item);
        if (item.children && item.children.size > 0) {
            enumLevel(item.children, itemsIndex);
        }
        return acc;
    }, itemsIndex);
};
const collectTreeItemsFlat = (tree) => {
    const result = new Map();
    if (!tree) {
        return result;
    }
    return enumLevel(tree, result);
};

const getNodesOnly = (items) => {
    if (!items) {
        return [];
    }
    return items.filter((item) => {
        return typeof item !== 'string';
    });
};

const saturateItem = (item, parentSlug) => {
    var _a, _b, _c;
    const { children: originChildren } = item, restItem = __rest(item, ["children"]);
    const base = Object.assign(Object.assign({}, restItem), { isSection: false, parentSlug });
    if (!((_a = item.children) === null || _a === void 0 ? void 0 : _a.length)) {
        return base;
    }
    const children = ((_b = item.children) === null || _b === void 0 ? void 0 : _b.length) ? fetchLevel(item.children, item.slug) : null;
    const isSection = ((_c = children === null || children === void 0 ? void 0 : children.size) !== null && _c !== void 0 ? _c : 0) !== 0;
    return Object.assign(Object.assign({}, base), { firstChildSlug: item.children[0].slug, isSection,
        children });
};
const fetchLevel = (data, parentSlug) => {
    return data.reduce((acc, item) => {
        return acc.set(item.slug, saturateItem(item, parentSlug));
    }, new Map());
};
const getSaturatedTree = (data) => {
    return fetchLevel(data, null);
};

const selectCustomElementsSaturatedTree = createSelector(selectCustomElementsRoot, (tree) => {
    const items = getNodesOnly(tree.collection);
    return getSaturatedTree(items);
});

const selectMode = createSelector(selectNavigationRoot, (state) => state.mode);

const ROLE_SWITCHER_SLUG = 'role_switcher';
const TO_PLATFORM_LABEL = 'goToManagementPortal';
const toLearnerView = getSaturatedTree([
    {
        name: 'learnerView',
        slug: ROLE_SWITCHER_SLUG,
        icon_id: 'RepeatBoldStroke',
        url: '/learn',
        children: [],
    },
]).get(ROLE_SWITCHER_SLUG);
const INSTRUCTOR_DASHBOARD_SLUG = 'instructor_dashboard';
const toInstructorDashboard = getSaturatedTree([
    {
        name: 'Dashboard',
        slug: INSTRUCTOR_DASHBOARD_SLUG,
        icon_id: 'DashboardBoldStroke',
        url: '/teach',
        children: [],
    },
]).get(INSTRUCTOR_DASHBOARD_SLUG);
const getToPlatformNode = (url) => {
    return getSaturatedTree([
        {
            name: TO_PLATFORM_LABEL,
            slug: ROLE_SWITCHER_SLUG,
            icon_id: 'RepeatBoldStroke',
            url,
            children: [],
        },
    ]).get(ROLE_SWITCHER_SLUG);
};
const avatarMenuItem = {
    label: TO_PLATFORM_LABEL,
    key: ROLE_SWITCHER_SLUG,
};

const PLATFORM_URL = '/portal';

const getFirstNode = (tree) => {
    var _a;
    for (const [, node] of tree) {
        if ((_a = node.children) === null || _a === void 0 ? void 0 : _a.size) {
            const childResult = getFirstNode(node.children);
            if (childResult) {
                return childResult;
            }
        }
        return node;
    }
    return null;
};

const selectRawNavigationData = navigationStructureApi.endpoints.getNavigationStructure.select();
const selectSaturatedTree = createSelector(
// for some reason first argument of selector (state) became wrong in case of endpoint enhance with tags
// let me know if you know how to resolve it (@mitasovr)
(state) => selectRawNavigationData(state), ({ data }) => {
    if (!data) {
        return null;
    }
    return getSaturatedTree(data);
});

const selectTreeWithoutFavorites = createSelector(selectSaturatedTree, (tree) => {
    if (!tree) {
        return null;
    }
    const treeClone = new Map([...tree]);
    treeClone.delete('platform_favorites');
    return treeClone;
});

const selectTreeFirstNode = createSelector(selectTreeWithoutFavorites, (tree) => {
    if (!tree) {
        return null;
    }
    return getFirstNode(tree);
});

const selectDefaultPlatformUrl = createSelector(selectTreeFirstNode, (firstNode) => {
    var _a;
    return (_a = firstNode === null || firstNode === void 0 ? void 0 : firstNode.url) !== null && _a !== void 0 ? _a : PLATFORM_URL;
});

const selectIsHamburgerEmpty = createSelector(selectTreeWithoutFavorites, (tree) => {
    if (!tree) {
        return null;
    }
    return tree.size === 0;
});

const selectHasPlatformRole = createSelector(selectIsHamburgerEmpty, (isHamburgerEmpty) => {
    if (isHamburgerEmpty === null) {
        return null;
    }
    return isHamburgerEmpty === false;
});

const selectSaturatedTreeIndex = createSelector(selectSaturatedTree, (tree) => {
    if (!tree) {
        return null;
    }
    return collectTreeItemsFlat(tree);
});

const LEARNER_OBJECTIVE_WORKFLOW_PERMISSION = 'ObjectiveWorkflow.Create';

const USERS_PERMISSIONS_ENDPOINT = '/users/me/permissions';

const userPermissionsApi = navigationPlatformApi.injectEndpoints({
    endpoints: (builder) => ({
        getUsersPermissions: builder.query({
            query: () => ({
                url: USERS_PERMISSIONS_ENDPOINT,
            }),
        }),
    }),
    overrideExisting: false,
});
const { useGetUsersPermissionsQuery, useLazyGetUsersPermissionsQuery } = userPermissionsApi;

const selectUserPermissions = createSelector(userPermissionsApi.endpoints.getUsersPermissions.select(), ({ data }) => {
    return (data === null || data === void 0 ? void 0 : data.items) || [];
});

const selectUserHasLearnerPermission = createSelector(selectUserPermissions, (userPermissions) => {
    return userPermissions.findIndex((permission) => permission.name === LEARNER_OBJECTIVE_WORKFLOW_PERMISSION) !== -1;
});

const DEBUG_DOMAIN_QUERY_PARAM = 'debugDomain';
// INFO
// You can use cldev-dummy-tenant.alemira.dev domain for example locally
const getCurrentDomain = () => {
    return new URLSearchParams(window.location.search).get(DEBUG_DOMAIN_QUERY_PARAM) || window.location.hostname;
};

const publicTenantSettingsApi = navigationPlatformApi.injectEndpoints({
    endpoints: (builder) => ({
        getPublicTenantSettings: builder.query({
            query: () => ({
                url: `${PUBLIC_TENANT_SETTINGS_ENDPOINT}?domain=${getCurrentDomain()}`,
            }),
        }),
    }),
    overrideExisting: false,
});
const { useGetPublicTenantSettingsQuery, useLazyGetPublicTenantSettingsQuery } = publicTenantSettingsApi;

const getPublicTenantSettingsMap = (data) => {
    const result = new Map();
    if (!data) {
        return result;
    }
    Object.entries(data).forEach(([key, value]) => {
        result.set(key, { value });
    });
    return result;
};
const selectPublicTenantSettingsMap = createSelector(publicTenantSettingsApi.endpoints.getPublicTenantSettings.select(), ({ data }) => {
    return getPublicTenantSettingsMap(data !== null && data !== void 0 ? data : null);
});
const selectIsPublicSettingsGivesNotFound = (state) => { var _a, _b; return ((_b = (_a = publicTenantSettingsApi.endpoints.getPublicTenantSettings.select()(state)) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.status) === 404; };

function parseSetting(value = 'null') {
    if (value === null || value.trim() === '') {
        return undefined;
    }
    try {
        return JSON.parse(value);
    }
    catch (e) {
        return undefined;
    }
}

const usePublicTenantSettings = (key, jsonParse = false) => {
    var _a, _b;
    const tenantSettingsMap = useSelector(selectPublicTenantSettingsMap);
    const setting = (_b = (_a = tenantSettingsMap.get(key)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : null;
    return useMemo(() => (jsonParse ? parseSetting(setting) : setting), [jsonParse, setting]);
};

const useTenantSettings = (key, jsonParse = false) => {
    var _a, _b;
    const tenantSettingsMap = useSelector(selectTenantSettingsMap);
    const setting = (_b = (_a = tenantSettingsMap.get(key)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : null;
    return useMemo(() => (jsonParse ? parseSetting(setting) : setting), [jsonParse, setting]);
};

const useCommonTenantLinks = () => {
    const commonNavigationItems = useSelector(selectTenantCommonNavigation);
    return useMemo(() => {
        if (!(commonNavigationItems === null || commonNavigationItems === void 0 ? void 0 : commonNavigationItems.length))
            return undefined;
        return commonNavigationItems
            .filter((i) => i.visible)
            .map((item) => {
            var _a;
            return ({
                isSection: !!((_a = item.children) === null || _a === void 0 ? void 0 : _a.length),
                name: item.name,
                icon_id: item.iconName,
                parentSlug: null,
                slug: '',
                target: '_blank',
                url: item.url,
                children: item.children
                    ? item.children
                        .filter((i) => i.visible)
                        .reduce((acc, child) => {
                        var _a;
                        acc.set((_a = child.url) !== null && _a !== void 0 ? _a : acc.size.toString(), {
                            isSection: false,
                            name: child.name,
                            icon_id: child.iconName,
                            parentSlug: null,
                            slug: '',
                            target: '_blank',
                            url: child.url,
                        });
                        return acc;
                    }, new Map())
                    : undefined,
            });
        });
    }, [commonNavigationItems]);
};

const selectTenantCommonNavigation = createSelector$1(selectTenantSettingsMap, (map) => {
    var _a, _b;
    const rawValue = (_b = (_a = map.get(TENANT_COMMON_NAVIGATION)) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : null;
    if (!rawValue) {
        return [];
    }
    return parseSetting(rawValue);
});

const selectInstructorDashboardEnabled = createSelector(selectTenantSettingsMap, (tenantSettingsMap) => {
    var _a, _b;
    const settingValue = (_a = tenantSettingsMap.get('instructor.dashboard')) === null || _a === void 0 ? void 0 : _a.value;
    return (_b = parseSetting(settingValue)) === null || _b === void 0 ? void 0 : _b.enabled;
});

const selectHamburgerTree = createSelector(selectDefaultPlatformUrl, selectHasPlatformRole, selectTreeWithoutFavorites, selectMode, selectCustomElementsSaturatedTree, selectSaturatedTreeIndex, selectUserHasLearnerPermission, selectInstructorDashboardEnabled, (defaultPlatformUrl, hasPlatformRole, tree, mode, customElements, treeIndex, userHasLearnerPermission, instructorDashboardEnabled) => {
    const features = FeaturesMap[mode];
    if (tree && features.isSwitchToLearnerAvailable && userHasLearnerPermission) {
        tree = new Map([[ROLE_SWITCHER_SLUG, toLearnerView], ...tree]);
    }
    else if (tree && features.isSwitchToPlatformAvailable && hasPlatformRole === true) {
        tree = new Map([[ROLE_SWITCHER_SLUG, getToPlatformNode(defaultPlatformUrl)], ...tree]);
    }
    if (tree && instructorDashboardEnabled) {
        tree = new Map([[INSTRUCTOR_DASHBOARD_SLUG, toInstructorDashboard], ...tree]);
    }
    if (features.isHamburgerExtendable) {
        const nextTree = tree !== null && tree !== void 0 ? tree : new Map();
        customElements.forEach((customNode) => {
            const existNode = treeIndex === null || treeIndex === void 0 ? void 0 : treeIndex.get(customNode.slug);
            if (existNode) {
                return;
            }
            nextTree.set(customNode.slug, customNode);
        });
        tree = nextTree;
    }
    return tree;
});

const selectHamburgerTreeIndex = createSelector$1(selectHamburgerTree, (tree) => {
    if (!tree) {
        return new Map();
    }
    return collectTreeItemsFlat(tree);
});

const selectSaturatedBreadcrumbsItems = createSelector(selectBreadcrumbsRoot, (state) => {
    const items = getNodesOnly(state.collection);
    return getSaturatedTree(items);
});

const selectBreadcrumbsItemsIndex = createSelector(selectSaturatedBreadcrumbsItems, (itemsMap) => {
    return collectTreeItemsFlat(itemsMap);
});

const selectLastSidebar = createSelector(selectSidebarRoot, (state) => {
    var _a;
    return (_a = state.collection[state.collection.length - 1]) !== null && _a !== void 0 ? _a : null;
});

// getCollectionSlugs
const selectSidebarItemsIndex = createSelector(selectLastSidebar, (items) => {
    const nodes = getNodesOnly(items);
    const saturatedNodes = getSaturatedTree(nodes);
    return collectTreeItemsFlat(saturatedNodes);
});

const selectCustomElementsSaturatedTreeIndex = createSelector(selectCustomElementsSaturatedTree, (tree) => {
    return collectTreeItemsFlat(tree);
});

const selectMixedIndex = createSelector(selectCustomElementsSaturatedTreeIndex, selectBreadcrumbsItemsIndex, selectSidebarItemsIndex, selectHamburgerTreeIndex, (customElementsItemsIndex, breadcrumbsItemsIndex, sidebarItemsIndex, hamburgerTreeIndex) => {
    return new Map([...customElementsItemsIndex, ...breadcrumbsItemsIndex, ...sidebarItemsIndex, ...hamburgerTreeIndex]);
});

const selectIsNavOpen = createSelector(selectNavigationRoot, (navigationState) => navigationState.isNavOpen);

const useNavComponentContext = () => {
    const dispatch = useDispatch();
    const setNavOpen$1 = (value) => dispatch(setNavOpen(value));
    const isNavOpen = useSelector(selectIsNavOpen);
    return {
        setNavOpen: setNavOpen$1,
        isNavOpen,
    };
};

const useCreatePlatformContext = ({ appPrefix, mode = DEFAULT_MODE }) => {
    const dispatch = useDispatch();
    const features = FeaturesMap[mode];
    const linkClickEvent = useSubEvent();
    const navigationEvent = useSubEvent();
    const { setNavOpen } = useNavComponentContext();
    useGetUsersPermissionsQuery();
    const onLinkClick = useCallback((ev, item) => {
        setNavOpen(false);
        dispatch(setHamburgerOpen(false));
        const isHandled = navigationEvent.emit(item);
        if (isHandled) {
            ev.preventDefault();
            return;
        }
        // in case of we sure it's path to other app, just let it go
        if (features.isLinksWithPrefix) {
            const isExternalUrl = !!item.url && !new RegExp(`^/${appPrefix}(/|$)`).test(item.url);
            if (isExternalUrl) {
                return;
            }
        }
        ev.preventDefault();
        linkClickEvent.emit(item);
    }, [appPrefix, dispatch, features.isLinksWithPrefix, linkClickEvent, navigationEvent, setNavOpen]);
    return {
        /**
         * `linkClickEvent` happens when user clicks on link related to the current app
         */
        linkClickEvent,
        /**
         * `navigationEvent` happens when user clicks on any link in the navigation
         */
        navigationEvent,
        onLinkClick,
        mode,
    };
};
const PlatformContext = createContext(null);
const PlatformContextProvider = (props) => {
    if (props.handleAxiosError) {
        setGlobalAxiosErrorHandler(props.handleAxiosError);
    }
    const context = useCreatePlatformContext(props);
    return jsx(PlatformContext.Provider, Object.assign({ value: context }, { children: props.children }));
};
const usePlatformContext = () => {
    const context = useContext(PlatformContext);
    if (!context) {
        throw new Error('usePlatformContext must be used within a PlatformContextProvider');
    }
    return context;
};

const useHamburgerContextRedux = (tenantSettingsLoading) => {
    const { mode } = usePlatformContext();
    const features = FeaturesMap[mode];
    const { data, isSuccess } = useGetNavigationStructureQuery(undefined, {
        skip: !features.loadNavigationStructure || tenantSettingsLoading,
    });
    return {
        /**
         * if `loadNavigationStructure` is disabled, we consider that tree is loaded
         * if `currentData` is not null and request is successful, we consider that tree is loaded
         */
        isTreeLoaded: !features.loadNavigationStructure || (data !== null && isSuccess),
    };
};

const useActionsContext = () => {
    const dispatch = useDispatch();
    const actionsLayers = useMemo(() => createActionsActions(dispatch), [dispatch]);
    return {
        actionsLayers,
    };
};

const useBreadcrumbsContextRedux = () => {
    const dispatch = useDispatch();
    const breadcrumbs = useMemo(() => createBreadcrumbsActions(dispatch), [dispatch]);
    return {
        breadcrumbs,
    };
};

const useSidebarContextRedux = () => {
    const dispatch = useDispatch();
    const sidebarLayers = useMemo(() => createSidebarActions(dispatch), [dispatch]);
    return {
        sidebarLayers,
    };
};

function getCookie(name) {
    const matches = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1')}=([^;]*)`));
    return matches ? decodeURIComponent(matches[1]) : undefined;
}
function setCookie(name, value, options = {}) {
    let { expires } = options;
    if (typeof expires === 'number' && expires) {
        const d = new Date();
        d.setTime(d.getTime() + expires * 1000);
        expires = options.expires = d;
    }
    if (expires && expires instanceof Date && expires.toUTCString) {
        options.expires = expires.toUTCString();
    }
    value = encodeURIComponent(value);
    let updatedCookie = `${name}=${value}`;
    Object.keys(options).forEach((propName) => {
        updatedCookie += `; ${propName}`;
        const propValue = options[propName];
        if (propValue !== true) {
            updatedCookie += `=${propValue}`;
        }
    });
    document.cookie = updatedCookie;
}

const SELECTED_TEAM_COOKIE_KEY = (userId) => `platform:user:${userId}:team`;
const useGetSelectedTeam = (teams, userId) => {
    return useMemo(() => {
        const selectedTeamId = userId ? getCookie(SELECTED_TEAM_COOKIE_KEY(userId)) : '';
        return teams.find((team) => team.id === selectedTeamId);
    }, [teams, userId]);
};

class CacheWithState {
    constructor(cancel, data = null, isLoading = true, error = null) {
        this.cancel = cancel;
        this.data = data;
        this.isLoading = isLoading;
        this.error = error;
    }
}
const useAsyncCache = (predicate, cacheKey, options) => {
    const [renderCache, render] = useState({});
    const cache = useMemo(() => new Map(), []);
    const cacheRef = useMutableRef(cache);
    const optionsRef = useMutableRef(options);
    const predicateRef = useMutableRef(predicate);
    useEffect(() => {
        var _a, _b;
        const { current: currentCache } = cacheRef;
        const existCacheItem = currentCache.get(cacheKey);
        if (existCacheItem) {
            return;
        }
        const { promise, cancel } = predicateRef.current();
        currentCache.set(cacheKey, new CacheWithState(cancel));
        // @todo meaning?
        const maxSuccessAmount = (_b = (_a = optionsRef.current) === null || _a === void 0 ? void 0 : _a.maxSuccessAmount) !== null && _b !== void 0 ? _b : null;
        if (maxSuccessAmount !== null) {
            truncateCache(currentCache, maxSuccessAmount);
        }
        promise
            .then((data) => {
            const cacheItem = currentCache.get(cacheKey);
            // it's possible in case of promise resolved after cache history truncated because of maxSuccessAmount
            if (!cacheItem) {
                return;
            }
            currentCache.set(cacheKey, Object.assign(Object.assign({}, cacheItem), { data, isLoading: false }));
            render({});
        })
            .catch((error) => {
            const cacheItem = currentCache.get(cacheKey);
            // it's possible in case of promise resolved after cache history truncated because of maxSuccessAmount
            if (!cacheItem) {
                return;
            }
            currentCache.set(cacheKey, Object.assign(Object.assign({}, cacheItem), { isLoading: false, error }));
            render({});
        });
        return () => {
            const cacheItem = currentCache.get(cacheKey);
            if (!cacheItem) {
                return;
            }
            if (cacheItem.isLoading) {
                cancel();
            }
            if (!cacheItem.data) {
                currentCache.delete(cacheKey);
            }
        };
    }, [cacheKey, cacheRef, optionsRef, predicateRef]);
    const recent = useMemo(() => {
        var _a, _b;
        const { current: currentCache } = cacheRef;
        const cacheByKey = currentCache.get(cacheKey);
        if (cacheByKey === null || cacheByKey === void 0 ? void 0 : cacheByKey.data) {
            return cacheByKey.data;
        }
        const result = (_a = Array.from(currentCache)
            .reverse()
            .find(([, item]) => !item.isLoading && !item.error)) !== null && _a !== void 0 ? _a : null;
        return (_b = result === null || result === void 0 ? void 0 : result[1].data) !== null && _b !== void 0 ? _b : null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cacheRef, renderCache]);
    const drop = useCallback((cacheKey) => {
        const { current: currentCache } = cacheRef;
        if (!cacheKey) {
            currentCache.clear();
            render({});
            return;
        }
        currentCache.delete(cacheKey);
        render({});
    }, [cacheRef]);
    return {
        cacheRef,
        recent,
        drop,
    };
};
function truncateCache(cache, maxLen) {
    const cacheArray = Array.from(cache);
    let successCount = 0;
    for (let i = cacheArray.length - 1; i >= 0; i--) {
        const [cacheKey, cacheItem] = cacheArray[i];
        const isSuccess = !cacheItem.isLoading && !cacheItem.data;
        if (isSuccess) {
            successCount++;
        }
        if (successCount > maxLen) {
            cacheItem.cancel();
            cache.delete(cacheKey);
        }
    }
}

/**
 * @deprecated
 * Should be replaced with RTK Query
 */
const createEndpoint = (url) => () => (params) => {
    const controller = new AbortController();
    const promise = client
        .get(url, {
        signal: controller.signal,
        params,
    })
        .then((resp) => resp === null || resp === void 0 ? void 0 : resp.data);
    return {
        promise,
        cancel: () => controller.abort(),
    };
};

const fetchUserTeams = () => createEndpoint('/teams');

const emptyResponse = {
    promise: Promise.resolve({
        items: [],
        pagination: {
            has_next: false,
            next_page: '',
        },
    }),
    cancel: () => { },
};
const useGetTeams = ({ cacheKey, isTeamSwitcherExist, userId, }) => {
    var _a;
    const teamsRequest = useMemo(() => {
        if (isTeamSwitcherExist && userId) {
            return fetchUserTeams()();
        }
        return () => emptyResponse;
    }, [isTeamSwitcherExist, userId]);
    const { recent: teamsResponse } = useAsyncCache(teamsRequest, `${cacheKey}_${isTeamSwitcherExist}_${userId}`);
    return (_a = teamsResponse === null || teamsResponse === void 0 ? void 0 : teamsResponse.items) !== null && _a !== void 0 ? _a : [];
};

// one year in seconds
const COOKIE_EXPIRES = 31536000;

/*
  The useSkipInitialCallEffect hook is a custom React hook that behaves similarly to the
  built-in useEffect hook, but with a slight difference.
  
  The useEffect hook runs after every render, including the first one.
  However, there are cases where you might want to skip the effect on
  the first render and only run it on subsequent renders. This is where
  useSkipInitialCallEffect comes in.
*/
const useSkipInitialCallEffect = (cb, dep) => {
    const initializeRef = React.useRef(false);
    React.useEffect((...args) => {
        if (initializeRef.current) {
            cb(...args);
        }
        else {
            initializeRef.current = true;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dep);
};

const useTeamSelector = ({ user, teams, initialSelectedTeam }) => {
    const [selectedTeam, setSelectedTeamState] = useState(initialSelectedTeam);
    const saveCookie = (team) => {
        setCookie(SELECTED_TEAM_COOKIE_KEY(user.id), team.id, {
            path: '/',
            expires: COOKIE_EXPIRES,
        });
    };
    const setSelectedTeam = (team) => {
        if (!user || !team)
            return;
        saveCookie(team);
        setSelectedTeamState(team);
    };
    useSkipInitialCallEffect(() => {
        const fetchAndSetTeam = () => {
            if (!user)
                return;
            const setFirstTeamAsSelected = () => {
                const [firstTeam] = teams;
                if (firstTeam) {
                    saveCookie(firstTeam);
                    setSelectedTeamState(firstTeam);
                }
            };
            // if it is first open and there is no selected team
            if (!initialSelectedTeam) {
                setFirstTeamAsSelected();
                return;
            }
            // if initiallySelectedTeam.id doesn't exist in the teams response,
            // it means the team may be deleted
            const foundTeam = teams.find((team) => {
                return team.id === initialSelectedTeam.id;
            });
            if (!foundTeam) {
                setFirstTeamAsSelected();
                return;
            }
            setSelectedTeamState(initialSelectedTeam);
        };
        fetchAndSetTeam();
    }, [initialSelectedTeam, teams, user]);
    return [selectedTeam, setSelectedTeam];
};

const useTeamSwitcherContext = ({ user, cacheKey, isTeamSwitcherExist }) => {
    const teamsList = useGetTeams({ cacheKey, isTeamSwitcherExist, userId: user === null || user === void 0 ? void 0 : user.id });
    const teams = useMemo(() => {
        const teamsListClone = [...teamsList];
        teamsListClone.sort((a, b) => a.name.localeCompare(b.name));
        return teamsListClone;
    }, [teamsList]);
    const initialSelectedTeam = useGetSelectedTeam(teams, user === null || user === void 0 ? void 0 : user.id);
    const [selectedTeam, setSelectedTeam] = useTeamSelector({
        user,
        teams,
        initialSelectedTeam,
    });
    return {
        teams,
        selectedTeam,
        setSelectedTeam,
    };
};

const useTenantSettingsContextRedux = ({ features }) => {
    const { isLoading: tenantSettingsLoading } = useGetTenantSettingsQuery(undefined, {
        skip: !features.loadTenantsSettings,
    });
    const tenantSettingsIndex = useSelector(selectTenantSettingsMap);
    return {
        tenantSettingsIndex,
        tenantSettingsLoading,
    };
};

const useCustomElementsContextRedux = () => {
    const dispatch = useDispatch();
    const customElements = useMemo(() => createCustomElementsActions(dispatch), [dispatch]);
    return {
        customElements,
    };
};

const USERS_ME_ENDPOINT = '/users/me';

const platformUserApi = navigationPlatformApi.injectEndpoints({
    endpoints: (builder) => ({
        getUsersMe: builder.query({
            query: () => ({
                url: USERS_ME_ENDPOINT,
            }),
        }),
    }),
    overrideExisting: false,
});
const { useGetUsersMeQuery, useLazyGetUsersMeQuery } = platformUserApi;

const selectUsersMe = createSelector(platformUserApi.endpoints.getUsersMe.select(), ({ data }) => data || null);

const useUsersMeContext = () => {
    const { mode } = usePlatformContext();
    const features = FeaturesMap[mode];
    const [getUsersMe, { data = null, isLoading }] = useLazyGetUsersMeQuery();
    useEffect(() => {
        if (!features.loadUserData) {
            return;
        }
        getUsersMe();
    }, [features.loadUserData, getUsersMe]);
    return { user: data, isLoading };
};

const treeDeepMerge = (dstTree, srcTree) => {
    const result = new Map([...dstTree]);
    srcTree.forEach((srcNode) => {
        const dstNode = dstTree.get(srcNode.slug);
        if (!dstNode) {
            result.set(srcNode.slug, srcNode);
            return;
        }
        const { children: dstChildren = null } = dstNode;
        const { children: srcChildren = null } = srcNode;
        const bothHaveChildren = !!dstChildren && !!srcChildren;
        const children = bothHaveChildren
            ? treeDeepMerge(dstChildren, srcChildren)
            : new Map([...(dstChildren || srcChildren || [])]);
        result.set(srcNode.slug, Object.assign(Object.assign({}, srcNode), { children }));
    });
    return result;
};

const upsertChild = (node, child, index) => {
    const { children: prevChildren } = node;
    const nextChildren = new Map([...(prevChildren !== null && prevChildren !== void 0 ? prevChildren : [])]);
    nextChildren.set(child.slug, child);
    const nextNode = Object.assign(Object.assign({}, node), { children: nextChildren });
    const { parentSlug } = nextNode;
    if (!parentSlug) {
        return nextNode;
    }
    const parentNode = index.get(parentSlug);
    if (!parentNode) {
        throw new Error('@platform/layout: Wrong parentSlug');
    }
    return upsertChild(parentNode, nextNode, index);
};

const EMPTY_MAP = new Map();
const selectHamburgerTreeWithCustomElements = createSelector(selectHamburgerTree, selectCustomElementsSaturatedTree, selectHamburgerTreeIndex, (hamburgerTree, customElementsTree, treeItemsIndex) => {
    const nextTree = new Map([...(hamburgerTree !== null && hamburgerTree !== void 0 ? hamburgerTree : [])]);
    customElementsTree.forEach((customNode) => {
        const existNode = treeItemsIndex.get(customNode.slug);
        if (!existNode) {
            return;
        }
        const { children: existChildren } = existNode;
        const { children: nextChildren } = customNode;
        const children = treeDeepMerge(existChildren !== null && existChildren !== void 0 ? existChildren : EMPTY_MAP, nextChildren !== null && nextChildren !== void 0 ? nextChildren : EMPTY_MAP);
        let nextNode = Object.assign(Object.assign({}, existNode), { children });
        const { parentSlug } = nextNode;
        const parentNode = treeItemsIndex.get(parentSlug);
        if (parentNode) {
            nextNode = upsertChild(parentNode, nextNode, treeItemsIndex);
        }
        nextTree.set(nextNode.slug, nextNode);
    });
    return nextTree;
});

const selectHamburgerTreeWithCustomElementsIndex = createSelector(selectHamburgerTreeWithCustomElements, (tree) => {
    return collectTreeItemsFlat(tree);
});

const selectActiveSlug = createSelector(selectNavigationRoot, (navigationState) => navigationState.activeSlug);

const selectActivePath = createSelector(selectHamburgerTreeWithCustomElementsIndex, selectActiveSlug, (mixedIndex, activeSlug) => {
    var _a, _b;
    const path = new Set();
    let currentSlug = activeSlug;
    while (currentSlug) {
        path.add(currentSlug);
        currentSlug = (_b = (_a = mixedIndex.get(currentSlug)) === null || _a === void 0 ? void 0 : _a.parentSlug) !== null && _b !== void 0 ? _b : null;
    }
    return path;
});

const selectUserPermissionsLoading = createSelector(userPermissionsApi.endpoints.getUsersPermissions.select(), ({ isLoading }) => isLoading);

const usePlatformAccessGuard = () => {
    const { mode } = usePlatformContext();
    // Empty hamburger means there is no permissions for current user for platform
    const isHamburgerEmpty = useSelector(selectIsHamburgerEmpty);
    const hasLearnerPermission = useSelector(selectUserHasLearnerPermission);
    const userPermissionsLoading = useSelector(selectUserPermissionsLoading);
    if (userPermissionsLoading) {
        return;
    }
    if (mode === 'platform' && isHamburgerEmpty === true && !hasLearnerPermission) {
        // No need to redirect to somewhere, just show NoAccess component
        return true;
    }
    if (mode === 'platform' && isHamburgerEmpty === true && hasLearnerPermission) {
        console.log('[navigation] Redirecting to /learn due to empty hamburger & learner permission');
        window.location.href = '/learn';
    }
};

const useUserPermissionsContextRedux = ({ features }) => {
    useGetUsersPermissionsQuery(undefined, {
        skip: !features.isSwitchToLearnerAvailable,
    });
};

const defaultTranslations = {
    language: 'Language',
    myProfile: 'My profile',
    subscriptionBilling: 'Subscription and billing',
    signOut: 'Sign out',
    noAccessText: 'You have no access. Please contact the administrator.',
    learnerView: 'Learner view',
    goToManagementPortal: 'Go to management portal',
    Dashboard: 'Dashboard',
};

const useCreateContext = (props) => {
    const dispatch = useDispatch();
    const { supportedLocales, appPrefix, apiPrefix, cacheKey, mode = DEFAULT_MODE, handleAxiosError } = props;
    // TODO: mitasovr: think how it could be done without `useMemo`
    useMemo(() => {
        dispatch(setMode(mode));
    }, [dispatch, mode]);
    const features = FeaturesMap[mode];
    const handleAxiosErrorRef = useMutableRef(handleAxiosError);
    const activeSlug = useSelector(selectActiveSlug);
    const [setActiveSlugHandler] = useState(() => {
        return (nextSlug) => {
            dispatch(setActiveSlug(nextSlug));
        };
    });
    const [isNavOpen, setNavOpen] = useState(false); // TODO: mitasovr: move it to `usePlatformContext`
    const [rightSlot, setRightSlot] = useState(null);
    const [leftSlot, setLeftSlot] = useState(null);
    const [leftAvatarSlot, setLeftAvatarSlot] = useState(null);
    const [footerSlot, setFooterSlot] = useState(null);
    const [userName, setUserName] = useState(null);
    const [logoLink, setLogoLink] = useState(null);
    const [isTeamSwitcherExist, setTeamSwitcherExist] = useState(false);
    const [isTeamSwitcherVisible, setTeamSwitcherVisible] = useState(false);
    const [colorScheme, setColorScheme] = useState();
    const [translations, setTranslations] = useState(defaultTranslations);
    const logoutClickEvent = useSubEvent();
    useUserPermissionsContextRedux({ features });
    // Request: tenants settings
    const { tenantSettingsIndex, tenantSettingsLoading } = useTenantSettingsContextRedux({
        features,
    });
    const tenantSupportedLocales = useSelector(selectTenantLocales);
    const tenantDefaultLocale = useSelector(selectTenantDefaultLocale);
    const { availableLocales, localeChangeEvent, localeMenu, currentLocale, setLocale, selectedLocale } = useLocaleContext(supportedLocales, tenantSupportedLocales, tenantDefaultLocale);
    // createClient: second time we set the locale
    useMemo(() => {
        createClient({
            apiPrefix,
            locale: currentLocale,
            handleAxiosError: handleAxiosErrorRef.current,
        });
    }, [apiPrefix, currentLocale, handleAxiosErrorRef]);
    const { user, isLoading: userLoading } = useUsersMeContext();
    // @todo teams coupled with lms
    // TODO: rewrite it with RTK Query
    // TEAMS
    const { teams, selectedTeam, setSelectedTeam } = useTeamSwitcherContext({
        user,
        cacheKey,
        isTeamSwitcherExist,
    });
    // BREADCRUMBS
    const { breadcrumbs } = useBreadcrumbsContextRedux();
    // ACTIONS
    const { actionsLayers } = useActionsContext();
    // SATURATED TREE
    const { isTreeLoaded } = useHamburgerContextRedux(tenantSettingsLoading);
    const shouldShowAccessGuard = usePlatformAccessGuard();
    // SIDEBAR
    const { sidebarLayers } = useSidebarContextRedux();
    // CUSTOM ELEMENTS
    const { customElements } = useCustomElementsContextRedux();
    // ACTIVE SLUG
    const activePath = useSelector(selectActivePath);
    const { onLinkClick, linkClickEvent, navigationEvent } = usePlatformContext();
    // TODO: mitasovr: make context object not changeable, take care of rerendering
    // @todo god object found, solid,
    return useMemo(() => ({
        user,
        userLoading,
        mode,
        features,
        appPrefix,
        cacheKey,
        isNavOpen,
        setNavOpen,
        /**
         * @deprecated use `selectActiveSlug` instead
         */
        activeSlug,
        /**
         * @deprecated dispatch `setActiveSlug` action
         */
        setActiveSlug: setActiveSlugHandler,
        /**
         * @deprecated use `selectActivePath` instead
         */
        activePath,
        actionsLayers,
        breadcrumbs,
        sidebarLayers,
        availableLocales,
        localeChangeEvent,
        localeMenu,
        currentLocale,
        setLocale,
        selectedLocale,
        customElements,
        /**
         * @deprecated use `usePlatformContext` instead
         * `navigationEvent` happens when user clicks on any link in the navigation
         */
        navigationEvent,
        /**
         * @deprecated use `usePlatformContext` instead
         * `linkClickEvent` happens when user clicks on link related to the current app
         */
        linkClickEvent,
        logoutClickEvent,
        /**
         * @deprecated use `usePlatformContext` instead
         */
        onLinkClick,
        /**
         * @deprecated use `usePlatformContext` instead
         */
        tenantSettingsIndex,
        tenantSettingsLoading,
        rightSlot,
        setRightSlot,
        leftSlot,
        setLeftSlot,
        footerSlot,
        setFooterSlot,
        leftAvatarSlot,
        setLeftAvatarSlot,
        userName,
        setUserName,
        logoLink,
        setLogoLink,
        isTeamSwitcherExist,
        /**
         * @deprecated
         * it will be used feature flags soon, setTeamSwitcherExist is just for development process
         */
        setTeamSwitcherExist,
        teams,
        selectedTeam,
        setSelectedTeam,
        isTeamSwitcherVisible,
        setTeamSwitcherVisible,
        isTreeLoaded,
        colorScheme,
        setColorScheme,
        shouldShowAccessGuard,
        translations,
        setTranslations,
    }), [
        user,
        userLoading,
        mode,
        features,
        appPrefix,
        cacheKey,
        isNavOpen,
        activeSlug,
        setActiveSlugHandler,
        activePath,
        actionsLayers,
        breadcrumbs,
        sidebarLayers,
        availableLocales,
        localeChangeEvent,
        localeMenu,
        currentLocale,
        setLocale,
        selectedLocale,
        customElements,
        navigationEvent,
        linkClickEvent,
        logoutClickEvent,
        onLinkClick,
        tenantSettingsIndex,
        tenantSettingsLoading,
        rightSlot,
        leftSlot,
        footerSlot,
        leftAvatarSlot,
        userName,
        logoLink,
        isTeamSwitcherExist,
        teams,
        selectedTeam,
        setSelectedTeam,
        isTeamSwitcherVisible,
        isTreeLoaded,
        colorScheme,
        shouldShowAccessGuard,
        translations,
        setTranslations,
    ]);
};

const ReduxContextProvider = ({ children }) => {
    const parentContext = useContext(ReactReduxContext);
    const state = parentContext ? parentContext.store.getState() : {};
    const keys = Object.keys(state);
    const isRegistered = keys.includes(navigationPlatformApi.reducerPath);
    if (isRegistered) {
        return jsx(Fragment, { children: children });
    }
    if (!parentContext) {
        console.warn('Platform Navigation: Redux context provided. Make sure it\'s only context on the page.');
        return jsx(Provider, Object.assign({ store: navigationStore }, { children: children }));
    }
    throw new Error('Platform Navigation: Navigation Redux reducers should be connected to the Redux Store of the page.');
};

const useHubSpotScript = (mode) => {
    const hasLearnerPermission = useSelector(selectUserHasLearnerPermission);
    const userPermissionsLoading = useSelector(selectUserPermissionsLoading);
    useEffect(() => {
        const hubSpotId = window.ROLLUP_HUBSPOT_ID || 26717931;
        const skipByUserRole = userPermissionsLoading || hasLearnerPermission;
        if (mode !== 'LMS-1' || skipByUserRole)
            return;
        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.id = 'hs-script-loader';
        script.async = true;
        script.defer = true;
        script.src = `//js-eu1.hs-scripts.com/${hubSpotId}.js`;
        document.body.appendChild(script);
        return () => {
            document.body.removeChild(script);
        };
    }, [mode, hasLearnerPermission, userPermissionsLoading]);
};

const NavigationContext = createContext(null);
const ProvideNavigationContext = (props) => {
    const { children } = props;
    useHubSpotScript(props.mode);
    return (jsx(PlatformContextProvider, Object.assign({}, props, { children: jsx(ReduxContextProvider, { children: jsx(ProvideBreakpoints, { children: jsx(ProvideNavigationContextInner, Object.assign({}, props, { children: children })) }) }) })));
};
const ProvideNavigationContextInner = (props) => {
    const { children } = props;
    const contextValue = useCreateContext(props);
    return (jsx(ProvideBreakpoints, { children: jsx(NavigationContext.Provider, Object.assign({ value: contextValue }, { children: children })) }));
};

const useNavigationContext = () => {
    const context = useContext(NavigationContext);
    if (!context) {
        throw new Error('@platform/layout: useNavigationContext must be used within a ProvideNavigationContext');
    }
    return context;
};

// This hook is used in other applications, don't remove it
const useSetActiveSlug = (slug) => {
    const dispatch = useDispatch();
    useEffect(() => {
        dispatch(setActiveSlug(slug));
        return () => {
            dispatch(setActiveSlug(null));
        };
    }, [dispatch, slug]);
};

const ACTION_LOGOUT = 'auth/signout';
const clearAllStorage = () => {
    const cookies = document.cookie.split('; ');
    cookies.forEach((cookie) => {
        const name = cookie.split('=').shift();
        document.cookie = `${name}=; Expires=Thu, 01 Jan 1970 00:00:01 GMT; Path=/`;
    });
    Object.keys(window.localStorage).forEach((key) => {
        if (key !== ACTION_LOGOUT) {
            window.localStorage.removeItem(key);
        }
    });
    window.sessionStorage.clear();
};

var css_248z$G = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.NavComponent-module_menu__ShuoZ {\n  margin: 0;\n  padding: 0;\n  list-style: none;\n  display: flex;\n  position: relative;\n  z-index: 18;\n  font-size: var(--font-m);\n  margin: 0;\n}\n@media (max-width: 1279px) {\n  .NavComponent-module_menu__ShuoZ {\n    position: absolute;\n    top: calc(100% - var(--size-xxs));\n    flex-direction: column;\n    opacity: 0;\n    transform: translateY(-50%) scaleY(0);\n    transition: transform 0.2s ease-in-out, opacity 0.2s;\n    pointer-events: none;\n  }\n}\n\n@media (max-width: 1279px) {\n  .NavComponent-module_menuOpen__WVr-t {\n    opacity: 1;\n    transform: translateY(0) scaleY(1);\n    pointer-events: initial;\n  }\n}\n\n.NavComponent-module_item__HkSLW {\n  display: flex;\n  align-items: center;\n  margin: 0;\n  font-weight: var(--font-regular);\n}\n@media (min-width: 1280px) {\n  .NavComponent-module_item__HkSLW:not(:first-child) {\n    margin-inline-start: var(--size-xxs);\n  }\n  .NavComponent-module_breadcrumbs__QI0P2 .NavComponent-module_item__HkSLW {\n    margin: 0;\n  }\n  .NavComponent-module_breadcrumbs__QI0P2 .NavComponent-module_item__HkSLW:not(:first-child)::before {\n    content: \"/\";\n    display: flex;\n    align-items: center;\n    justify-content: center;\n    width: var(--size-l);\n    height: var(--size-l);\n    color: var(--uikit-line-new-20, #d6d6d6);\n    text-align: center;\n  }\n}\n@media (max-width: 1279px) {\n  .NavComponent-module_item__HkSLW {\n    margin-block-start: 2px;\n  }\n}\n\n.NavComponent-module_currentItem__MG9Ao {\n  position: relative;\n}\n\n.NavComponent-module_button__LTDxZ {\n  display: flex;\n  align-items: center;\n  margin: 0;\n  padding: 0;\n  border: none;\n  background: none;\n  color: inherit;\n  font-family: inherit;\n  font-size: inherit;\n  font-weight: inherit;\n  cursor: pointer;\n  gap: var(--size-xxxs);\n  font-weight: var(--font-semibold);\n  cursor: pointer;\n}\n\n.NavComponent-module_buttonWithArrow__mLWrv {\n  padding-inline-end: var(--size-xxs);\n}\n\n.NavComponent-module_link__A8uJn {\n  outline: 0;\n}\n@media (min-width: 1279px) {\n  .NavComponent-module_regular__Ov9Z1 .NavComponent-module_link__A8uJn:hover, .NavComponent-module_regular__Ov9Z1 .NavComponent-module_link__A8uJn:visited:hover {\n    background: var(--ui-button-state-hover, var(--color-primary-5));\n    color: var(--ui-button-state-hover-text-color);\n  }\n  .NavComponent-module_regular__Ov9Z1 .NavComponent-module_link__A8uJn:active, .NavComponent-module_regular__Ov9Z1 .NavComponent-module_link__A8uJn:visited:active {\n    background: var(--ui-button-state-active, var(--color-primary-15));\n    color: var(--ui-button-state-active-text-color);\n  }\n}\n.NavComponent-module_link__A8uJn, .NavComponent-module_link__A8uJn:visited {\n  color: var(--ui-breadcrumb-link-color, var(--uikit-text-new-primary, #141414));\n  line-height: 1.5;\n  text-decoration: none;\n}\n.NavComponent-module_breadcrumbs__QI0P2 .NavComponent-module_link__A8uJn, .NavComponent-module_breadcrumbs__QI0P2 .NavComponent-module_link__A8uJn:visited {\n  color: var(--ui-breadcrumb-link-color, var(--uikit-text-new-secondary, #676767));\n  text-decoration: underline;\n  text-underline-offset: 1px;\n  text-decoration-thickness: 1px;\n}\n.NavComponent-module_link__A8uJn:active {\n  color: var(--ui-breadcrumb-link-active-color, var(--uikit-decoration-new-red-60, #d4083b));\n}\n.NavComponent-module_link__A8uJn.NavComponent-module_active__87Ia6, .NavComponent-module_link__A8uJn.NavComponent-module_active__87Ia6:visited {\n  color: var(--ui-breadcrumb-link-active-color, var(--uikit-decoration-new-red-60, #d4083b));\n}\n\n.NavComponent-module_itemContent__wU9LI {\n  display: inline-flex;\n  align-items: center;\n  user-select: none;\n}\n.NavComponent-module_itemContent__wU9LI:not(.NavComponent-module_link__A8uJn) {\n  color: var(--uikit-text-new-primary, #141414);\n}\n@media (min-width: 1279px) {\n  .NavComponent-module_itemContent__wU9LI {\n    border-radius: 4px;\n    line-height: 1;\n  }\n}\n@media (max-width: 1279px) {\n  .NavComponent-module_itemContent__wU9LI {\n    --border-radius: 22px;\n    height: 44px;\n    padding: 0 var(--size-s);\n    border-radius: var(--border-radius);\n    background: var(--uikit-bg-c-default-soft, #efefef);\n    position: relative;\n  }\n  .NavComponent-module_itemContent__wU9LI::after {\n    position: absolute;\n    top: -1px;\n    right: -1px;\n    bottom: -1px;\n    left: -1px;\n    border: 1px solid transparent;\n    content: \"\";\n    border-radius: var(--border-radius);\n  }\n  .NavComponent-module_itemContent__wU9LI:hover {\n    background: var(--uikit-bg-c-default-soft-hovered, #d6d6d6);\n  }\n  .NavComponent-module_itemContent__wU9LI:active {\n    background: var(--uikit-bg-c-default-soft-pressed, #b8b8b8);\n  }\n  .NavComponent-module_itemContent__wU9LI:focus::after {\n    border-color: var(--uikit-border-c-focus, #3d73d8);\n  }\n}\n.NavComponent-module_regular__Ov9Z1 .NavComponent-module_itemContent__wU9LI {\n  font-weight: var(--font-semibold);\n}\n@media (min-width: 1279px) {\n  .NavComponent-module_regular__Ov9Z1 .NavComponent-module_itemContent__wU9LI {\n    padding: var(--size-xxxs);\n  }\n  .NavComponent-module_regular__Ov9Z1 .NavComponent-module_itemContent__wU9LI.NavComponent-module_active__87Ia6, .NavComponent-module_regular__Ov9Z1 .NavComponent-module_itemContent__wU9LI.NavComponent-module_active__87Ia6:visited {\n    color: var(--ui-breadcrumb-link-active-color, var(--uikit-decoration-new-red-60, #d4083b));\n  }\n}";
var styles$F = {"menu":"NavComponent-module_menu__ShuoZ","menuOpen":"NavComponent-module_menuOpen__WVr-t","item":"NavComponent-module_item__HkSLW","breadcrumbs":"NavComponent-module_breadcrumbs__QI0P2","currentItem":"NavComponent-module_currentItem__MG9Ao","button":"NavComponent-module_button__LTDxZ","buttonWithArrow":"NavComponent-module_buttonWithArrow__mLWrv","link":"NavComponent-module_link__A8uJn","regular":"NavComponent-module_regular__Ov9Z1","active":"NavComponent-module_active__87Ia6","itemContent":"NavComponent-module_itemContent__wU9LI"};
styleInject(css_248z$G);

const NavComponent = ({ items, mainItem, activeSlug = null, mode = 'regular' }) => {
    const { isNavOpen, setNavOpen } = useNavComponentContext();
    const { onLinkClick } = usePlatformContext();
    const ref = useRef(null);
    useClickAway(ref, () => {
        setNavOpen(false);
    });
    if (!items.length && !mainItem) {
        return null;
    }
    return (jsxs("div", Object.assign({ ref: ref, className: "header-nav" }, { children: [!!mainItem && (jsx("div", Object.assign({ className: styles$F.currentItem }, { children: jsxs("button", Object.assign({ className: cn(styles$F.link, styles$F.button, styles$F.buttonWithArrow), onClick: () => setNavOpen(!isNavOpen) }, { children: [mainItem.name, items.length > 0 && jsx(SvgComponent, { name: "ChevronSmallDownBoldStroke", size: "md" })] })) }))), items.length > 0 && (jsx("ul", Object.assign({ className: cn(styles$F.menu, {
                    [styles$F.menuOpen]: isNavOpen,
                    [styles$F.breadcrumbs]: mode === 'breadcrumbs',
                    [styles$F.regular]: mode === 'regular',
                }), role: "menu" }, { children: items.map((item) => (jsxs(Fragment$1, { children: [!!item.url && (jsx("li", Object.assign({ className: styles$F.item, "data-key": item.slug || item.name }, { children: jsx("a", Object.assign({ href: item.url, target: item.target !== undefined ? item.target : '_top', className: cn(styles$F.link, styles$F.itemContent, {
                                    [styles$F.active]: item.slug === activeSlug && mode !== 'breadcrumbs',
                                }), "data-slug": item.slug, onClick: (ev) => onLinkClick(ev, item) }, { children: item.name })) }))), !item.url && (jsx("li", Object.assign({ className: styles$F.item, "data-key": item.slug || item.name }, { children: jsx("span", Object.assign({ className: styles$F.itemContent }, { children: item.name })) })))] }, item.slug || item.name))) })))] })));
};

const useBreakpoints = () => {
    const context = useContext(BreakpointsContext);
    if (!context) {
        throw new Error('useBreakpoints: context not provided');
    }
    return context;
};

const getCollectionSlugs = (collection) => {
    const acc = new Set();
    if (!collection) {
        return acc;
    }
    return collection.reduce((acc, item) => {
        const slug = typeof item !== 'string' ? item.slug : item;
        return acc.add(slug);
    }, acc);
};

// getCollectionSlugs
const selectBreadcrumbsSlugs = createSelector(selectBreadcrumbsRoot, (state) => {
    return getCollectionSlugs(state.collection);
});

const splitLast = (items) => {
    const last = items.slice(-1)[0];
    return {
        last: last ? last : null,
        rest: items.slice(0, -1),
    };
};
const Breadcrumbs = () => {
    const breadcrumbsSlugs = useSelector(selectBreadcrumbsSlugs);
    const mixedIndex = useSelector(selectMixedIndex);
    const { fromDesktop } = useBreakpoints();
    const slugs = useMemo(() => {
        if (breadcrumbsSlugs.size < 1) {
            return null;
        }
        const items = Array.from(breadcrumbsSlugs.values());
        if (fromDesktop) {
            return {
                rest: items,
                last: null,
            };
        }
        return splitLast(Array.from(breadcrumbsSlugs.values()));
    }, [breadcrumbsSlugs, fromDesktop]);
    const result = useMemo(() => {
        var _a;
        if (!slugs) {
            return null;
        }
        const rest = slugs.rest
            .map((item) => mixedIndex.get(item))
            .filter((item) => !!item);
        return {
            rest,
            last: (_a = mixedIndex.get(slugs.last)) !== null && _a !== void 0 ? _a : null,
        };
    }, [slugs, mixedIndex]);
    if (!result) {
        return null;
    }
    return jsx(NavComponent, { items: result.rest, mainItem: result.last, mode: "breadcrumbs" });
};

const BRANDING_TENANT_SETTING_KEY = 'platform.branding';

const isObject = (item) => {
    return item && typeof item === 'object' && !Array.isArray(item);
};
// Define the function to merge objects deeply
const mergeDeep = (target, ...sources) => {
    if (!sources.length)
        return target;
    const source = sources.shift();
    if (isObject(target) && isObject(source)) {
        for (const key in source) {
            if (isObject(source[key])) {
                if (!target[key])
                    Object.assign(target, { [key]: {} });
                mergeDeep(target[key], source[key]);
            }
            else {
                Object.assign(target, { [key]: source[key] });
            }
        }
    }
    return mergeDeep(target, ...sources);
};

const useBrandingSettings = (setting, accessMode = 'logged') => {
    var _a, _b, _c;
    const isPublic = accessMode === 'public';
    const tenantSettingsIndex = useSelector(selectTenantSettingsMap);
    const publicTenantSettingsIndex = useSelector(selectPublicTenantSettingsMap);
    const index = isPublic ? publicTenantSettingsIndex : tenantSettingsIndex;
    const brandSettingsValue = ((_a = index.get(BRANDING_TENANT_SETTING_KEY)) === null || _a === void 0 ? void 0 : _a.value) || '{}';
    const allBrandingSettings = JSON.parse(brandSettingsValue);
    const commonBrandingSettings = (_b = allBrandingSettings.common) !== null && _b !== void 0 ? _b : {};
    const domainBrandingSettings = (_c = allBrandingSettings[getCurrentDomain()]) !== null && _c !== void 0 ? _c : {};
    const mergedSettings = mergeDeep(commonBrandingSettings, domainBrandingSettings);
    return mergedSettings[setting];
};

const DEFAULT_LOGO = logoConfig.logo;
const RenderLogo = ({ logoId }) => {
    const { iconId, height } = useMemo(() => {
        var _a;
        return (_a = logoConfig[logoId !== null && logoId !== void 0 ? logoId : '']) !== null && _a !== void 0 ? _a : DEFAULT_LOGO;
    }, [logoId]);
    return jsx(SvgComponent, { name: iconId, height: height });
};

const TenantLogo = ({ isHamburger }) => {
    const { mode } = usePlatformContext();
    const { loadTenantsSettings } = FeaturesMap[mode];
    const [requestTenantSettings, { isLoading }] = useLazyGetTenantSettingsQuery();
    useEffect(() => {
        if (!loadTenantsSettings) {
            return;
        }
        requestTenantSettings(undefined, true);
    }, [loadTenantsSettings, requestTenantSettings]);
    const regularLogoId = useBrandingSettings('logo');
    const logoOnHamburgerId = useBrandingSettings('logoOnHamburger');
    if (isLoading) {
        return null;
    }
    const logoId = isHamburger ? logoOnHamburgerId || regularLogoId : regularLogoId;
    return jsx(RenderLogo, { logoId: logoId });
};

const HOME_SATURATED_ITEM = {
    slug: 'homepage',
    name: 'Home Page',
    url: '/',
    icon_id: 'WarningBoldFilled',
    parentSlug: null,
    isSection: false,
};
const LinkedTenantLogo = ({ className, link }) => {
    var _a;
    const defaultPlatformUrl = useSelector(selectDefaultPlatformUrl);
    const { onLinkClick } = usePlatformContext();
    const tenantSettingsIndex = useSelector(selectTenantSettingsMap);
    const tenantSettingsCommonUrl = (_a = tenantSettingsIndex.get('platform.common.home_url')) === null || _a === void 0 ? void 0 : _a.value;
    const homeUrl = useMemo(() => {
        return link || tenantSettingsCommonUrl || defaultPlatformUrl;
    }, [link, defaultPlatformUrl, tenantSettingsCommonUrl]);
    const handleLinkClick = useCallback((ev) => {
        onLinkClick(ev, Object.assign(Object.assign({}, HOME_SATURATED_ITEM), { url: homeUrl }));
    }, [homeUrl, onLinkClick]);
    return (jsx("a", Object.assign({ href: homeUrl, target: "_top", "data-slug": HOME_SATURATED_ITEM.slug, className: className, onClick: handleLinkClick }, { children: jsx(TenantLogo, {}) })));
};

const Logo = ({ className }) => {
    const { logoLink } = useNavigationContext();
    return jsx(LinkedTenantLogo, { link: logoLink, className: className });
};

const useTranslation = () => {
    const { translations } = useNavigationContext();
    return {
        t: (key) => (translations === null || translations === void 0 ? void 0 : translations[key]) || key,
    };
};

const useUserMenuRoleSwitchItem = () => {
    const { mode } = usePlatformContext();
    const features = FeaturesMap[mode];
    const hasPlatformRole = useSelector(selectHasPlatformRole);
    const { t } = useTranslation();
    if (features.isSwitchToLearnerAvailable) {
        return null;
    }
    if (features.isSwitchToPlatformAvailable && hasPlatformRole === true) {
        return Object.assign(Object.assign({}, avatarMenuItem), { label: t(TO_PLATFORM_LABEL) });
    }
    return null;
};

var css_248z$F = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.UserMenu-module_dropdownSubMenu__bf6og {\n  max-height: 400px;\n  z-index: 5;\n}";
var styles$E = {"dropdownSubMenu":"UserMenu-module_dropdownSubMenu__bf6og"};
styleInject(css_248z$F);

const useUserMenuItems = () => {
    const { t } = useTranslation();
    return useMemo(() => {
        const LOGOUT = {
            label: t('signOut'),
            key: 'logout',
            testId: 'logout',
        };
        const PROFILE = {
            label: t('myProfile'),
            key: 'profile',
            testId: 'my-profile',
        };
        const BILLING = {
            label: t('subscriptionBilling'),
            key: 'billing',
            testId: 'billing',
        };
        return {
            LOGOUT,
            PROFILE,
            BILLING,
        };
    }, [t]);
};

const { UserContainer } = Header;
const { UserDefaultAvatar } = UserContainer;
const UserMenu = () => {
    const { t } = useTranslation();
    const { navigationEvent } = usePlatformContext();
    const { logoutClickEvent, setLocale, localeMenu, currentLocale, selectedLocale, user, userName, features, mode } = useNavigationContext();
    const defaultPlatformUrl = useSelector(selectDefaultPlatformUrl);
    const roleAvatarMenu = useUserMenuRoleSwitchItem();
    const { LOGOUT, PROFILE, BILLING } = useUserMenuItems();
    /* For now (05.08.2024), we show billing only in AP bundle */
    const isBillingEnabled = mode === 'LMS-1';
    const languageTranslation = t('language');
    const menuItems = useMemo(() => {
        const items = [];
        if (features.hasPlatformProfile)
            items.push(PROFILE);
        if (roleAvatarMenu)
            items.push(roleAvatarMenu);
        if (isBillingEnabled)
            items.push(BILLING);
        const languageLabel = localeMenu.label.replace('Language', languageTranslation);
        items.push(Object.assign(Object.assign({}, localeMenu), { label: languageLabel }));
        items.push(LOGOUT);
        return items;
    }, [roleAvatarMenu, isBillingEnabled, localeMenu, languageTranslation, features, LOGOUT, PROFILE, BILLING]);
    const customNavigate = useCallback((url, key) => {
        const isHandled = navigationEvent.emit({
            name: '',
            slug: `user-menu-${key.toLowerCase()}`,
            parentSlug: null,
            isSection: false,
            url,
        });
        if (isHandled)
            return;
        window.location.href = `${PLATFORM_URL}${url}`;
    }, [navigationEvent]);
    const handleMenuClick = useCallback((ev) => {
        const { key, keyPath } = ev;
        if (key === LOGOUT.key) {
            logoutClickEvent.emit();
            return;
        }
        if (key === PROFILE.key) {
            customNavigate('/profile', key);
            return;
        }
        // TODO: subject to change after billing is implemented
        if (key === BILLING.key) {
            customNavigate('/billing', key);
            return;
        }
        if (keyPath[keyPath.length - 1] === 'language') {
            setLocale(key);
            return;
        }
        if (key === ROLE_SWITCHER_SLUG) {
            console.log('useCreateContext: handleToPlatformClick: ROLE_SWITCHER_SLUG, redirect to:', defaultPlatformUrl);
            document.location = defaultPlatformUrl;
        }
    }, [LOGOUT.key, PROFILE.key, BILLING.key, defaultPlatformUrl, logoutClickEvent, setLocale, customNavigate]);
    const getUserName = () => {
        var _a, _b;
        if (userName)
            return userName;
        if (user)
            return `${(_a = user === null || user === void 0 ? void 0 : user.first_name) !== null && _a !== void 0 ? _a : ''} ${(_b = user === null || user === void 0 ? void 0 : user.last_name) !== null && _b !== void 0 ? _b : ''}`;
        return '';
    };
    return (jsx(UserDefaultAvatar, { items: menuItems, onSelect: handleMenuClick, userName: getUserName(), selectedKeys: [selectedLocale !== null && selectedLocale !== void 0 ? selectedLocale : currentLocale], submenuClassName: styles$E.dropdownSubMenu }));
};

var css_248z$E = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.DropdownOpener-module_teamSwitcherButton__GZA3u button {\n  font-family: \"Hauss\", sans-serif;\n  font-weight: 400;\n  font-size: 1rem;\n  line-height: 1.5rem;\n  letter-spacing: normal;\n}\n.DropdownOpener-module_teamSwitcherButton__GZA3u button:hover {\n  background: var(--uikit-bg-c-brand-secondary-hovered, rgba(20, 20, 20, 0.1019607843));\n  border-radius: 4px;\n}\n.DropdownOpener-module_teamSwitcherButton__GZA3u button:focus-within {\n  background: var(--uikit-bg-c-brand-secondary-pressed, rgba(20, 20, 20, 0.2));\n}";
var styles$D = {"teamSwitcherButton":"DropdownOpener-module_teamSwitcherButton__GZA3u"};
styleInject(css_248z$E);

const truncateString = (str = '', maxLength) => {
    if ((str === null || str === void 0 ? void 0 : str.length) <= maxLength) {
        return str;
    }
    return str.substring(0, maxLength) + '...';
};
const DropdownOpener = ({ isOpened, text, onClick }) => {
    return (jsx("div", Object.assign({ className: styles$D.teamSwitcherButton }, { children: jsx(InlineButton, { text: truncateString(text, 20), type: "button", onClick: onClick, iconAfterText: isOpened ? 'ChevronUpBoldStroke' : 'ChevronDownBoldStroke', iconBeforeText: "GroupBoldStroke" }) })));
};

var css_248z$D = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.TeamSwitcher-module_teamSwitcherContainer__1-aLn {\n  position: relative;\n  z-index: 1;\n}\n\n.TeamSwitcher-module_teamSwitcherDropdown__S8Avx {\n  top: calc(100% + 8px);\n  right: 0;\n  position: absolute;\n  min-width: max-content;\n  max-width: 300px;\n}\n\n.TeamSwitcher-module_teamSwitcherDropdownRootMenu__jdbwc {\n  max-height: 400px;\n  overflow-y: auto;\n  max-width: 250px;\n}";
var styles$C = {"teamSwitcherContainer":"TeamSwitcher-module_teamSwitcherContainer__1-aLn","teamSwitcherDropdown":"TeamSwitcher-module_teamSwitcherDropdown__S8Avx","teamSwitcherDropdownRootMenu":"TeamSwitcher-module_teamSwitcherDropdownRootMenu__jdbwc"};
styleInject(css_248z$D);

const MANAGE_TEAMS_KEY = 'manage-teams';
const createManageTeamsOption = () => ({
    label: 'Manage teams',
    key: MANAGE_TEAMS_KEY,
    beforeTextItemProps: { icon: 'GroupMediumStroke' },
    divider: true,
});
const TeamSwitcher = () => {
    const { navigationEvent } = usePlatformContext();
    const { teams, selectedTeam, setSelectedTeam, user } = useNavigationContext();
    const [isDropdownOpened, setDropdownOpened] = useState(false);
    const switcherRef = useRef(null);
    useClickAway(switcherRef, () => setDropdownOpened(false));
    const toggleDropdown = useCallback(() => setDropdownOpened((prev) => !prev), []);
    const handleSelectTeam = useCallback((teamId) => {
        if (!(user === null || user === void 0 ? void 0 : user.id))
            return;
        setCookie(SELECTED_TEAM_COOKIE_KEY(user.id), teamId, {
            path: '/',
            expires: COOKIE_EXPIRES,
        });
        const foundTeam = teams.find((team) => team.id === teamId);
        if (foundTeam) {
            setSelectedTeam(foundTeam);
        }
        toggleDropdown();
    }, [setSelectedTeam, teams, toggleDropdown, user === null || user === void 0 ? void 0 : user.id]);
    const customNavigate = useCallback((url, key) => {
        const isHandled = navigationEvent.emit({
            name: '',
            slug: `team-switcher-${key.toLowerCase()}`,
            parentSlug: null,
            isSection: false,
            url,
        });
        if (isHandled)
            return;
        window.location.href = `${PLATFORM_URL}${url}`;
    }, [navigationEvent]);
    const handleSelect = useCallback(({ key }) => {
        if (key === MANAGE_TEAMS_KEY) {
            customNavigate('/profile/teams', key);
            return;
        }
        handleSelectTeam(key);
    }, [handleSelectTeam, customNavigate]);
    const teamItems = useMemo(() => {
        const teamItems = [];
        teamItems.push(...teams.map(({ id, name }) => ({
            label: name,
            key: id,
            beforeTextItemProps: { text: name },
        })));
        teamItems.push(createManageTeamsOption());
        return teamItems;
    }, [teams]);
    const selectedTeamName = (selectedTeam === null || selectedTeam === void 0 ? void 0 : selectedTeam.name) || '';
    const defaultSelectedKeys = useMemo(() => ((selectedTeam === null || selectedTeam === void 0 ? void 0 : selectedTeam.id) ? [selectedTeam.id] : []), [selectedTeam]);
    return (jsxs("div", Object.assign({ className: styles$C.teamSwitcherContainer, ref: switcherRef }, { children: [jsx(DropdownOpener, { isOpened: isDropdownOpened, onClick: toggleDropdown, text: selectedTeamName }), isDropdownOpened && (jsx("div", Object.assign({ className: styles$C.teamSwitcherDropdown }, { children: jsx(DropdownList, { items: teamItems, onSelect: handleSelect, defaultSelectedKeys: defaultSelectedKeys, rootClassName: styles$C.teamSwitcherDropdownRootMenu }) })))] })));
};

function isInIframe() {
    try {
        return window.self !== window.top;
    }
    catch (e) {
        return true;
    }
}

let timeout;
const debounce = (func, wait) => {
    clearTimeout(timeout);
    timeout = setTimeout(func, wait);
};

const getTranslateXY = (element) => {
    const style = window.getComputedStyle(element);
    const matrix = new DOMMatrixReadOnly(style.transform);
    return {
        x: matrix.m41,
        y: matrix.m42,
    };
};

var css_248z$C = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.ImpersonationFloatingButton-module_wrapper__RRFvo {\n  z-index: 1000000;\n  position: fixed;\n  top: 0px;\n  left: 0px;\n  height: 100%;\n  width: 100%;\n  pointer-events: none;\n}\n.ImpersonationFloatingButton-module_wrapper__RRFvo div[class^=FloatingButton-module_wrapper_] {\n  border-radius: var(--size-xxs);\n  border: 1px solid var(--uikit-dark-fixed-line-new-10-inverse, #313131);\n  pointer-events: all;\n}\n.ImpersonationFloatingButton-module_wrapper__RRFvo div[class^=FloatingButton-module_dragIcon_] {\n  color: var(--uikit-dark-fixed-text-new-primary-inverse, #f3f3f3);\n  padding: var(--size-xxs) 0px var(--size-xxs) var(--size-xxs);\n}\n.ImpersonationFloatingButton-module_wrapper__RRFvo hr[class^=FloatingButton-module_separator_] {\n  display: none;\n}\n\n.ImpersonationFloatingButton-module_wrapperMinimized__vA5W7 div[class^=FloatingButton-module_dragIcon_] {\n  padding: var(--size-xxs);\n}\n\n.ImpersonationFloatingButton-module_container__RNpGP {\n  display: flex;\n  padding: var(--size-xxs) var(--size-xxs) var(--size-xxs) 0px;\n  align-items: center;\n  gap: var(--size-l);\n}\n\n.ImpersonationFloatingButton-module_buttonsContainer__h6-KU {\n  display: flex;\n  flex-direction: row;\n  align-items: center;\n  gap: var(--size-xxs);\n}\n.ImpersonationFloatingButton-module_buttonsContainer__h6-KU button[class^=IconButton-module_button_] {\n  border-radius: var(--size-xxxs);\n  background-color: var(--uikit-bg-c-negative, #d4083b);\n}\n\n.ImpersonationFloatingButton-module_viewingAsText__OXoLl {\n  color: var(--uikit-dark-fixed-text-new-primary-inverse, #f3f3f3);\n  font-size: var(--size-ss);\n  padding: 0px 6px;\n}\n\n.ImpersonationFloatingButton-module_viewingAsTextName__y49wa {\n  color: var(--uikit-dark-fixed-text-new-primary-inverse, #f3f3f3);\n  font-weight: 500;\n}\n\n.ImpersonationFloatingButton-module_hideText__4O98T {\n  color: var(--uikit-dark-fixed-text-new-primary-inverse, #f3f3f3);\n  padding: 0px 6px;\n}";
var styles$B = {"wrapper":"ImpersonationFloatingButton-module_wrapper__RRFvo","wrapperMinimized":"ImpersonationFloatingButton-module_wrapperMinimized__vA5W7","container":"ImpersonationFloatingButton-module_container__RNpGP","buttonsContainer":"ImpersonationFloatingButton-module_buttonsContainer__h6-KU","viewingAsText":"ImpersonationFloatingButton-module_viewingAsText__OXoLl","viewingAsTextName":"ImpersonationFloatingButton-module_viewingAsTextName__y49wa"};
styleInject(css_248z$C);

const ImpersonationFloatingButton = ({ onSignOut, user }) => {
    const wrapperRef = useRef(null);
    const [minimized, setMinimized] = useState(() => localStorage.getItem('impersonationButtonMinimized') === 'true');
    const [buttonPosition, setButtonPosition] = useState(() => {
        const { x, y } = JSON.parse(localStorage.getItem('impersonationButtonPosition') || '{}');
        return {
            x: x || (window.innerWidth - 360) / 2,
            y: y || window.innerHeight - 48 - 30,
        };
    });
    if (!user)
        return null;
    const name = [user.first_name, user.middle_name, user.last_name].filter(Boolean).join(' ');
    const handleMinimize = () => {
        setMinimized((minimized) => !minimized);
        localStorage.setItem('impersonationButtonMinimized', String(!minimized));
    };
    const handleDrag = () => {
        var _a, _b;
        const button = (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.childNodes) === null || _b === void 0 ? void 0 : _b[0];
        if (!button)
            return;
        debounce(() => {
            const { x, y } = getTranslateXY(button);
            setButtonPosition({ x, y });
            localStorage.setItem('impersonationButtonPosition', JSON.stringify({ x, y }));
        }, 300);
    };
    return (jsx("div", Object.assign({ ref: wrapperRef, className: cn(styles$B.wrapper, { [styles$B.wrapperMinimized]: minimized }) }, { children: jsx(FloatingButton, Object.assign({ onDrag: handleDrag, draggable: true, theme: "dark", orientation: "horizontal", defaultPosition: buttonPosition }, { children: jsxs("div", Object.assign({ className: styles$B.container }, { children: [!minimized && (jsxs("span", Object.assign({ className: styles$B.viewingAsText }, { children: ["Viewing as ", jsx("span", Object.assign({ className: styles$B.viewingAsTextName }, { children: name }))] }))), jsxs("div", Object.assign({ className: styles$B.buttonsContainer }, { children: [jsx(IconButton, { onClick: onSignOut, "aria-label": "Sign out", iconName: "ExitBoldStroke", size: "small" }), jsx(InlineButton, { onClick: handleMinimize, text: minimized ? 'Show' : 'Hide', color: "neutralWhite", size: "small" })] }))] })) })) })));
};

var css_248z$B = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.ImpersonationBorders-module_border__11oMu {\n  pointer-events: none;\n  z-index: 1000000;\n  position: fixed;\n  height: 100%;\n  width: 100%;\n  top: 0;\n  left: 0;\n  border-radius: 3px;\n  border: 4px solid var(--uikit-decoration-new-blue-60, #3d73d8);\n}";
var styles$A = {"border":"ImpersonationBorders-module_border__11oMu"};
styleInject(css_248z$B);

const ImpersonationBorders = () => {
    return jsx("div", { className: styles$A.border });
};

const CHANNEL_NAME = 'cookieChannel';
const COOKIE_SET = 'cookieSet';
const COOKIE_NAME = 'ap_impersonation';
const impersonationChannel = new BroadcastChannel(CHANNEL_NAME);
const useListenImpersonationChannel = (onRefresh) => {
    useEffect(() => {
        const prevCookie = getCookie(COOKIE_NAME);
        impersonationChannel.addEventListener('message', (event) => {
            const nextCookie = getCookie(COOKIE_NAME);
            const shouldRefresh = event.data === COOKIE_SET && prevCookie !== nextCookie;
            if (shouldRefresh) {
                onRefresh();
            }
        });
        return () => {
            impersonationChannel.removeEventListener('message', () => { });
        };
    }, [onRefresh]);
};

const useIsImpersonationMode = () => {
    const impersonationCookie = getCookie(COOKIE_NAME);
    useEffect(() => {
        impersonationChannel.postMessage(COOKIE_SET);
    }, [impersonationCookie]);
    return !!impersonationCookie;
};

const ImpersonationComponent = ({ onSignOut, user, insidePortal = false }) => {
    const isImpersonating = useIsImpersonationMode();
    if (!isImpersonating) {
        return null;
    }
    const isLti = isInIframe();
    if (isLti) {
        return null;
    }
    const Impersonation = (jsxs(Fragment, { children: [jsx(ImpersonationBorders, {}), jsx(ImpersonationFloatingButton, { onSignOut: onSignOut, user: user })] }));
    if (insidePortal) {
        return Impersonation;
    }
    return createPortal(Impersonation, document.body);
};

const HEADER_HEIGHT = 60;
const useHideHeaderOnScroll = () => {
    const { fromDesktop } = useBreakpoints();
    const [isScrolledDown, setIsScrolledDown] = useState(false);
    useEffect(() => {
        if (fromDesktop) {
            setIsScrolledDown(false);
            return;
        }
        let lastScrollY = window.scrollY;
        let ticking = false;
        const updateScrollDir = () => {
            const scrollY = window.scrollY;
            const scrollPosition = scrollY;
            const threshold = 0;
            if (Math.abs(scrollY - lastScrollY) < threshold) {
                ticking = false;
                return;
            }
            const isScrolledUp = scrollY < lastScrollY;
            lastScrollY = scrollY > 0 ? scrollY : 0;
            ticking = false;
            setIsScrolledDown(!isScrolledUp && scrollPosition > HEADER_HEIGHT);
        };
        const onScroll = () => {
            if (!ticking) {
                window.requestAnimationFrame(updateScrollDir);
                ticking = true;
            }
        };
        window.addEventListener('scroll', onScroll);
        return () => window.removeEventListener('scroll', onScroll);
    }, [fromDesktop, setIsScrolledDown]);
    return isScrolledDown;
};

var css_248z$A = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.Headline-module_header__gxy-D {\n  --header-burger-width: var(--layout-sidebar-width);\n  --header-side-gap: var(--size-m);\n  --header-border-color: var(--layout-border-color);\n  --header-background-color: var(--layout-header-color);\n  grid-area: header;\n  display: flex;\n  align-items: stretch;\n  position: sticky;\n  top: 0;\n  z-index: 10;\n  background: var(--header-background-color);\n  transition: transform 0.2s ease-in-out;\n}\n\n.Headline-module_hidden__wuDdf {\n  transform: translateY(-100%);\n}\n\n.Headline-module_burger__ue1Zh {\n  flex: 0 0 auto;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  background-color: var(--ui-left-bar-background-color);\n}\n@media (max-width: 1279px) {\n  .Headline-module_burger__ue1Zh {\n    margin-inline: var(--layout-inline-gap) 2px;\n    background-color: unset;\n  }\n}\n@media (min-width: 1280px) {\n  .Headline-module_burger__ue1Zh {\n    --ui-icon-button-tertiary-default: var(--color-common);\n    flex: 0 0 var(--header-burger-width);\n    border-inline-end: 1px solid var(--header-border-color);\n  }\n}\n\n.Headline-module_main__xxuE1 {\n  flex: 1;\n  display: flex;\n  align-items: stretch;\n  justify-content: space-between;\n  padding-inline: var(--header-side-gap);\n  box-shadow: inset 0 -2px 0 -1px var(--header-border-color);\n}\n@media (max-width: 1279px) {\n  .Headline-module_main__xxuE1 {\n    padding-inline: 0;\n    padding-inline-end: var(--header-side-gap);\n    box-shadow: none;\n  }\n}\n\n.Headline-module_logo__Bp0iy {\n  display: flex;\n  align-items: center;\n}\n@media (max-width: 1279px) {\n  .Headline-module_logo__Bp0iy {\n    display: none;\n  }\n}\n\n.Headline-module_nav__mc86o {\n  flex: 1;\n  display: flex;\n  align-items: center;\n  position: relative;\n  font-size: var(--font-m);\n  font-weight: var(--font-semibold);\n}\n@media (min-width: 1280px) {\n  .Headline-module_nav__mc86o {\n    margin-inline: var(--size-xxl) var(--header-side-gap);\n  }\n}\n\n.Headline-module_actions__OO8cC {\n  display: flex;\n  align-items: center;\n  margin-inline-end: var(--header-side-gap);\n}\n\n.Headline-module_user__vpxrS {\n  display: flex;\n  align-items: center;\n  gap: var(--size-xs);\n}";
var styles$z = {"header":"Headline-module_header__gxy-D","hidden":"Headline-module_hidden__wuDdf","burger":"Headline-module_burger__ue1Zh","main":"Headline-module_main__xxuE1","logo":"Headline-module_logo__Bp0iy","nav":"Headline-module_nav__mc86o","actions":"Headline-module_actions__OO8cC","user":"Headline-module_user__vpxrS"};
styleInject(css_248z$A);

const Sections = () => {
    const { fromDesktop } = useBreakpoints();
    const activeSlug = useSelector(selectActiveSlug);
    const mixedIndex = useSelector(selectMixedIndex);
    const item = mixedIndex.get(activeSlug);
    const result = useMemo(() => {
        var _a;
        if (!(item === null || item === void 0 ? void 0 : item.parentSlug)) {
            return null;
        }
        const parentItem = mixedIndex.get(item.parentSlug);
        if (!(parentItem === null || parentItem === void 0 ? void 0 : parentItem.children)) {
            return null;
        }
        if (fromDesktop) {
            return {
                items: Array.from(parentItem.children.values()),
                mainItem: null,
            };
        }
        const mainItem = (_a = parentItem.children.get(activeSlug)) !== null && _a !== void 0 ? _a : null;
        const nextItems = new Map([...parentItem.children]);
        nextItems.delete(activeSlug);
        return {
            items: Array.from(nextItems.values()),
            mainItem,
        };
    }, [activeSlug, fromDesktop, item === null || item === void 0 ? void 0 : item.parentSlug, mixedIndex]);
    if (!(result === null || result === void 0 ? void 0 : result.items) || !activeSlug) {
        return null;
    }
    return jsx(NavComponent, { items: result.items, mainItem: result.mainItem, activeSlug: activeSlug });
};

const selectLastActions = createDraftSafeSelector(selectActionsRoot, (state) => {
    var _a;
    return (_a = state.collection[state.collection.length - 1]) !== null && _a !== void 0 ? _a : null;
});

var css_248z$z = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.ChatWindow-module_header__CeUgA {\n  font-family: \"Hauss\", sans-serif;\n  font-weight: 500;\n  font-size: 1rem;\n  line-height: 1.5rem;\n  letter-spacing: normal;\n}";
var styles$y = {"header":"ChatWindow-module_header__CeUgA"};
styleInject(css_248z$z);

var css_248z$y = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.bottom-sheet-open {\n  scrollbar-gutter: auto;\n  --ui-drawer-z-index: 1000;\n}\n.bottom-sheet-open .bottom-sheet-content section {\n  overflow-y: auto;\n  scrollbar-gutter: auto;\n}\n\n.BottomSheetWrapper-module_modalHeader__sJeWs {\n  display: flex;\n  justify-content: space-between;\n  width: 100%;\n  align-items: center;\n}\n\n.BottomSheetWrapper-module_hidden__3gW-Y {\n  display: none;\n}\n\n.BottomSheetWrapper-module_content__0AUN0 {\n  height: 100%;\n  overflow: auto;\n  padding: var(--size-m);\n}\n.BottomSheetWrapper-module_content__0AUN0.BottomSheetWrapper-module_noPadding__lgeiI {\n  padding: 0;\n}";
var styles$x = {"modalHeader":"BottomSheetWrapper-module_modalHeader__sJeWs","hidden":"BottomSheetWrapper-module_hidden__3gW-Y","content":"BottomSheetWrapper-module_content__0AUN0","noPadding":"BottomSheetWrapper-module_noPadding__lgeiI"};
styleInject(css_248z$y);

const BottomSheetWrapper = ({ enableScrollBarGutter, noPadding, items: itemsProp, isOpen, onClose, }) => {
    const items = useMemo(() => itemsProp.map((item) => ({
        header: ({ currentLevel, goToNextLevel }) => (jsx("div", Object.assign({ className: styles$x.modalHeader }, { children: item.header({ currentLevel, goToNextLevel }) }))),
        content: ({ currentLevel, goToNextLevel }) => (jsx("div", Object.assign({ className: cn(styles$x.content, {
                [styles$x.noPadding]: noPadding,
            }) }, { children: item.content({ currentLevel, goToNextLevel }) }))),
    })), [itemsProp, noPadding]);
    return (jsx("div", Object.assign({ className: isOpen ? '' : styles$x.hidden }, { children: jsx(BottomSheet, { items: items, onClose: onClose, isOpen: isOpen, enableScrollBarGutter: enableScrollBarGutter }) })));
};

function filterUnread(message) {
    return message.unread !== undefined && !message.disableNotifications;
}
// https://developer.rocket.chat/reference/api/schema-definition/message
const useChatUnreadMessagesMap = (chatLoginData) => {
    const { user_id, auth_token, chat_base_url } = chatLoginData || {};
    const [unreadMessagesMap, setUnreadMessagesMap] = useState({});
    useEffect(() => {
        if (!user_id || !auth_token) {
            return;
        }
        const chatDomain = chat_base_url === null || chat_base_url === void 0 ? void 0 : chat_base_url.replace(new RegExp('https?://'), '');
        const socket = new WebSocket(`wss://${chatDomain}/websocket`);
        socket.addEventListener('open', () => {
            const connectRequest = {
                msg: 'connect',
                version: '1',
                support: ['1', 'pre2', 'pre1'],
            };
            // Send authentication payload
            socket.send(JSON.stringify(connectRequest));
        });
        socket.addEventListener('message', (event) => {
            const message = JSON.parse(event.data);
            if (message.msg === 'error') {
                throw new Error(message.reason);
            }
            if (message.msg === 'ping') {
                socket.send(JSON.stringify({
                    msg: 'pong',
                }));
                return;
            }
            if (message.msg === 'connected') {
                const authRequest = {
                    msg: 'method',
                    method: 'login',
                    id: 'authenticate',
                    params: [{ resume: auth_token }],
                };
                socket.send(JSON.stringify(authRequest));
                return;
            }
            if (message.msg !== 'result' && message.msg !== 'changed') {
                // no action console it if need debug
                return;
            }
            switch (message.id) {
                case 'authenticate':
                    // Successfully authenticated, now get unread message count
                    // this is initial request
                    const getUnreadPayload = {
                        msg: 'method',
                        method: 'subscriptions/get',
                        id: 'getUnreadCount',
                        params: [{ unread: true }],
                    };
                    socket.send(JSON.stringify(getUnreadPayload));
                    // this is subscription request
                    const subscribeToNotificationsPayload = {
                        msg: 'sub',
                        id: 'unreadCountSubscription',
                        name: 'stream-notify-user',
                        params: [`${user_id}/subscriptions-changed`, false],
                    };
                    socket.send(JSON.stringify(subscribeToNotificationsPayload));
                    break;
                // this works, don't know why it has id = "id"
                case 'id': // 'unreadCountSubscription':
                    // Unread message count response
                    message.fields.args.filter(filterUnread).forEach(({ unread, rid }) => {
                        setUnreadMessagesMap((unreadMap) => (Object.assign(Object.assign({}, unreadMap), { [rid]: unread })));
                    });
                    break;
                case 'getUnreadCount':
                    // Unread message count response
                    message.result.filter(filterUnread).forEach(({ unread, rid }) => {
                        setUnreadMessagesMap((unreadMap) => (Object.assign(Object.assign({}, unreadMap), { [rid]: unread })));
                    });
                    break;
            }
        });
        return () => {
            socket.close();
        };
    }, [user_id, auth_token, chat_base_url, setUnreadMessagesMap]);
    return { unreadMessagesMap };
};

var css_248z$x = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.BreadcrumbsNarrow-module_bottomSheetWrapper__Ha0fq {\n  position: relative;\n  z-index: 30;\n}\n\n.BreadcrumbsNarrow-module_contentWrapper__gjyyw {\n  padding: var(--size-xxl) var(--size-s);\n}\n.BreadcrumbsNarrow-module_contentWrapper__gjyyw li:last-child > div {\n  pointer-events: none;\n}\n\n.BreadcrumbsNarrow-module_menuWrapper__j1KMM {\n  display: inline-flex;\n  overflow: hidden;\n}\n\n.BreadcrumbsNarrow-module_lastItemWrapper__c6DRu {\n  margin: 0;\n  display: inline-flex;\n  justify-content: center;\n  align-items: center;\n}\n.BreadcrumbsNarrow-module_lastItemWrapper__c6DRu a {\n  color: var(--uikit-text-new-primary, #141414);\n  text-decoration: none;\n  pointer-events: none;\n}\n.BreadcrumbsNarrow-module_lastItemWrapper__c6DRu:not(:first-child)::before {\n  content: \"/\";\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  width: var(--size-l);\n  height: var(--size-l);\n  color: var(--ui-breadcrumb-seperator-color, var(--uikit-line-new-20, #d6d6d6));\n  text-align: center;\n}\n\n.BreadcrumbsNarrow-module_tableHeader__hPUMx {\n  font-family: \"Hauss\", sans-serif;\n  font-weight: 500;\n  font-size: 1rem;\n  line-height: 1.5rem;\n  letter-spacing: normal;\n}\n\n.BreadcrumbsNarrow-module_bottomSheetWrapperBreadcrumbItem__fshUi a {\n  color: var(--uikit-text-new-primary, #141414);\n  text-decoration: none;\n}\n\n.BreadcrumbsNarrow-module_buttonWrapper__XFRif {\n  display: flex;\n  align-items: center;\n}\n\n.BreadcrumbsNarrow-module_itemWithIconWrapper__95EsY {\n  display: flex;\n  gap: var(--size-xxs);\n  align-items: center;\n}\n\n.BreadcrumbsNarrow-module_iconWrapper__-BiBO,\n.BreadcrumbsNarrow-module_nameWrapper__eq-Kw {\n  display: flex;\n}\n\nhtml[dir=rtl] .BreadcrumbsNarrow-module_iconWrapper__-BiBO {\n  transform: scaleX(-1);\n}";
var styles$w = {"bottomSheetWrapper":"BreadcrumbsNarrow-module_bottomSheetWrapper__Ha0fq","contentWrapper":"BreadcrumbsNarrow-module_contentWrapper__gjyyw","menuWrapper":"BreadcrumbsNarrow-module_menuWrapper__j1KMM","lastItemWrapper":"BreadcrumbsNarrow-module_lastItemWrapper__c6DRu","tableHeader":"BreadcrumbsNarrow-module_tableHeader__hPUMx","bottomSheetWrapperBreadcrumbItem":"BreadcrumbsNarrow-module_bottomSheetWrapperBreadcrumbItem__fshUi","buttonWrapper":"BreadcrumbsNarrow-module_buttonWrapper__XFRif","itemWithIconWrapper":"BreadcrumbsNarrow-module_itemWithIconWrapper__95EsY","iconWrapper":"BreadcrumbsNarrow-module_iconWrapper__-BiBO","nameWrapper":"BreadcrumbsNarrow-module_nameWrapper__eq-Kw"};
styleInject(css_248z$x);

const PORTAL_USER_MENU = 'user-menu-portal';
const PORTAL_LANGUAGE_MENU = 'language-menu-portal';
const PORTAL_HAMBURGER_MENU = 'hamburger-menu-portal';
const PORTAL_LOGO = 'logo-portal';
const PORTAL_USER_AVATAR = 'user-avatar-portal';
const PORTAL_HEADER_MENU = 'header-menu-portal';
const PORTAL_HEADER_ACTIONS = 'header-actions-portal';
const PORTAL_HEADER_CHAT = 'header-actions-chat';
const PORTAL_IMPERSONATION = 'impersonation-portal';
const PORTAL_HEADER_MENU_RIGHT_SLOT = 'header-menu-portal-right-slot';

const useCreatePortalContext = () => {
    const [mountPoints, setMountPoints] = useState(new Map());
    const [emptyStatus, setEmptyStatusState] = useState(new Map());
    const mount = useCallback((name, element) => {
        setMountPoints((prev) => {
            const prevElement = prev.get(name);
            if (prevElement === element) {
                return prev;
            }
            const next = new Map(prev);
            next.set(name, element);
            return next;
        });
        return {
            unmount: () => {
                setMountPoints((prev) => {
                    const prevElement = prev.get(name);
                    if (!!prevElement && prevElement !== element) {
                        // if unmount is called, when portal is already remounted with another element
                        return prev;
                    }
                    const next = new Map(prev);
                    next.delete(name);
                    return next;
                });
            },
        };
    }, []);
    const setEmptyStatus = useCallback((name, status) => {
        setEmptyStatusState((prev) => {
            const prevStatus = prev.get(name);
            if (prevStatus === status) {
                return prev;
            }
            const next = new Map(prev);
            next.set(name, status);
            return next;
        });
    }, []);
    return {
        mount,
        mountPoints,
        emptyStatus,
        setEmptyStatus,
    };
};

const PortalContext = createContext(null);
const PortalContextProvider = ({ children }) => {
    const context = useCreatePortalContext();
    return (jsx(PortalContext.Provider, Object.assign({ value: context }, { children: children })));
};

const usePortalContext = () => {
    const context = useContext(PortalContext);
    if (!context) {
        throw new Error('PortalContext provider is missing');
    }
    return context;
};

const usePortal = (name, customMountPoint) => {
    const { mountPoints, setEmptyStatus } = usePortalContext();
    const mountPoint = mountPoints.get(name) || null;
    const Portal = useMemo(() => {
        const Component = ({ children }) => {
            const hasChildren = Children.count(children) !== 0;
            useEffect(() => {
                setEmptyStatus(name, hasChildren);
                return () => {
                    setEmptyStatus(name, false);
                };
            }, [hasChildren]);
            if (!mountPoint || !hasChildren) {
                return null;
            }
            return createPortal(children, customMountPoint || mountPoint);
        };
        return Component;
    }, [customMountPoint, mountPoint, name, setEmptyStatus]);
    return Portal;
};

const PORTAL_BOTTOM_SHEET_USER_MENU = 'bottom-sheet-user-menu-portal';
const PORTAL_BOTTOM_SHEET_TOC = 'bottom-sheet-toc-portal';
const PORTAL_BOTTOM_SHEET_BREADCRUMBS = 'bottom-sheet-breadcrumbs-portal';
const PORTAL_TOC = 'toc-portal';
const PORTAL_CONTENT_HEADER = 'content-header-portal';
const PORTAL_SIDE_BAR = 'side-bar-portal';
const PORTAL_TOP_BAR = 'top-bar-portal';

const BottomSheetContent = memo(({ data, handleClickBreadcrumbMenuItem, clickableRefs }) => {
    const getLastChildId = () => {
        // if data is not defined
        if (!data)
            return null;
        const children = data.children;
        // if passed children is not an array
        if (!children || !Array.isArray(children))
            return null;
        const length = children.length;
        const lastChild = children[length - 1];
        // if length of arr is 0, lastChild will be null
        if (!lastChild)
            return null;
        return lastChild.id;
    };
    const lastChildId = getLastChildId();
    // filter falsy values
    const selectedIds = [lastChildId].filter(Boolean);
    return (jsx("div", Object.assign({ className: styles$w.contentWrapper }, { children: jsx(SelectableListMenu, Object.assign({ data: data, size: "large", onClickMenuItem: handleClickBreadcrumbMenuItem, selectedIds: selectedIds }, { children: ({ element, posinset }) => (jsx("div", Object.assign({ ref: clickableRefs[element.id], className: styles$w.bottomSheetWrapperBreadcrumbItem }, { children: posinset === 1 ? (element.name) : (jsxs("div", Object.assign({ className: styles$w.itemWithIconWrapper }, { children: [jsx(SelectableListMenu.CustomIcon, { wrapperSize: 16, wrapperProps: { icon: 'DownwardsArrowBoldStroke' }, className: styles$w.iconWrapper }), jsx("span", Object.assign({ className: styles$w.nameWrapper }, { children: element.name }))] }))) }))) })) })));
});
const BreadcrumbsNarrow = memo(({ children, title }) => {
    var _a, _b, _c;
    const [isOpen, setIsOpen] = useState(false);
    const onClose = useCallback(() => {
        setIsOpen(false);
    }, []);
    const data = useMemo(() => {
        const childArray = Children.map(children, (child, index) => {
            if (!isValidElement(child)) {
                return null;
            }
            return {
                id: `breadcrumb-${index}`,
                name: child.props.children,
            };
        });
        return {
            id: 'root-breadcrumb',
            name: 'root-breadcrumb',
            children: childArray !== null && childArray !== void 0 ? childArray : [],
        };
    }, [children]);
    const clickableRefs = useMemo(() => (data.children || []).reduce((acc, currentValue) => {
        acc[currentValue.id] = createRef();
        return acc;
    }, {}), [data.children]);
    const handleSelectBreadcrumbMenuItem = useCallback((item) => {
        var _a;
        const id = item.element.id;
        (_a = clickableRefs[id].current.querySelector('a')) === null || _a === void 0 ? void 0 : _a.click();
        onClose();
    }, [clickableRefs, onClose]);
    const bottomSheetItems = useMemo(() => [
        {
            header: title && jsx("div", Object.assign({ className: styles$w.tableHeader }, { children: title })),
            content: () => (jsx(BottomSheetContent, { data: data, handleClickBreadcrumbMenuItem: handleSelectBreadcrumbMenuItem, clickableRefs: clickableRefs })),
        },
    ], [title, data, handleSelectBreadcrumbMenuItem, clickableRefs]);
    const HeaderMenuPortal = usePortal(PORTAL_HEADER_MENU);
    const BottomSheetPortal = usePortal(PORTAL_BOTTOM_SHEET_BREADCRUMBS);
    return (jsxs(Fragment, { children: [jsx(HeaderMenuPortal, { children: jsxs("div", Object.assign({ className: styles$w.menuWrapper }, { children: [((_a = data === null || data === void 0 ? void 0 : data.children) === null || _a === void 0 ? void 0 : _a.length) && data.children.length > 1 ? (jsx("div", Object.assign({ className: styles$w.buttonWrapper }, { children: jsx(InlineButton, { color: "neutral", type: "button", icon: "MoreHorizontalBoldStroke", size: "small", "aria-label": "Breadcrumbs menu", onClick: () => setIsOpen(true) }) }))) : null, jsx("div", Object.assign({ className: styles$w.lastItemWrapper }, { children: ((_b = data === null || data === void 0 ? void 0 : data.children) === null || _b === void 0 ? void 0 : _b.length) ? (_c = data.children[data.children.length - 1]) === null || _c === void 0 ? void 0 : _c.name : null }))] })) }), jsx(BottomSheetPortal, { children: jsx("div", Object.assign({ className: styles$w.bottomSheetWrapper }, { children: jsx(BottomSheet, { items: bottomSheetItems, isOpen: isOpen, enableScrollBarGutter: true, onClose: onClose }) })) })] }));
});

var css_248z$w = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.BreadcrumbsWide-module_breadcrumbsWide__uBLhd {\n  display: flex;\n  align-items: center;\n  padding-inline-start: var(--size-l);\n  padding-inline-end: var(--size-l);\n}";
var styles$v = {"breadcrumbsWide":"BreadcrumbsWide-module_breadcrumbsWide__uBLhd"};
styleInject(css_248z$w);

const BreadcrumbsWide = ({ children }) => {
    const HeaderMenuPortal = usePortal(PORTAL_HEADER_MENU);
    return (jsx(HeaderMenuPortal, { children: jsx("div", Object.assign({ className: styles$v.breadcrumbsWide }, { children: children })) }));
};

const BreadcrumbsPlugin = ({ children, title }) => {
    const { fromDesktop } = useBreakpoints();
    if (!children) {
        return null;
    }
    if (fromDesktop) {
        return jsx(BreadcrumbsWide, { children: children });
    }
    return jsx(BreadcrumbsNarrow, Object.assign({ title: title }, { children: children }));
};

var css_248z$v = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.BreadcrumbItemComponent-module_breadcrumbItemComponent__y7i2k:first-of-type a {\n  white-space: nowrap;\n}\n.BreadcrumbItemComponent-module_breadcrumbItemComponent__y7i2k:last-of-type a {\n  color: var(--uikit-text-new-primary, #141414);\n  text-decoration: none;\n  pointer-events: none;\n}\n.BreadcrumbItemComponent-module_breadcrumbItemComponent__y7i2k:not(:last-of-type) {\n  display: flex;\n  align-items: center;\n}\n.BreadcrumbItemComponent-module_breadcrumbItemComponent__y7i2k:not(:last-of-type) a {\n  color: var(--ui-breadcrumb-link-color, var(--uikit-text-new-secondary, #676767));\n  text-decoration: var(--ui-breadcrumb-link-decoration, underline);\n  text-underline-offset: 1px;\n  text-decoration-thickness: 1px;\n}\n.BreadcrumbItemComponent-module_breadcrumbItemComponent__y7i2k:not(:last-of-type)::after {\n  content: \"/\";\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: var(--size-l);\n  height: var(--size-l);\n  color: var(--ui-breadcrumb-seperator-color, var(--uikit-line-new-20, #d6d6d6));\n  opacity: var(--ui-breadcrumb-seperator-opacity, 1);\n  text-align: center;\n}\n.BreadcrumbItemComponent-module_breadcrumbItemComponent__y7i2k:not(:first-of-type) a, .BreadcrumbItemComponent-module_breadcrumbItemComponent__y7i2k:not(:first-of-type) span {\n  display: -webkit-box;\n  -webkit-line-clamp: 1;\n  line-clamp: 1;\n  -webkit-box-orient: vertical;\n  -moz-box-orient: vertical;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  word-break: break-all;\n}";
var style = {"breadcrumbItemComponent":"BreadcrumbItemComponent-module_breadcrumbItemComponent__y7i2k"};
styleInject(css_248z$v);

// children is <a> tag
const BreadcrumbItemComponent = ({ children }) => {
    const { fromTablet } = useBreakpoints();
    if (fromTablet) {
        return jsx("div", Object.assign({ className: style.breadcrumbItemComponent }, { children: children }));
    }
    return (jsx(Fragment, { children: children }));
};

var css_248z$u = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.DropdownItemComponent-module_menuItem__ncRPh {\n  --menu-item-space-after-item: 12px;\n  font-family: \"Hauss\", sans-serif;\n  font-weight: 400;\n  font-size: 1rem;\n  line-height: 1.5rem;\n  letter-spacing: normal;\n  --menu-item-height: 40px;\n  --menu-item-vertical-padding: 8px;\n  --menu-item-text-color: var(--ui-dropdown-button-text-color, var(--uikit-text-new-primary, #141414));\n  --menu-item-background-color: var(--ui-dropdown-button-bg-color, var(--uikit-bg-c-default, #ffffff));\n  --menu-item-min-width: var(--ui-menu-item-min-width, 140px);\n  box-sizing: border-box;\n  cursor: pointer;\n  padding: var(--menu-item-vertical-padding) 16px;\n  height: var(--menu-item-height);\n  background-color: var(--menu-item-background-color);\n  min-width: var(--menu-item-min-width);\n  color: var(--menu-item-text-color);\n  width: 100%;\n  display: inline-flex;\n}\n.DropdownItemComponent-module_menuItem__ncRPh:hover:not(:is(.DropdownItemComponent-module_menuItemDisabled__QKuoV, .DropdownItemComponent-module_menuItemChecked__wenjV)) {\n  --menu-item-background-color: var(--ui-button-state-hover, var(--uikit-bg-c-default-hovered, #efefef));\n}\n.DropdownItemComponent-module_menuItem__ncRPh:active:not(:is(.DropdownItemComponent-module_menuItemDisabled__QKuoV, .DropdownItemComponent-module_menuItemChecked__wenjV)) {\n  --menu-item-background-color: var(--ui-button-state-active, var(--uikit-bg-c-default-pressed, #d6d6d6));\n}\n.DropdownItemComponent-module_menuItem__ncRPh:focus-visible {\n  outline: 2px solid var(--uikit-border-c-focus, #3d73d8);\n  outline-offset: -2px;\n}\n\n.DropdownItemComponent-module_menuItemSmall__Aj3B- {\n  --menu-item-space-after-item: 8px;\n  font-family: \"Hauss\", sans-serif;\n  font-weight: 400;\n  font-size: 0.875rem;\n  line-height: 1.25rem;\n  letter-spacing: normal;\n  --menu-item-height: 32px;\n  --menu-item-vertical-padding: 6px;\n}\n\n.DropdownItemComponent-module_menuItemDisabled__QKuoV {\n  --menu-item-text-color: var(--uikit-text-on-c-disabled, #b8b8b8);\n  --menu-item-background-color: var(--uikit-bg-c-default, #ffffff);\n  cursor: not-allowed;\n}\n\n.DropdownItemComponent-module_menuItemChecked__wenjV {\n  --menu-item-background-color: var(--ui-dropdown-button-active-bg-color, var(--uikit-bg-c-default-soft, #efefef));\n}\n.DropdownItemComponent-module_menuItemChecked__wenjV:hover {\n  --menu-item-background-color: var(--uikit-bg-c-default-soft-hovered, #d6d6d6);\n}\n.DropdownItemComponent-module_menuItemChecked__wenjV:active {\n  --menu-item-background-color: var(--uikit-bg-c-default-soft-pressed, #b8b8b8);\n}\n\n.DropdownItemComponent-module_itemBeforeText__pVh2B {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  margin-inline-end: var(--menu-item-space-after-item);\n}\n\n.DropdownItemComponent-module_content__uUm7A {\n  display: inline-flex;\n  align-items: center;\n  box-sizing: border-box;\n  margin-inline-end: auto;\n  width: max-content;\n}\n\n.DropdownItemComponent-module_icon__h-RdM {\n  display: inline-flex;\n  align-items: center;\n  justify-content: center;\n  margin-inline-start: 4px;\n}";
var styles$u = {"menuItem":"DropdownItemComponent-module_menuItem__ncRPh","menuItemDisabled":"DropdownItemComponent-module_menuItemDisabled__QKuoV","menuItemChecked":"DropdownItemComponent-module_menuItemChecked__wenjV","itemBeforeText":"DropdownItemComponent-module_itemBeforeText__pVh2B","content":"DropdownItemComponent-module_content__uUm7A","icon":"DropdownItemComponent-module_icon__h-RdM"};
styleInject(css_248z$u);

const DropdownItemComponent = (props) => {
    const { children, before, after, active, className, disabled } = props;
    return (jsxs("div", Object.assign({ "data-testid": "dropdownItem", className: cn({
            [styles$u.menuItem]: true,
            [styles$u.menuItemChecked]: active,
            [styles$u.menuItemDisabled]: disabled,
        }, className) }, { children: [before && (jsx("span", Object.assign({ className: styles$u.itemBeforeText }, { children: before }))), jsx("span", Object.assign({ className: styles$u.content }, { children: children })), after && (jsx("span", Object.assign({ className: styles$u.icon }, { children: after })))] })));
};

const PortalHost = ({ name, className }) => {
    const ref = useRef(null);
    const { mount } = usePortalContext();
    useEffect(() => {
        const { current } = ref;
        if (!current) {
            return;
        }
        const { unmount } = mount(name, current);
        return unmount;
    }, [mount, name]);
    return jsx("div", { ref: ref, className: className, "data-portal": name });
};

const usePortalEmptyStatus = (name) => {
    const { emptyStatus } = usePortalContext();
    return emptyStatus.get(name) || false;
};

var css_248z$t = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.HeaderComponent-module_header__QUwIU {\n  --header-height: 56px;\n  --sidebar-width: 60px;\n  display: flex;\n  flex-grow: 1;\n  align-items: stretch;\n}\n@media (min-width: 10000px) {\n  .HeaderComponent-module_header__QUwIU {\n    --header-height: 60px;\n    --sidebar-width: 60px;\n  }\n}\n\n.HeaderComponent-module_headerHamburger__Dpe-6 {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  flex-shrink: 0;\n  width: var(--sidebar-width);\n  height: var(--header-height);\n  border-block-end: 1px solid var(--uikit-line-new-10, #efefef);\n  padding-inline-start: 12px;\n  background: unset;\n}\n@media (min-width: 1280px) {\n  .HeaderComponent-module_headerHamburger__Dpe-6 {\n    padding-inline-start: 0;\n    border-block-end: 0;\n    border-inline-end: 1px solid var(--uikit-line-new-10, #efefef);\n    background: var(--ui-left-bar-background-color);\n  }\n}\n\n.HeaderComponent-module_headerHost__-rg5m {\n  border-block-end: 1px solid var(--uikit-line-new-10, #efefef);\n  flex-grow: 1;\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  padding-inline-start: 12px;\n  padding-inline-end: 12px;\n}\n@media (min-width: 713px) {\n  .HeaderComponent-module_headerHost__-rg5m {\n    padding-inline-start: 20px;\n    padding-inline-end: 20px;\n  }\n}\n\n.HeaderComponent-module_headerMiddleRight__JYjgB {\n  display: none;\n  display: block;\n}\n\n.HeaderComponent-module_leftWrapper__GBULx,\n.HeaderComponent-module_rightWrapper__ldPra {\n  display: flex;\n  align-items: center;\n  gap: var(--size-m);\n}\n\n.HeaderComponent-module_userMenuWrapper__ZjedZ {\n  display: flex;\n  gap: var(--size-xs);\n}";
var styles$t = {"header":"HeaderComponent-module_header__QUwIU","headerHamburger":"HeaderComponent-module_headerHamburger__Dpe-6","headerHost":"HeaderComponent-module_headerHost__-rg5m","headerMiddleRight":"HeaderComponent-module_headerMiddleRight__JYjgB","leftWrapper":"HeaderComponent-module_leftWrapper__GBULx","rightWrapper":"HeaderComponent-module_rightWrapper__ldPra","userMenuWrapper":"HeaderComponent-module_userMenuWrapper__ZjedZ"};
styleInject(css_248z$t);

const HeaderComponent = () => {
    const isHamburgerHasContent = usePortalEmptyStatus(PORTAL_HAMBURGER_MENU);
    const isLogoHasContent = usePortalEmptyStatus(PORTAL_LOGO);
    const isMenuHasContent = usePortalEmptyStatus(PORTAL_HEADER_MENU);
    const isActionsHasContent = usePortalEmptyStatus(PORTAL_HEADER_ACTIONS);
    const isImpersonationHasContent = usePortalEmptyStatus(PORTAL_IMPERSONATION);
    const isMenuRightSlotHasContent = usePortalEmptyStatus(PORTAL_HEADER_MENU_RIGHT_SLOT);
    const isChatHasContent = usePortalEmptyStatus(PORTAL_HEADER_CHAT);
    return (jsxs("div", Object.assign({ className: styles$t.header }, { children: [isHamburgerHasContent && jsx(PortalHost, { className: styles$t.headerHamburger, name: PORTAL_HAMBURGER_MENU }), jsxs("div", Object.assign({ className: styles$t.headerHost }, { children: [jsxs("div", Object.assign({ className: styles$t.leftWrapper }, { children: [isLogoHasContent && jsx(PortalHost, { className: styles$t.headerLeft, name: PORTAL_LOGO }), isMenuHasContent && jsx(PortalHost, { name: PORTAL_HEADER_MENU }), isMenuRightSlotHasContent && jsx(PortalHost, { name: PORTAL_HEADER_MENU_RIGHT_SLOT })] })), jsxs("div", Object.assign({ className: styles$t.rightWrapper }, { children: [isActionsHasContent && jsx(PortalHost, { className: styles$t.headerMiddleRight, name: PORTAL_HEADER_ACTIONS }), isImpersonationHasContent && jsx(PortalHost, { name: PORTAL_IMPERSONATION }), jsxs("div", Object.assign({ className: styles$t.userMenuWrapper }, { children: [isChatHasContent && jsx(PortalHost, { className: styles$t.headerMiddleRight, name: PORTAL_HEADER_CHAT }), jsx(PortalHost, { className: styles$t.headerRight, name: PORTAL_USER_MENU })] }))] }))] }))] })));
};

const HeaderPlugin = () => {
    const TopBarPortal = usePortal(PORTAL_TOP_BAR);
    return (jsx(TopBarPortal, { children: jsx(HeaderComponent, {}) }));
};

var css_248z$s = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\nbody {\n  --ui-sidebar-bg-color: var(--ts-ui-sidebar-bg-color, var(--uikit-bg-c-default));\n  --ui-sidebar-scrollbar-thumb-color: var(\n    --ts-ui-sidebar-scrollbar-thumb-color,\n    var(--uikit-dark-fixed-text-new-graphics-only-inverse)\n  );\n  --ui-sidebar-button-text-color: var(--ts-ui-sidebar-button-text-color, var(--uikit-text-new-primary));\n  --ui-sidebar-button-hover-bg-color: var(--ts-ui-sidebar-button-hover-bg-color, var(--uikit-bg-c-default-soft));\n  --ui-sidebar-button-hover-text-color: var(--ts-ui-sidebar-button-hover-text-color, var(--uikit-text-new-primary));\n  --ui-sidebar-button-active-bg-color: var(--ts-ui-sidebar-button-active-bg-color, var(--uikit-bg-c-default-soft));\n  --ui-sidebar-button-active-text-color: var(--ts-ui-sidebar-button-active-text-color, var(--uikit-text-new-primary));\n  --ui-sidebar-button-focus-bg-color: var(--ts-ui-sidebar-button-focus-bg-color, var(--uikit-bg-c-default-pressed));\n  --ui-sidebar-button-focus-text-color: var(--ts-ui-sidebar-button-focus-text-color, var(--uikit-text-new-primary));\n  --ui-left-bar-background-color: var(--ui-sidebar-bg-color);\n  --ui-hamburger-section-name-color: var(--ts-ui-hamburger-section-name-color, #757575);\n  --ui-hamburger-section-chevron-color: var(--ts-ui-hamburger-section-chevron-color, #757575);\n  --ui-hamburger-close-button-bg-color: var(--ts-ui-hamburger-close-button-bg-color, var(--uikit-bg-c-default-soft));\n  --ui-hamburger-close-button-text-color: var(--ts-ui-hamburger-close-button-text-color, var(--uikit-text-new-primary));\n  --ui-hamburger-close-button-hover-bg-color: var(\n    --ts-ui-hamburger-close-button-hover-bg-color,\n    var(--uikit-bg-c-default-pressed)\n  );\n  --ui-hamburger-close-button-hover-text-color: var(\n    --ts-ui-hamburger-close-button-hover-text-color,\n    var(--uikit-text-on-c-default)\n  );\n  --ui-hamburger-close-button-focus-bg-color: var(--ts-ui-hamburger-close-button-focus-bg-color);\n  --ui-hamburger-close-button-focus-text-color: var(--ts-ui-hamburger-close-button-focus-text-color);\n  --ui-hamburger-close-button-active-bg-color: var(\n    --ts-ui-hamburger-close-button-active-bg-color,\n    var(--uikit-bg-c-default-soft)\n  );\n  --ui-hamburger-close-button-active-text-color: var(\n    --ts-ui-hamburger-close-button-active-text-color,\n    var(--uikit-text-new-primary)\n  );\n  --ui-hamburger-open-button-in-sidebar-bg-color: var(--ts-ui-hamburger-open-button-in-sidebar-bg-color, transparent);\n  --ui-hamburger-open-button-in-sidebar-text-color: var(\n    --ts-ui-hamburger-open-button-in-sidebar-text-color,\n    var(--uikit-text-new-primary)\n  );\n  --ui-hamburger-open-button-in-sidebar-hover-bg-color: var(\n    --ts-ui-hamburger-open-button-in-sidebar-hover-bg-color,\n    var(--uikit-bg-c-default-soft)\n  );\n  --ui-hamburger-open-button-in-sidebar-hover-text-color: var(\n    --ts-ui-hamburger-open-button-in-sidebar-hover-text-color,\n    var(--uikit-text-new-primary)\n  );\n  --ui-hamburger-open-button-in-sidebar-focus-bg-color: var(--ts-ui-hamburger-open-button-in-sidebar-focus-bg-color);\n  --ui-hamburger-open-button-in-sidebar-focus-text-color: var(\n    --ts-ui-hamburger-open-button-in-sidebar-focus-text-color\n  );\n  --ui-hamburger-open-button-in-sidebar-active-bg-color: var(\n    --ts-ui-hamburger-open-button-in-sidebar-active-bg-color,\n    var(--uikit-bg-c-default-pressed)\n  );\n  --ui-hamburger-open-button-in-sidebar-active-text-color: var(\n    --ts-ui-hamburger-open-button-in-sidebar-active-text-color,\n    var(--uikit-text-new-primary)\n  );\n  --ui-hamburger-header-border-bottom-color: var(\n    --ts-ui-hamburger-header-border-bottom-color,\n    var(--uikit-bg-c-default-soft)\n  );\n}\nbody .sidebar-panel,\nbody .hamburger-item-component,\nbody .stencil-left-sidebar {\n  --ui-button-state-hover: var(--ui-sidebar-button-hover-bg-color);\n  --ui-button-state-focus: var(--ui-sidebar-button-focus-bg-color);\n  --ui-button-state-active: var(--ui-sidebar-button-active-bg-color);\n  --ui-button-state-hover-text-color: var(--ui-sidebar-button-hover-text-color);\n  --ui-button-state-focus-text-color: var(--ui-sidebar-button-focus-text-color);\n  --ui-button-state-active-text-color: var(--ui-sidebar-button-active-text-color);\n}\n\nbody.acronis-theme {\n  background: var(--uikit-surface-new-10);\n  --ui-breadcrumb-link-color: var(--uikit-bg-c-brand-primary);\n  --ui-breadcrumb-seperator-color: var(--uikit-bg-c-brand-primary);\n  --ui-breadcrumb-seperator-opacity: 0.3;\n  --ui-breadcrumb-link-decoration: none;\n}\nbody.acronis-theme .header-nav {\n  --ui-breadcrumb-link-active-color: var(--uikit-decoration-new-red-70);\n}\nbody.acronis-theme .bottom-sheet-content {\n  --ui-button-state-hover: var(--uikit-bg-c-default-soft-hovered);\n  --ui-button-state-focus: var(--uikit-bg-c-default-soft-pressed);\n  --ui-button-state-active: var(--uikit-text-new-graphics-only);\n}\nbody.acronis-theme #hamburger-header {\n  --ui-hamburger-header-flex-direction: row-reverse;\n}\n\n.StencilComponent-module_root__ty-0P {\n  --layout-header-height: 56px;\n  --layout-footer-height: 64px;\n  --layout-sidebar-width: 60px;\n  --layout-content-header-height: 52px;\n  --layout-end-height: 25vh;\n  --layout-inline-gap: var(--size-s);\n  --layout-border-color: var(--uikit-line-new-10, #efefef);\n  --layout-header-color: var(--uikit-surface-new-00, #ffffff);\n  position: relative;\n  min-height: 100vh;\n  display: flex;\n  flex-direction: column;\n  flex-grow: 1;\n}\n@media (min-width: 10000px) {\n  .StencilComponent-module_root__ty-0P {\n    --layout-header-height: 60px;\n    --layout-footer-height: 64px;\n    --layout-sidebar-width: 60px;\n    --layout-content-header-height: 48px;\n  }\n}\n.StencilComponent-module_root__ty-0P.StencilComponent-module_debug__Y-EXN {\n  box-shadow: inset 0 0 20px blue;\n}\n@supports (-webkit-touch-callout: none) {\n  .StencilComponent-module_root__ty-0P {\n    min-height: -webkit-fill-available;\n  }\n}\n\n.StencilComponent-module_barsTransition__xOwEF {\n  transition: transform 0.2s ease-in-out;\n}\n\n.StencilComponent-module_topBar__3hVby {\n  position: sticky;\n  top: 0;\n  height: var(--layout-header-height);\n  display: flex;\n  align-items: stretch;\n  justify-content: space-between;\n  z-index: 30;\n  background-color: var(--uikit-surface-new-00, #ffffff);\n}\n.StencilComponent-module_debug__Y-EXN .StencilComponent-module_topBar__3hVby {\n  background: rgba(138, 43, 226, 0.2);\n}\n.StencilComponent-module_debug__Y-EXN .StencilComponent-module_topBar__3hVby::before {\n  content: \"topBar\";\n  position: absolute;\n  top: 50%;\n  left: 50%;\n  transform: translate(-50%, -50%);\n  font: 40px/1 sans-serif;\n  color: blueviolet;\n  opacity: 0.2;\n}\n.StencilComponent-module_noStickyBars__STUED .StencilComponent-module_topBar__3hVby {\n  transform: translateY(-100%);\n}\n\n.StencilComponent-module_bottomBar__L6O-y {\n  position: sticky;\n  bottom: 0;\n  height: var(--layout-footer-height);\n}\n.StencilComponent-module_debug__Y-EXN .StencilComponent-module_bottomBar__L6O-y {\n  background: rgba(165, 42, 42, 0.2);\n}\n.StencilComponent-module_debug__Y-EXN .StencilComponent-module_bottomBar__L6O-y::before {\n  content: \"bottomBar\";\n  position: absolute;\n  top: 50%;\n  left: 50%;\n  transform: translate(-50%, -50%);\n  font: 40px/1 sans-serif;\n  color: brown;\n  opacity: 0.2;\n}\n.StencilComponent-module_noStickyBars__STUED .StencilComponent-module_bottomBar__L6O-y {\n  transform: translateY(100%);\n}\n\n.StencilComponent-module_contentHost__x1Zsb {\n  display: flex;\n  align-items: stretch;\n  flex-grow: 1;\n}\n\n.StencilComponent-module_content__jSRAr {\n  min-height: 100%;\n  flex-grow: 1;\n  display: flex;\n  flex-direction: column;\n}\n.StencilComponent-module_debug__Y-EXN .StencilComponent-module_content__jSRAr {\n  background: rgba(255, 255, 0, 0.2);\n}\n.StencilComponent-module_debug__Y-EXN .StencilComponent-module_content__jSRAr::before {\n  content: \"content\";\n  position: absolute;\n  top: 50%;\n  left: 50%;\n  transform: translate(-50%, -50%);\n  font: 40px/1 sans-serif;\n  color: yellow;\n  opacity: 0.2;\n}\n\n.StencilComponent-module_contentHeader__ZWyXl {\n  position: sticky;\n  top: var(--layout-header-height);\n  height: var(--layout-content-header-height);\n  z-index: 1;\n  transition: transform 0.2s ease-in-out;\n}\n.StencilComponent-module_debug__Y-EXN .StencilComponent-module_contentHeader__ZWyXl {\n  background: rgba(255, 0, 0, 0.2);\n}\n.StencilComponent-module_debug__Y-EXN .StencilComponent-module_contentHeader__ZWyXl::before {\n  content: \"contentHeader\";\n  position: absolute;\n  top: 50%;\n  left: 50%;\n  transform: translate(-50%, -50%);\n  font: 40px/1 sans-serif;\n  color: red;\n  opacity: 0.2;\n}\n@media (min-width: 1280px) {\n  .StencilComponent-module_contentHeader__ZWyXl {\n    transition: none;\n  }\n}\n.StencilComponent-module_noStickyBars__STUED .StencilComponent-module_contentHeader__ZWyXl {\n  transform: translateY(calc(var(--layout-header-height) * -1));\n}\n\n.StencilComponent-module_contentWrapper__hxgCD {\n  flex-grow: 1;\n}\n\n.StencilComponent-module_leftSideBarHost__UnZeC {\n  flex-shrink: 0;\n  width: var(--layout-sidebar-width);\n  display: none;\n  background: var(--ui-left-bar-background-color, var(--uikit-surface-new-00, #ffffff));\n  position: relative;\n  z-index: 21;\n}\n@media (min-width: 713px) {\n  .StencilComponent-module_leftSideBarHost__UnZeC {\n    border-inline-end: 1px solid var(--layout-border-color);\n    display: block;\n  }\n}\n\n.StencilComponent-module_leftSideBar__BpWY3 {\n  position: sticky;\n  top: var(--layout-header-height);\n  width: var(--layout-sidebar-width);\n}\n.StencilComponent-module_debug__Y-EXN .StencilComponent-module_leftSideBar__BpWY3 {\n  background: rgba(0, 128, 0, 0.2);\n}\n.StencilComponent-module_debug__Y-EXN .StencilComponent-module_leftSideBar__BpWY3::before {\n  content: \"sideBar\";\n  position: absolute;\n  top: 50%;\n  left: 50%;\n  transform: translate(-50%, -50%);\n  font: 40px/1 sans-serif;\n  color: green;\n  opacity: 0.2;\n}\n.StencilComponent-module_debug__Y-EXN .StencilComponent-module_leftSideBar__BpWY3::before {\n  writing-mode: vertical-rl;\n  text-orientation: upright;\n}\n\n.StencilComponent-module_topBarHeader__cACH- {\n  flex: 1;\n}\n@media (min-width: 1280px) {\n  .StencilComponent-module_topBarHeader__cACH- {\n    border-block-end: 1px solid var(--layout-border-color);\n  }\n}\n\n.StencilComponent-module_rootEnd__4iXPn {\n  position: absolute;\n  inset-inline: 0;\n  bottom: 0;\n  height: var(--layout-end-height);\n  pointer-events: none;\n}\n\n.StencilComponent-module_bottomSheet__kwIfw {\n  position: absolute;\n  top: 100%;\n  height: 0;\n  overflow: hidden;\n}\n\n.StencilComponent-module_toc__KKze1 {\n  width: 0;\n  position: relative;\n}\n\n.StencilComponent-module_tocWrapper__czceC {\n  position: sticky;\n  top: var(--layout-header-height);\n  height: calc(100vh - var(--layout-header-height));\n  z-index: 1;\n}";
var styles$s = {"root":"StencilComponent-module_root__ty-0P","topBar":"StencilComponent-module_topBar__3hVby StencilComponent-module_barsTransition__xOwEF","noStickyBars":"StencilComponent-module_noStickyBars__STUED","contentHost":"StencilComponent-module_contentHost__x1Zsb","content":"StencilComponent-module_content__jSRAr","contentHeader":"StencilComponent-module_contentHeader__ZWyXl","contentWrapper":"StencilComponent-module_contentWrapper__hxgCD","leftSideBarHost":"StencilComponent-module_leftSideBarHost__UnZeC","leftSideBar":"StencilComponent-module_leftSideBar__BpWY3","bottomSheet":"StencilComponent-module_bottomSheet__kwIfw","toc":"StencilComponent-module_toc__KKze1","tocWrapper":"StencilComponent-module_tocWrapper__czceC"};
styleInject(css_248z$s);

const ImpersonationListener = () => {
    const [showRefreshModal, setShowRefreshModal] = useState(false);
    const handleImpersonationTokenRefresh = () => {
        setShowRefreshModal(true);
    };
    const handlePageReload = () => {
        window.location.reload();
    };
    useListenImpersonationChannel(handleImpersonationTokenRefresh);
    // TODO: https://gitlab.constr.dev/platform/virtual-lab/ui-kit/-/merge_requests/352
    // TODO: use closeable={false} when it will be implemented
    return (jsx(Modal, Object.assign({ rootElementId: "root", isOpen: showRefreshModal, onOk: handlePageReload, title: "Refresh the page", type: "warning", closeable: false }, { children: "Please refresh the page, as you're viewing the system as another user" })));
};

const DEBUG_LAYOUT = false;
const StencilComponent = (props) => {
    const { children } = props;
    const isScrolledDown = useHideHeaderOnScroll();
    const { fromDesktop } = useBreakpoints();
    const hideBars = !fromDesktop && isScrolledDown;
    const isTOCHasContent = usePortalEmptyStatus(PORTAL_TOC);
    const isContentHeaderHasContent = usePortalEmptyStatus(PORTAL_CONTENT_HEADER);
    const isSidebarHasContent = usePortalEmptyStatus(PORTAL_SIDE_BAR);
    // const [debugColorScheme, setDebugColorScheme] = useState<ComponentProps<typeof Theme>['colorScheme']>()
    return (jsxs("div", Object.assign({ className: cn(styles$s.root, hideBars && styles$s.noStickyBars, DEBUG_LAYOUT), "data-testid": "stencil" }, { children: [jsx(ImpersonationListener, {}), jsx(PortalHost, { className: styles$s.topBar, name: PORTAL_TOP_BAR }), jsxs("div", Object.assign({ className: styles$s.contentHost, "data-testid": "contentHost" }, { children: [fromDesktop && isSidebarHasContent && (jsx("div", Object.assign({ className: cn(styles$s.leftSideBarHost, 'stencil-left-sidebar'), "data-testid": "leftSideBarHost" }, { children: jsx(PortalHost, { className: styles$s.leftSideBar, name: PORTAL_SIDE_BAR }) }))), isTOCHasContent && fromDesktop && (jsx("div", Object.assign({ className: styles$s.toc }, { children: jsx(PortalHost, { className: styles$s.tocWrapper, name: PORTAL_TOC }) }))), jsxs("main", Object.assign({ className: styles$s.content, "data-testid": "content", id: "main-content" }, { children: [isContentHeaderHasContent && jsx(PortalHost, { className: styles$s.contentHeader, name: PORTAL_CONTENT_HEADER }), jsx("div", Object.assign({ className: styles$s.contentWrapper }, { children: children }))] }))] })), jsx(PortalHost, { className: styles$s.bottomSheet, name: PORTAL_BOTTOM_SHEET_USER_MENU }), jsx(PortalHost, { className: styles$s.bottomSheet, name: PORTAL_BOTTOM_SHEET_TOC }), jsx(PortalHost, { className: styles$s.bottomSheet, name: PORTAL_BOTTOM_SHEET_BREADCRUMBS })] })));
};

const Stencil = ({ children }) => {
    return (jsx(ReduxContextProvider, { children: jsx(ProvideBreakpoints, { children: jsx(StencilInner, { children: children }) }) }));
};
const StencilInner = ({ children }) => {
    return (jsx(PortalContextProvider, { children: jsx(StencilComponent, { children: children }) }));
};

var css_248z$r = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.LeftPanelComponent-module_leftPanel__U4CWH {\n  --ui-scrollbar-thumb-color: var(--ui-sidebar-scrollbar-thumb-color);\n  --left-panel--width: 100%;\n  overflow: hidden;\n  overflow-y: auto;\n  position: fixed;\n  z-index: 40;\n  top: 0;\n  bottom: 0;\n  left: 0;\n  width: var(--left-panel--width);\n  background: var(--ui-left-bar-background-color, var(--uikit-surface-new-00, #ffffff));\n  opacity: 0;\n  overscroll-behavior: contain;\n  -webkit-overflow-scrolling: touch;\n  visibility: hidden;\n  transform: translateX(calc(var(--left-panel--width) * -1));\n  transition: transform 0.05s ease-out, opacity 0.05s ease-out;\n}\n.LeftPanelComponent-module_leftPanel__U4CWH::-webkit-scrollbar {\n  width: 6px;\n  height: unset;\n  background-color: transparent;\n}\n.LeftPanelComponent-module_leftPanel__U4CWH::-webkit-scrollbar-thumb {\n  background: var(--ui-scrollbar-thumb-color, var(--uikit-dark-fixed-text-new-graphics-only-inverse, #8f8f8f));\n  border-radius: 4px;\n}\n@media screen and (min-width: 712px) {\n  .LeftPanelComponent-module_leftPanel__U4CWH {\n    --left-panel--width: 316px;\n    border-inline-end: 1px solid var(--uikit-line-new-10, #efefef);\n  }\n}\nhtml[dir=rtl] .LeftPanelComponent-module_leftPanel__U4CWH {\n  left: auto;\n  right: 0;\n  transform: translateX(var(--left-panel--width));\n}\n.LeftPanelComponent-module_leftPanel__U4CWH ul, .LeftPanelComponent-module_leftPanel__U4CWH li {\n  list-style-type: none;\n}\n\n.LeftPanelComponent-module_open__voHQR {\n  opacity: 1;\n  visibility: visible;\n}\n.LeftPanelComponent-module_open__voHQR, html[dir=rtl] .LeftPanelComponent-module_open__voHQR {\n  transform: translateX(0);\n}";
var styles$r = {"leftPanel":"LeftPanelComponent-module_leftPanel__U4CWH","open":"LeftPanelComponent-module_open__voHQR"};
styleInject(css_248z$r);

const LeftPanelComponent = ({ children, isOpen, onClose, className }) => {
    const containerRef = useRef(null);
    useClickAway(containerRef, () => {
        if (!isOpen || !onClose) {
            return;
        }
        onClose();
    });
    return (jsx("div", Object.assign({ ref: containerRef, className: cn(styles$r.leftPanel, isOpen && styles$r.open, className) }, { children: children })));
};

var css_248z$q = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.HamburgerPlugin-module_header__fjPum {\n  --header-height: 56px;\n  height: var(--header-height);\n  display: flex;\n  flex-direction: var(--ui-hamburger-header-flex-direction, row);\n  justify-content: space-between;\n  align-items: center;\n  padding: var(--size-xs);\n  border-block-end: 1px solid var(--ui-hamburger-header-border-bottom-color, var(--uikit-line-new-10, #efefef));\n  background: var(--ui-left-bar-background-color);\n}\n@media (min-width: 10000px) {\n  .HamburgerPlugin-module_header__fjPum {\n    --header-height: 60px;\n  }\n}\n\n.HamburgerPlugin-module_body__5eWuy {\n  display: flex;\n  flex-direction: column;\n  padding: var(--size-m) var(--size-xs);\n}\n@media (min-width: 10000px) {\n  .HamburgerPlugin-module_body__5eWuy {\n    padding: 6px var(--size-xs);\n  }\n}";
var styles$q = {"header":"HamburgerPlugin-module_header__fjPum","body":"HamburgerPlugin-module_body__5eWuy"};
styleInject(css_248z$q);

var css_248z$p = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.HamburgerCloseButton-module_closeButton__S3fzL {\n  display: flex;\n  align-items: center;\n  margin: 0;\n  padding: 0;\n  border: none;\n  background: none;\n  color: inherit;\n  font-family: inherit;\n  font-size: inherit;\n  font-weight: inherit;\n  cursor: pointer;\n  background-color: var(--ui-hamburger-close-button-bg-color);\n  color: var(--ui-hamburger-close-button-text-color);\n  border-radius: 50%;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  height: 32px;\n  width: 32px;\n}\n.HamburgerCloseButton-module_closeButton__S3fzL:hover {\n  background-color: var(--ui-hamburger-close-button-hover-bg-color);\n  color: var(--ui-hamburger-close-button-hover-text-color);\n}\n.HamburgerCloseButton-module_closeButton__S3fzL:focus {\n  background-color: var(--ui-hamburger-close-button-focus-bg-color);\n  color: var(--ui-hamburger-close-button-focus-text-color);\n}\n.HamburgerCloseButton-module_closeButton__S3fzL:active {\n  background-color: var(--ui-hamburger-close-button-active-bg-color);\n  color: var(--ui-hamburger-close-button-active-text-color);\n}";
var styles$p = {"closeButton":"HamburgerCloseButton-module_closeButton__S3fzL"};
styleInject(css_248z$p);

const HamburgerCloseButton = ({ onClick }) => {
    return (jsx("button", Object.assign({ onClick: onClick, className: styles$p.closeButton, "aria-label": "Close menu" }, { children: jsx(SvgComponent, { name: "CloseBold", size: "sm" }) })));
};

var css_248z$o = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.HamburgerOpenButton-module_openButton__BZ2xB {\n  display: flex;\n  align-items: center;\n  margin: 0;\n  padding: 0;\n  border: none;\n  background: none;\n  color: inherit;\n  font-family: inherit;\n  font-size: inherit;\n  font-weight: inherit;\n  cursor: pointer;\n  background-color: var(--ui-hamburger-open-button-in-sidebar-bg-color);\n  color: var(--ui-hamburger-open-button-in-sidebar-text-color);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  width: var(--size-xxxl);\n  height: var(--size-xxxl);\n  border-radius: var(--size-xxxs);\n}\n.HamburgerOpenButton-module_openButton__BZ2xB:hover {\n  background-color: var(--ui-hamburger-open-button-in-sidebar-hover-bg-color);\n  color: var(--ui-hamburger-open-button-in-sidebar-hover-text-color);\n}\n.HamburgerOpenButton-module_openButton__BZ2xB:focus {\n  background-color: var(--ui-hamburger-open-button-in-sidebar-focus-bg-color);\n  color: var(--ui-hamburger-open-button-in-sidebar-focus-text-color);\n}\n.HamburgerOpenButton-module_openButton__BZ2xB:active {\n  background-color: var(--ui-hamburger-open-button-in-sidebar-active-bg-color);\n  color: var(--ui-hamburger-open-button-in-sidebar-active-text-color);\n}\n\n.HamburgerOpenButton-module_openIconButtonWrapper__4Ljwy {\n  margin-right: var(--size-xxs);\n}";
var styles$o = {"openButton":"HamburgerOpenButton-module_openButton__BZ2xB","openIconButtonWrapper":"HamburgerOpenButton-module_openIconButtonWrapper__4Ljwy"};
styleInject(css_248z$o);

const HamburgerOpenButton = ({ showRoundedIcon, onClick }) => {
    if (showRoundedIcon) {
        return (jsx("div", Object.assign({ className: styles$o.openIconButtonWrapper }, { children: jsx(IconButton, { iconName: "MenuBoldStroke", color: "tertiary", shape: "rounded", size: "small", "aria-label": "Open menu", onClick: onClick }) })));
    }
    return (jsx("button", Object.assign({ onClick: onClick, className: styles$o.openButton, "aria-label": "Open menu" }, { children: jsx(SvgComponent, { name: "MenuBoldStroke", size: showRoundedIcon ? 'sm' : 'md' }) })));
};

var css_248z$n = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.LogoComponent-module_logoWrapper__9n6jx {\n  display: flex;\n  align-items: center;\n}";
var styles$n = {"logoWrapper":"LogoComponent-module_logoWrapper__9n6jx"};
styleInject(css_248z$n);

const LogoComponent = ({ children }) => {
    const LogoPortal = usePortal(PORTAL_LOGO);
    return (jsx(LogoPortal, { children: jsx("div", Object.assign({ className: styles$n.logoWrapper }, { children: children })) }));
};

const LogoSlotPlugin = ({ children }) => {
    const { fromDesktop } = useBreakpoints();
    if (!children) {
        return null;
    }
    if (fromDesktop) {
        return jsx(LogoComponent, { children: children });
    }
    return null;
};

const PublicTenantLogo = ({ isHamburger }) => {
    const [requestPublicTenantSettings, { isLoading }] = useLazyGetPublicTenantSettingsQuery();
    useEffect(() => {
        requestPublicTenantSettings();
    }, [requestPublicTenantSettings]);
    const regularLogoId = useBrandingSettings('logo', 'public');
    const logoOnHamburgerId = useBrandingSettings('logoOnHamburger', 'public');
    if (isLoading) {
        return null;
    }
    const logoId = isHamburger ? logoOnHamburgerId || regularLogoId : regularLogoId;
    return jsx(RenderLogo, { logoId: logoId });
};

const PortalLogoPlugin = () => {
    return (jsx(LogoSlotPlugin, { children: jsx(LinkedTenantLogo, {}) }));
};

const HamburgerPlugin = (props) => {
    const { children = null, logo } = props;
    const { fromDesktop } = useBreakpoints();
    const [isOpen, setOpen] = useState(false);
    const handleOpen = useCallback(() => {
        setOpen(true);
    }, []);
    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);
    const HamburgerPortal = usePortal(PORTAL_HAMBURGER_MENU);
    return (jsxs(Fragment, { children: [jsx(HamburgerPortal, { children: jsx(HamburgerOpenButton, { showRoundedIcon: !fromDesktop, onClick: handleOpen }) }), jsxs(LeftPanelComponent, Object.assign({ isOpen: isOpen, onClose: handleClose }, { children: [jsxs("div", Object.assign({ className: styles$q.header, id: "hamburger-header" }, { children: [jsx("div", Object.assign({ className: styles$q.close }, { children: jsx(HamburgerCloseButton, { onClick: handleClose }) })), jsx("div", Object.assign({ id: "logo-container" }, { children: !!logo ? logo : jsx(TenantLogo, { isHamburger: true }) }))] })), jsx("ul", Object.assign({ className: styles$q.body }, { children: children }))] }))] }));
};

const PORTAL_CONTENT_HEADER_RIGHT = 'content-header-right-portal';
const PORTAL_CONTENT_HEADER_CENTER = 'content-header-center-portal';
const PORTAL_CONTENT_HEADER_LEFT = 'content-header-left-portal';

var css_248z$m = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.TOCWideComponent-module_tocOpener__ypp-f {\n  position: absolute;\n  height: 48px;\n  width: 48px;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n}\nhtml[dir=rtl] .TOCWideComponent-module_tocOpener__ypp-f {\n  transform: scaleX(-1);\n}\n\nhtml[dir=rtl] .TOCWideComponent-module_tocCloser__8AkTc {\n  transform: scaleX(-1);\n}\n\n.TOCWideComponent-module_toc__QRtRF {\n  --left-panel--width: 100%;\n  overflow: hidden;\n  overflow-y: auto;\n  z-index: 20;\n  position: sticky;\n  height: 100%;\n  left: 0;\n  width: var(--left-panel--width);\n  background: var(--ts-ui-learner-toc-menu-background, var(--uikit-surface-new-00, #ffffff));\n  color: var(--ts-ui-learner-toc-menu-text-color);\n  opacity: 0;\n  overscroll-behavior: contain;\n  -webkit-overflow-scrolling: touch;\n  transform: translateX(calc(var(--left-panel--width) * -1));\n  transition: transform 0.05s ease-out, opacity 0.025s ease-out 0.05s;\n}\n@media (min-width: 713px) {\n  .TOCWideComponent-module_toc__QRtRF {\n    --left-panel--width: 360px;\n    border-inline-end: 1px solid var(--ts-ui-learner-toc-menu-border-inline-end-color, var(--uikit-line-new-10, #efefef));\n  }\n}\nhtml[dir=rtl] .TOCWideComponent-module_toc__QRtRF {\n  left: auto;\n  right: 0;\n  transform: translateX(var(--left-panel--width));\n}\n\n.TOCWideComponent-module_open__QE04f {\n  opacity: 1;\n}\n.TOCWideComponent-module_open__QE04f, html[dir=rtl] .TOCWideComponent-module_open__QE04f {\n  transform: translateX(0);\n}\n\n.TOCWideComponent-module_titleWrapper__WrBv5 {\n  --title-height: 60px;\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n  height: var(--title-height);\n  padding: var(--size-xs) var(--size-m);\n  border-bottom: 1px solid var(--ts-ui-learner-toc-menu-title-border-bottom-color, var(--uikit-line-new-10, #efefef));\n  background: var(--ts-ui-learner-toc-menu-title-background, var(--uikit-surface-new-00, #ffffff));\n  color: var(--ts-ui-learner-toc-menu-title-text-color);\n  position: sticky;\n  position: -webkit-sticky;\n  top: 0;\n  z-index: 1;\n  font-family: \"Hauss\", sans-serif;\n  font-weight: 500;\n  font-size: 1rem;\n  line-height: 1.5rem;\n  letter-spacing: normal;\n}";
var styles$m = {"tocOpener":"TOCWideComponent-module_tocOpener__ypp-f","tocCloser":"TOCWideComponent-module_tocCloser__8AkTc","toc":"TOCWideComponent-module_toc__QRtRF","open":"TOCWideComponent-module_open__QE04f","titleWrapper":"TOCWideComponent-module_titleWrapper__WrBv5"};
styleInject(css_248z$m);

const TOCWideComponent = (props) => {
    const { children, title, isOpen, onOpen, onClose } = props;
    const ariaLabelOpen = typeof title === 'string' ? `${title}, expand` : 'expand content';
    const ariaLabelClose = typeof title === 'string' ? `${title}, collapse` : 'collapse content';
    return (jsxs(Fragment, { children: [!isOpen && (jsx("div", Object.assign({ className: styles$m.tocOpener }, { children: jsx(InlineButton, { icon: "ChevronRightDoubleBoldStroke", onClick: onOpen, size: "small", "aria-label": ariaLabelOpen }) }))), jsxs("div", Object.assign({ className: cn(styles$m.toc, isOpen && styles$m.open) }, { children: [jsxs("div", Object.assign({ className: styles$m.titleWrapper }, { children: [title, jsx("div", Object.assign({ className: styles$m.tocCloser }, { children: jsx(InlineButton, { icon: "ChevronLeftDoubleBoldStroke", onClick: onClose, size: "small", "aria-label": ariaLabelClose }) }))] })), children] }))] }));
};

var css_248z$l = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.TOCPlugin-module_mobileTOCToggle__g2Dh- {\n  cursor: pointer;\n  background: none;\n  padding: 0;\n  margin: 0;\n  border: 1px solid var(--uikit-line-new-10, #efefef);\n  color: var(--uikit-bg-c-brand-primary, #141414);\n  height: var(--size-xl);\n  width: var(--size-xl);\n  border-radius: var(--size-xxxs);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n}";
var styles$l = {"mobileTOCToggle":"TOCPlugin-module_mobileTOCToggle__g2Dh-"};
styleInject(css_248z$l);

const TOCPlugin = (props) => {
    const { children, title, isOpen, onOpen, onClose } = props;
    const { fromDesktop } = useBreakpoints();
    const TOCPortal = usePortal(PORTAL_TOC);
    const TOCBottomSheetPortal = usePortal(PORTAL_BOTTOM_SHEET_TOC);
    const ContentHeaderLeftPortal = usePortal(PORTAL_CONTENT_HEADER_LEFT);
    const ariaLabelOpen = typeof title === 'string' ? `${title}, expand` : 'expand content';
    return (jsxs(Fragment, { children: [fromDesktop && (jsx(TOCPortal, { children: jsx(TOCWideComponent, Object.assign({ title: title, isOpen: isOpen, onOpen: onOpen, onClose: onClose }, { children: children })) })), !fromDesktop && (jsxs(Fragment, { children: [jsx(ContentHeaderLeftPortal, { children: jsx("button", Object.assign({ "aria-label": ariaLabelOpen, onClick: onOpen, className: styles$l.mobileTOCToggle }, { children: jsx(Wrapper, { icon: "ListBulletedBoldStroke", size: 16 }) })) }), jsx(TOCBottomSheetPortal, { children: jsx(BottomSheet, { isOpen: isOpen, items: [
                                {
                                    header: title,
                                    content: () => children,
                                },
                            ], onClose: onClose, enableScrollBarGutter: true }) })] }))] }));
};

var css_248z$k = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.ContentHeaderComponent-module_contentHeader__ZGeYL {\n  display: grid;\n  align-items: center;\n  grid-template-columns: repeat(3, 1fr);\n  border-block-end: 1px solid var(--uikit-line-new-10, #efefef);\n  height: 52px;\n  padding-inline: 10px;\n  background: #fff;\n}\n@media (min-width: 1280px) {\n  .ContentHeaderComponent-module_contentHeader__ZGeYL {\n    height: 48px;\n  }\n}\n\n.ContentHeaderComponent-module_middleSlot__PaxBa {\n  display: flex;\n  align-items: center;\n  justify-content: center;\n}";
var styles$k = {"contentHeader":"ContentHeaderComponent-module_contentHeader__ZGeYL","middleSlot":"ContentHeaderComponent-module_middleSlot__PaxBa"};
styleInject(css_248z$k);

const ContentHeaderComponent = () => {
    return (jsxs("div", Object.assign({ className: styles$k.contentHeader }, { children: [jsx(PortalHost, { className: styles$k.leftSlot, name: PORTAL_CONTENT_HEADER_LEFT }), jsx(PortalHost, { className: styles$k.middleSlot, name: PORTAL_CONTENT_HEADER_CENTER }), jsx(PortalHost, { className: styles$k.rightSlot, name: PORTAL_CONTENT_HEADER_RIGHT })] })));
};

const ContentHeaderPlugin = () => {
    const isLeftSlotHasContent = usePortalEmptyStatus(PORTAL_CONTENT_HEADER_LEFT);
    const isCenterSlotHasContent = usePortalEmptyStatus(PORTAL_CONTENT_HEADER_CENTER);
    const isRightSlotHasContent = usePortalEmptyStatus(PORTAL_CONTENT_HEADER_RIGHT);
    const ContentHeaderPortal = usePortal(PORTAL_CONTENT_HEADER);
    if (!isLeftSlotHasContent && !isCenterSlotHasContent && !isRightSlotHasContent) {
        return null;
    }
    return (jsx(ContentHeaderPortal, { children: jsx(ContentHeaderComponent, {}) }));
};

var css_248z$j = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.SideBarComponent-module_sidebar__nvXPm {\n  display: flex;\n  align-items: center;\n}\n.SideBarComponent-module_sidebar__nvXPm a {\n  text-decoration: none;\n}\n@media (max-width: 1279px) {\n  .SideBarComponent-module_sidebar__nvXPm :not(:last-of-type) button {\n    border-end-end-radius: 0;\n    border-start-end-radius: 0;\n  }\n  .SideBarComponent-module_sidebar__nvXPm :not(:first-of-type) button {\n    margin-inline-start: -1px;\n    border-inline-start: 1px solid var(--uikit-line-new-10, #efefef);\n    border-start-start-radius: 0;\n    border-end-start-radius: 0;\n  }\n}\n@media (min-width: 1280px) {\n  .SideBarComponent-module_sidebar__nvXPm {\n    flex-direction: column;\n  }\n}\n\nul.SideBarComponent-module_sidebar__nvXPm {\n  margin: 0;\n  padding: 0;\n  list-style: none;\n}";
var styles$j = {"sidebar":"SideBarComponent-module_sidebar__nvXPm"};
styleInject(css_248z$j);

const SideBarComponent = ({ children, as: Tag = 'div' }) => {
    return jsx(Tag, Object.assign({ className: styles$j.sidebar }, { children: children }));
};

const SidebarSlotPlugin = ({ children }) => {
    const SideBarPortal = usePortal(PORTAL_SIDE_BAR);
    return jsx(SideBarPortal, { children: children });
};

const SideBarPlugin = (props) => {
    const { children, asContainer } = props;
    const { fromDesktop } = useBreakpoints();
    const ContentHeaderCenterPortal = usePortal(PORTAL_CONTENT_HEADER_CENTER);
    if (Children.count(children) === 0) {
        return null;
    }
    const child = jsx(SideBarComponent, Object.assign({ as: asContainer }, { children: children }));
    if (fromDesktop) {
        return jsx(SidebarSlotPlugin, { children: child });
    }
    return jsx(ContentHeaderCenterPortal, { children: child });
};

var css_248z$i = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.BottomToolbarComponent-module_bottomToolbar__rlYRV {\n  display: flex;\n  justify-content: center;\n  margin: 0 auto;\n}\n\n.BottomToolbarComponent-module_slot__GwfDk:not(:first-of-type) {\n  margin-inline-start: 8px;\n  border-inline-start: 1px solid var(--uikit-line-new-10, #efefef);\n  padding-inline-start: 8px;\n}";
var styles$i = {"bottomToolbar":"BottomToolbarComponent-module_bottomToolbar__rlYRV","slot":"BottomToolbarComponent-module_slot__GwfDk"};
styleInject(css_248z$i);

const BottomToolbarComponent = ({ leftSlot, rightSlot }) => {
    return (jsxs("div", Object.assign({ className: styles$i.bottomToolbar }, { children: [leftSlot && (jsx("div", Object.assign({ className: styles$i.slot }, { children: leftSlot }))), rightSlot && (jsx("div", Object.assign({ className: styles$i.slot }, { children: rightSlot })))] })));
};

const BottomToolbarPlugin = () => {
    throw new Error('BottomToolbarPlugin not implemented');
};

const ActionsPlugin = () => {
    throw new Error('ActionsPlugin not implemented');
};

const LanguageSubMenuItem = () => {
    return null;
};

const LanguageSwitcherPlugin = ({ children }) => {
    const LanguagePortal = usePortal(PORTAL_LANGUAGE_MENU);
    return (jsx(LanguagePortal, { children: children }));
};

const UserMenuComponent = () => {
    return null;
};

const UserMenuPlugin = ({ children }) => {
    return jsx(Fragment, {});
};

var css_248z$h = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.UnauthUserMenuPlugin-module_dropdown__7w4tp {\n  display: none;\n  position: absolute;\n}\n.UnauthUserMenuPlugin-module_open__21Spe .UnauthUserMenuPlugin-module_dropdown__7w4tp {\n  display: block;\n}";
var styles$h = {"dropdown":"UnauthUserMenuPlugin-module_dropdown__7w4tp","open":"UnauthUserMenuPlugin-module_open__21Spe"};
styleInject(css_248z$h);

const UnauthUserMenuPlugin = (props) => {
    const { title } = props;
    const [isOpen, setOpen] = useState(false);
    const { fromTablet } = useBreakpoints();
    const UserMenuPortal = usePortal(PORTAL_USER_MENU);
    const UserMenuBottomSheetPortal = usePortal(PORTAL_BOTTOM_SHEET_USER_MENU);
    const mobileChild = (!fromTablet && (jsx(UserMenuBottomSheetPortal, { children: jsx(BottomSheet, { isOpen: isOpen, items: [{
                    header: title,
                    content: () => (jsx(PortalHost, { name: PORTAL_LANGUAGE_MENU }))
                }], onClose: () => setOpen(false) }) })));
    return (jsxs(Fragment, { children: [jsxs(UserMenuPortal, { children: [fromTablet && (jsxs("div", Object.assign({ className: cn(styles$h.menu, isOpen && styles$h.open) }, { children: [jsx(IconButton, { iconName: 'PlanetBoldStroke', "aria-label": '', onClick: () => setOpen(true) }), jsx(PortalHost, { className: styles$h.dropdown, name: PORTAL_LANGUAGE_MENU })] }))), !fromTablet && (jsx(IconButton, { iconName: 'PlanetBoldStroke', "aria-label": '', onClick: () => setOpen(true) }))] }), mobileChild] }));
};

const AvatarPlugin = ({ children }) => {
    const AvatarPortal = usePortal(PORTAL_USER_AVATAR);
    return jsx(AvatarPortal, { children: children });
};

const UserMenuSlotPlugin = ({ children }) => {
    const UserMenuPortal = usePortal(PORTAL_USER_MENU);
    return (jsx(UserMenuPortal, { children: children }));
};

const UserMenuBottomSheetSlotPlugin = ({ children }) => {
    const BottomSheetPortal = usePortal(PORTAL_BOTTOM_SHEET_USER_MENU);
    return (jsx(BottomSheetPortal, { children: children }));
};

const ContentHeaderRightSlotPlugin = ({ children }) => {
    const Portal = usePortal(PORTAL_CONTENT_HEADER_RIGHT);
    return jsx(Portal, { children: children });
};

const ActionsSlotPlugin = ({ children }) => {
    const HeaderActionsPortal = usePortal(PORTAL_HEADER_ACTIONS);
    return jsx(HeaderActionsPortal, { children: children });
};

const HeaderMenuRightSlotPlugin = ({ children }) => {
    const HeaderMenuRightSlotPortal = usePortal(PORTAL_HEADER_MENU_RIGHT_SLOT);
    return jsx(HeaderMenuRightSlotPortal, { children: children });
};

var css_248z$g = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.SplashLoading-module_splashLoading__JTgaD {\n  position: fixed;\n  top: 0;\n  left: 0;\n  z-index: 9999;\n  width: 100%;\n  height: 100%;\n  background-color: #fff;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n}";
var styles$g = {"splashLoading":"SplashLoading-module_splashLoading__JTgaD"};
styleInject(css_248z$g);

// this component is for showing loading indicator until get tenant settings
// otherwise, there will be color blink
const SplashLoading = () => {
    return jsx("div", Object.assign({ className: styles$g.splashLoading }, { children: jsx(Spinner, {}) }));
};

const licenseApi = navigationPlatformApi.injectEndpoints({
    endpoints: (builder) => ({
        getLicenseFeatures: builder.query({
            query: () => ({
                url: 'licensed-features',
            }),
            transformResponse: (response) => response.features,
        }),
    }),
});
const { useGetLicenseFeaturesQuery } = licenseApi;
const selectLicensedFeatures = createSelector(licenseApi.endpoints.getLicenseFeatures.select(), ({ data }) => {
    return data !== null && data !== void 0 ? data : [];
});

const DEFAULT_FEATURES_TO_CHECK = ['LMS.Full'];
const DEFAULT_FALLBACK_URL = '/learn';
const DEFAULT_CHECK_ACCESS_MODE = 'redirect';
const DEFAULT_FALLBACK_COMPONENT = jsx("div", { children: "402 - You need to contact with administrator." });
const LicenseGuard = (props) => {
    const { children, skipCheck, featuresToCheck = DEFAULT_FEATURES_TO_CHECK, fallbackUrl = DEFAULT_FALLBACK_URL, checkAccessMode = DEFAULT_CHECK_ACCESS_MODE, fallbackComponent = DEFAULT_FALLBACK_COMPONENT, } = props;
    const { data: featureList, isLoading, error, } = useGetLicenseFeaturesQuery(undefined, {
        skip: skipCheck,
    });
    if (skipCheck) {
        return jsx(Fragment, { children: children });
    }
    // If we get a 401 error, we'll treat it as if the check was skipped
    if (error && 'status' in error && error.status === 401) {
        console.log('[LicenseGuard] Ignoring 401 unauthorized error, allowing access to make redirection flow work.');
        return jsx(Fragment, { children: children });
    }
    if (isLoading) {
        return jsx(SplashLoading, {});
    }
    const hasFeature = () => {
        if (!featureList)
            return false;
        return featuresToCheck.some((featureToCheck) => featureList.includes(featureToCheck));
    };
    if (hasFeature()) {
        return jsx(Fragment, { children: children });
    }
    switch (checkAccessMode) {
        case 'redirect':
            return jsx(RedirectToFallbackUrl, { fallbackUrl: fallbackUrl });
        case 'empty':
            return null;
        case 'drawStatus':
            return jsx(Fragment, { children: fallbackComponent });
    }
};
const RedirectToFallbackUrl = ({ fallbackUrl }) => {
    console.log('[navigation] Redirecting to fallbackUrl due to missing license feature.');
    window.location.href = fallbackUrl;
    return null;
};

// In some pages, we need to check if the user has a licensed feature to show some content.
const useHasLicensedFeature = (featuresToCheck = []) => {
    const { data: featureList } = useGetLicenseFeaturesQuery();
    return featuresToCheck.some((featureToCheck) => featureList === null || featureList === void 0 ? void 0 : featureList.includes(featureToCheck));
};

const proctorReadyEvent = 'proctoringReady_n6EY';
const ProctoredContext = createContext({
    isInProctor: false,
});
const ProctoredProvider = ({ children }) => {
    const [isInProctor, setIsInProctor] = useState(false);
    const isInIframe = window.self !== window.top;
    useEffect(() => {
        if (!isInIframe) {
            return;
        }
        const handleProctorReady = () => {
            setIsInProctor(true);
        };
        window.addEventListener('message', (event) => {
            if (event.data === proctorReadyEvent) {
                handleProctorReady();
            }
        });
        return () => {
            window.removeEventListener('message', (event) => {
                if (event.data === proctorReadyEvent) {
                    handleProctorReady();
                }
            });
        };
    }, [isInIframe]);
    return (jsx(ProctoredContext.Provider, Object.assign({ value: {
            isInProctor,
        } }, { children: children })));
};
const useProctoredContext = () => {
    const context = React__default.useContext(ProctoredContext);
    if (!context) {
        throw new Error('useProctoredContext must be used within a ProctoredContext!');
    }
    return context;
};

const useIsChatEnabled = () => {
    const enabledByTenant = useTenantSettings('platform.communication_hub', true);
    const enabledByFeature = useHasLicensedFeature(['Platform.Messaging']);
    const { isInProctor } = useProctoredContext();
    return (enabledByTenant === null || enabledByTenant === void 0 ? void 0 : enabledByTenant.enabled) && enabledByFeature && !isInProctor;
};

const CHAT_LOGIN_ENDPOINT = '/chat/login';
const chatLoginApi = navigationPlatformApi.injectEndpoints({
    endpoints: (builder) => ({
        postChatLogin: builder.query({
            query: () => ({
                url: CHAT_LOGIN_ENDPOINT,
                method: 'post',
            }),
        }),
    }),
    overrideExisting: false,
});
const { usePostChatLoginQuery } = chatLoginApi;

const getHashValue = (key) => {
    const matches = window.location.hash.match(new RegExp(key + '=([^&]*)'));
    return matches ? matches[1] : null;
};

const CHAT_HASH = 'chat';
const getChannelFromHash = () => {
    return getHashValue(CHAT_HASH);
};
const checkChatHash = () => {
    return window.location.hash.includes(CHAT_HASH);
};

const ChatContext = createContext({
    totalUnreadCount: 0,
    getUnreadCountByChannelId: () => 0,
    chatWindowOpened: false,
    openChatWindow: () => { },
    closeChatWindow: () => { },
    chatWindowChannel: '',
    setChatWindowChannel: () => { },
    chatLoginData: null,
    bottomSheetTitle: undefined,
});
const ChatProviderInner = ({ children, bottomSheetTitle }) => {
    const { data: chatLoginData } = usePostChatLoginQuery();
    const { unreadMessagesMap } = useChatUnreadMessagesMap(chatLoginData);
    const totalUnreadCount = Object.values(unreadMessagesMap).reduce((sum, unreadCount) => sum + unreadCount, 0);
    const getUnreadCountByChannelId = (channelId) => {
        return unreadMessagesMap[channelId] || 0;
    };
    const [chatWindowOpened, setChatWindowOpened] = useState(false);
    const [chatWindowChannel, setChatWindowChannel] = useState('');
    const hasChatHash = checkChatHash();
    const openChatWindow = useCallback((channel) => {
        setChatWindowChannel(channel);
        setChatWindowOpened(true);
    }, []);
    const closeChatWindow = useCallback(() => {
        setChatWindowChannel('');
        setChatWindowOpened(false);
        if (hasChatHash) {
            window.history.replaceState({}, '', window.location.pathname + window.location.search);
        }
    }, [hasChatHash]);
    useEffect(() => {
        if (!hasChatHash) {
            return;
        }
        setChatWindowOpened(true);
    }, [hasChatHash]);
    return (jsx(ChatContext.Provider, Object.assign({ value: {
            totalUnreadCount,
            getUnreadCountByChannelId,
            chatWindowOpened,
            openChatWindow,
            closeChatWindow,
            chatWindowChannel,
            setChatWindowChannel,
            chatLoginData,
            bottomSheetTitle,
        } }, { children: children })));
};
const useChatContext = () => {
    const context = React__default.useContext(ChatContext);
    if (!context) {
        throw new Error('useChatContext must be used within a ChatProvider!');
    }
    return context;
};
const ChatProvider = ({ children, bottomSheetTitle }) => {
    if (useIsChatEnabled()) {
        return jsx(ChatProviderInner, Object.assign({ bottomSheetTitle: bottomSheetTitle }, { children: children }));
    }
    return jsx(Fragment, { children: children });
};

var css_248z$f = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.ChatWindowIframe-module_iframe__g05qO {\n  width: 100%;\n  height: 100%;\n  border: 0;\n}";
var styles$f = {"iframe":"ChatWindowIframe-module_iframe__g05qO"};
styleInject(css_248z$f);

const ChatWindowIframe = ({ iframeRef, chatWindowUrl }) => {
    return (jsx("iframe", { ref: iframeRef, src: chatWindowUrl, className: styles$f.iframe, title: "chat", allow: "camera *;microphone *;clipboard-read;clipboard-write;encrypted-media", sandbox: "allow-same-origin allow-scripts allow-popups allow-forms allow-downloads", allowFullScreen: true }));
};

const ChatWindow = ({ chatWindowUrl }) => {
    const { chatWindowOpened, closeChatWindow, chatLoginData, bottomSheetTitle = 'Chat' } = useChatContext();
    const iframeRef = useRef(null);
    if (!(chatLoginData === null || chatLoginData === void 0 ? void 0 : chatLoginData.auth_token)) {
        return null;
    }
    if (!chatWindowUrl) {
        return null;
    }
    return (jsx(BottomSheetWrapper, { enableScrollBarGutter: true, noPadding: true, items: [
            {
                content: (_) => chatWindowOpened ? jsx(ChatWindowIframe, { iframeRef: iframeRef, chatWindowUrl: chatWindowUrl }) : null,
                header: () => jsx("div", Object.assign({ className: styles$y.header }, { children: bottomSheetTitle })),
            },
        ], onClose: closeChatWindow, isOpen: chatWindowOpened }));
};

var css_248z$e = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.ChatIndicator-module_chatIndicator__ogE5q {\n  position: relative;\n}\n\n.ChatIndicator-module_chatButton__bKpDz {\n  width: 40px;\n  height: 40px;\n}\n.ChatIndicator-module_chatButton__bKpDz svg {\n  width: 19px;\n}\n@media screen and (max-width: 1279px) {\n  .ChatIndicator-module_chatButton__bKpDz {\n    width: 32px;\n    height: 32px;\n  }\n  .ChatIndicator-module_chatButton__bKpDz svg {\n    width: 17px;\n  }\n}\n\n.ChatIndicator-module_notificationCount__rH-HJ {\n  position: absolute;\n  inset-block-start: -5px;\n  inset-inline-end: -3px;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  min-width: 18px;\n  height: 18px;\n  color: var(--uikit-strict-new-light-00, #ffffff);\n  font-size: 9px;\n  background-color: var(--uikit-decoration-new-red-60, #d4083b);\n  border-radius: 50%;\n  padding: 0 3px;\n  z-index: 1;\n  box-shadow: 0 0 0 1.5px var(--uikit-surface-new-00, #ffffff);\n}";
var styles$e = {"chatIndicator":"ChatIndicator-module_chatIndicator__ogE5q","chatButton":"ChatIndicator-module_chatButton__bKpDz","notificationCount":"ChatIndicator-module_notificationCount__rH-HJ"};
styleInject(css_248z$e);

const ChatIndicator = () => {
    const { totalUnreadCount, openChatWindow } = useChatContext();
    return (jsxs("div", Object.assign({ className: styles$e.chatIndicator }, { children: [jsx(IconButton, { onClick: () => openChatWindow(), "aria-label": "Open chat", iconName: "MessageBoldStroke", color: "tertiary", size: "small", shape: "rounded", className: styles$e.chatButton }), !!totalUnreadCount && jsx("div", Object.assign({ className: styles$e.notificationCount }, { children: totalUnreadCount }))] })));
};

const makeChannelUrl = (chatBaseUrl, channel) => `${chatBaseUrl}/group/${channel}`;
const useChatWindowUrl = () => {
    const { chatWindowChannel: customChannelWithUrl, chatLoginData } = useChatContext();
    const chatBaseUrl = chatLoginData === null || chatLoginData === void 0 ? void 0 : chatLoginData.chat_base_url;
    const defaultChannel = chatLoginData === null || chatLoginData === void 0 ? void 0 : chatLoginData.default_channel;
    const token = chatLoginData === null || chatLoginData === void 0 ? void 0 : chatLoginData.auth_token;
    if (!chatBaseUrl || !token) {
        return null;
    }
    const channelFromHash = getChannelFromHash();
    const prepareUrl = () => {
        // We have a channel in the hash, so we should use it
        // #chat=<channel>
        if (channelFromHash) {
            return makeChannelUrl(chatBaseUrl, channelFromHash);
        }
        const defaultChannelUrl = makeChannelUrl(chatBaseUrl, defaultChannel);
        // We have only #chat in the hash, so we should use the default channel
        if (checkChatHash()) {
            return defaultChannelUrl;
        }
        // customChannelWithUrl with comes from other apps
        return customChannelWithUrl || defaultChannelUrl;
    };
    const url = prepareUrl();
    if (!url) {
        return null;
    }
    if (!isValidUrl(url)) {
        console.log('Invalid channel URL');
        return null;
    }
    const urlWithTokenParam = new URL(url);
    // resumeToken is a special query parameter that is used to restore the chat session - RocketChat
    urlWithTokenParam.searchParams.append('resumeToken', token);
    return urlWithTokenParam.toString();
};
function isValidUrl(str) {
    try {
        new URL(str);
        return true;
    }
    catch (error) {
        return false;
    }
}

const Chat = () => {
    const chatWindowUrl = useChatWindowUrl();
    if (!useIsChatEnabled()) {
        return null;
    }
    return (jsxs("div", { children: [jsx(ChatIndicator, {}), jsx(ChatWindow, { chatWindowUrl: chatWindowUrl })] }));
};

const selectActionsLimit = createDraftSafeSelector(selectActionsRoot, (state) => {
    return state.actionsLimit;
});

const { Actions } = Header;
const Headline = ({ userMenu }) => {
    const { isTablet, isMobile } = useBreakpoints();
    const isHeaderHidden = useHideHeaderOnScroll();
    const dispatch = useDispatch();
    const { user, features, rightSlot, leftSlot, leftAvatarSlot, isTeamSwitcherExist, isTeamSwitcherVisible, logoutClickEvent, } = useNavigationContext();
    const { mode } = usePlatformContext();
    const breadcrumbsSlugs = useSelector(selectBreadcrumbsSlugs);
    const isBreadcrumbs = breadcrumbsSlugs && breadcrumbsSlugs.size > 0;
    const actions = useSelector(selectLastActions);
    const actionsLimit = useSelector(selectActionsLimit);
    return (jsxs("header", Object.assign({ className: cn(styles$z.header, { [styles$z.hidden]: isHeaderHidden }) }, { children: [(features.hamburgerVisible || isTablet) && (jsx("div", Object.assign({ className: styles$z.burger }, { children: jsx(HamburgerOpenButton, { showRoundedIcon: isTablet, onClick: () => dispatch(setHamburgerOpen(true)) }) }))), jsxs("div", Object.assign({ className: styles$z.main }, { children: [jsx(Logo, { className: styles$z.logo }), jsxs("nav", Object.assign({ className: styles$z.nav }, { children: [isBreadcrumbs && jsx(Breadcrumbs, {}), !isBreadcrumbs && jsx(Sections, {}), leftSlot] })), jsxs("div", Object.assign({ className: styles$z.actions }, { children: [isTeamSwitcherExist && isTeamSwitcherVisible && jsx(TeamSwitcher, {}), rightSlot, (actions === null || actions === void 0 ? void 0 : actions.length) ? jsx(Actions, { items: actions, limit: actionsLimit }) : null, jsx(ImpersonationComponent, { onSignOut: () => logoutClickEvent.emit(), user: user })] })), leftAvatarSlot !== null && leftAvatarSlot !== void 0 ? leftAvatarSlot : null, (!isMobile || mode === 'schools') && (jsxs("div", Object.assign({ className: styles$z.user }, { children: [jsx(Chat, {}), userMenu || jsx(UserMenu, {})] })))] }))] })));
};

var css_248z$d = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.SidebarComponentComponentBadge-module_badgeWrapper__Z7YFg {\n  --size: var(--size-s);\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  position: absolute;\n  inset-block-start: 6px;\n  inset-inline-end: 6px;\n  width: var(--size);\n  height: var(--size);\n  border-radius: 50%;\n  font-size: 9px;\n  color: var(--uikit-strict-new-light-00, #ffffff);\n  background-color: var(--uikit-decoration-new-red-60, #d4083b);\n  z-index: 1;\n}\n\n@media screen and (max-width: 1280px) {\n  .SidebarComponentComponentBadge-module_badgeWrapper__Z7YFg {\n    position: relative;\n    inset-block-start: unset;\n    inset-inline-end: unset;\n    margin-inline-start: var(--size-xxxs);\n    background-color: var(--uikit-decoration-new-red-20, #ffc9c0);\n    color: var(--uikit-text-new-primary, #141414);\n  }\n}";
var styles$d = {"badgeWrapper":"SidebarComponentComponentBadge-module_badgeWrapper__Z7YFg"};
styleInject(css_248z$d);

const SidebarComponentComponentBadge = (props) => {
    const { badge } = props;
    return jsx("div", Object.assign({ className: styles$d.badgeWrapper }, { children: badge }));
};

var css_248z$c = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.SidebarButtonComponentChild-module_button__pVaz8,\n.SidebarButtonComponentChild-module_button__pVaz8:visited {\n  display: flex;\n  align-items: center;\n  margin: 0;\n  padding: 0;\n  border: none;\n  background: none;\n  color: inherit;\n  font-family: inherit;\n  font-size: inherit;\n  font-weight: inherit;\n  cursor: pointer;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  position: relative;\n  width: var(--size-xxxl);\n  height: var(--size-xxxl);\n  margin: 0 auto;\n  margin-block-start: 2px;\n  margin-block-end: 2px;\n  border-radius: 4px;\n  color: var(--ui-sidebar-button-text-color, var(--uikit-text-new-primary, #141414));\n}\n.SidebarButtonComponentChild-module_button__pVaz8:hover, .SidebarButtonComponentChild-module_button__pVaz8:visited:hover,\n.SidebarButtonComponentChild-module_button__pVaz8:visited:hover,\n.SidebarButtonComponentChild-module_button__pVaz8:visited:visited:hover {\n  background: var(--ui-button-state-hover, var(--color-primary-5));\n  color: var(--ui-button-state-hover-text-color);\n}\n.SidebarButtonComponentChild-module_button__pVaz8:active, .SidebarButtonComponentChild-module_button__pVaz8:visited:active,\n.SidebarButtonComponentChild-module_button__pVaz8:visited:active,\n.SidebarButtonComponentChild-module_button__pVaz8:visited:visited:active {\n  background: var(--ui-button-state-active, var(--color-primary-15));\n  color: var(--ui-button-state-active-text-color);\n}\n.SidebarButtonComponentChild-module_button__pVaz8:focus,\n.SidebarButtonComponentChild-module_button__pVaz8:visited:focus {\n  z-index: 14;\n  outline: 0;\n}\n.SidebarButtonComponentChild-module_button__pVaz8.SidebarButtonComponentChild-module_active__gDZOV,\n.SidebarButtonComponentChild-module_button__pVaz8:visited.SidebarButtonComponentChild-module_active__gDZOV {\n  color: var(--ui-button-state-active-text-color);\n}\n\n.SidebarButtonComponentChild-module_marked__1fQVT {\n  --mark-size: var(--size-xxs);\n  --mark-inset-block-start: 14px;\n  --mark-inset-inline-end: 9px;\n}\n.SidebarButtonComponentChild-module_marked__1fQVT::before, .SidebarButtonComponentChild-module_marked__1fQVT::after {\n  content: \"\";\n  position: absolute;\n  border-radius: 50%;\n}\n.SidebarButtonComponentChild-module_marked__1fQVT::before {\n  inset-block-start: calc(var(--mark-inset-block-start) - 2px);\n  inset-inline-end: calc(var(--mark-inset-inline-end) - 2px);\n  width: calc(var(--size-xxs) + 4px);\n  height: calc(var(--size-xxs) + 4px);\n  background: inherit;\n}\n.SidebarButtonComponentChild-module_marked__1fQVT::after {\n  inset-block-start: var(--mark-inset-block-start);\n  inset-inline-end: var(--mark-inset-inline-end);\n  width: var(--size-xxs);\n  height: var(--size-xxs);\n  background: var(--color-danger);\n}\n\n.SidebarButtonComponentChild-module_active__gDZOV, .SidebarButtonComponentChild-module_active__gDZOV:hover,\n.SidebarButtonComponentChild-module_active__gDZOV:visited,\n.SidebarButtonComponentChild-module_active__gDZOV:visited:hover {\n  background: var(--ui-sidebar-button-active-bg-color, var(--uikit-bg-c-default-soft, #efefef));\n}";
var styles$c = {"button":"SidebarButtonComponentChild-module_button__pVaz8","active":"SidebarButtonComponentChild-module_active__gDZOV","marked":"SidebarButtonComponentChild-module_marked__1fQVT"};
styleInject(css_248z$c);

const SidebarButtonComponentChild = ({ iconId, name, url, isActive, isMarked, target, onClick, badge, slug, }) => {
    const buttonClasses = cn(styles$c.button, {
        [styles$c.active]: isActive,
        [styles$c.button]: !url,
        [styles$c.marked]: isMarked,
    });
    const iconComponent = jsx(SvgComponent, { name: iconId, size: "md" });
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            onClick === null || onClick === void 0 ? void 0 : onClick(event);
        }
    }, [onClick]);
    if (!url) {
        return (jsxs("button", Object.assign({ className: buttonClasses, "aria-label": name, "data-slug": slug, "data-icon": iconId, onClick: onClick, onKeyDown: handleKeyDown }, { children: [iconComponent, " ", badge && jsx(SidebarComponentComponentBadge, { badge: badge })] })));
    }
    return (jsxs("a", Object.assign({ href: url, target: target, className: buttonClasses, "aria-label": name, "data-slug": slug, "data-icon": iconId, onClick: onClick }, { children: [iconComponent, " ", badge && jsx(SidebarComponentComponentBadge, { badge: badge })] })));
};

const SidebarButtonComponent = (_a) => {
    var { iconId, name, withHint } = _a, rest = __rest(_a, ["iconId", "name", "withHint"]);
    if (!iconId) {
        return null;
    }
    if (withHint) {
        return (jsx(Tooltip2, Object.assign({ content: name, position: "right" }, { children: jsx(SidebarButtonComponentChild, Object.assign({ iconId: iconId, name: name }, rest)) })));
    }
    return (jsx(Fragment, { children: jsx(SidebarButtonComponentChild, Object.assign({ iconId: iconId, name: name }, rest)) }));
};

const DEFAULT_ICON$1 = 'WarningBoldFilled';
const SidebarButton = ({ slug, isActive, withHint }) => {
    var _a;
    const { onLinkClick } = usePlatformContext();
    const mixedIndex = useSelector(selectMixedIndex);
    const item = (_a = mixedIndex.get(slug)) !== null && _a !== void 0 ? _a : null;
    const handleLinkClick = useCallback((ev) => {
        if (!item) {
            return;
        }
        onLinkClick(ev, item);
    }, [onLinkClick, item]);
    if (!item) {
        return null;
    }
    return (jsx(SidebarButtonComponent, { iconId: item.icon_id || DEFAULT_ICON$1, name: item.name, slug: item.slug, url: item.url, isActive: isActive, withHint: withHint, onClick: handleLinkClick }));
};

var css_248z$b = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.SidebarPanelComponent-module_sidebar__I1unV {\n  --sidebar-border-color: var(--layout-border-color);\n  align-self: stretch;\n  grid-area: sidebar;\n  border-inline-end: 1px solid var(--sidebar-border-color);\n  background-color: var(--ui-left-bar-background-color);\n  z-index: 9;\n  display: none;\n}\n@media (min-width: 1280px) {\n  .SidebarPanelComponent-module_sidebar__I1unV {\n    display: block;\n  }\n}\n\n.SidebarPanelComponent-module_nav__or-P2 {\n  position: sticky;\n  z-index: 14;\n  top: var(--layout-header-height);\n  left: 0;\n  width: 100%;\n  max-height: calc(100vh - var(--layout-header-height));\n  overscroll-behavior: contain;\n}\n.SidebarPanelComponent-module_noHamburgerMode__I6oDN .SidebarPanelComponent-module_nav__or-P2 {\n  padding-top: 6px;\n}\n\n.SidebarPanelComponent-module_list__xgT1Q {\n  margin: 0;\n  padding: 0;\n  list-style: none;\n}\n\n.SidebarPanelComponent-module_item__lNLPV {\n  position: relative;\n  display: flex;\n  justify-content: center;\n}\n.SidebarPanelComponent-module_item__lNLPV:not(:first-child) {\n  margin-top: 1px;\n}";
var styles$b = {"sidebar":"SidebarPanelComponent-module_sidebar__I1unV","nav":"SidebarPanelComponent-module_nav__or-P2","noHamburgerMode":"SidebarPanelComponent-module_noHamburgerMode__I6oDN","list":"SidebarPanelComponent-module_list__xgT1Q"};
styleInject(css_248z$b);

var css_248z$a = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.SidebarMenu-module_container__5omKN {\n  position: relative;\n}\n.SidebarMenu-module_container__5omKN .SidebarMenu-module_menu__xHJEA {\n  display: none;\n}\n.SidebarMenu-module_container__5omKN:hover .SidebarMenu-module_menu__xHJEA, .SidebarMenu-module_container__5omKN:focus-within .SidebarMenu-module_menu__xHJEA {\n  display: block;\n}";
var styles$a = {"container":"SidebarMenu-module_container__5omKN","menu":"SidebarMenu-module_menu__xHJEA"};
styleInject(css_248z$a);

var css_248z$9 = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.HamburgerItemComponent-module_item__K3-z9,\na.HamburgerItemComponent-module_item__K3-z9 {\n  --navigation-height: var(--base-navigation-height, var(--size-xxxl));\n  --navigation-inline-start: var(--base-navigation-inline-start, var(--size-xs));\n  --navigation-inline-end: var(--base-navigation-inline-end, var(--size-xs));\n  --navigation-radius: var(--base-navigation-radius, 4px);\n  --navigation-element-color: var(--ui-sidebar-button-text-color, var(--uikit-text-new-primary, #141414));\n  display: flex;\n  align-items: center;\n  height: var(--navigation-height);\n  padding-inline-start: var(--navigation-inline-start);\n  padding-inline-end: var(--navigation-inline-end);\n  margin-block-start: 2px;\n  margin-block-end: 2px;\n  border-radius: var(--navigation-radius);\n  color: var(--navigation-element-color);\n  text-decoration: none;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n  user-select: none;\n  font-family: \"Hauss\", sans-serif;\n  font-weight: 500;\n  font-size: 1.125rem;\n  line-height: 1.75rem;\n  letter-spacing: normal;\n}\n.HamburgerItemComponent-module_item__K3-z9:visited,\na.HamburgerItemComponent-module_item__K3-z9:visited {\n  color: var(--navigation-element-color);\n}\n.HamburgerItemComponent-module_item__K3-z9:hover, .HamburgerItemComponent-module_item__K3-z9:visited:hover,\na.HamburgerItemComponent-module_item__K3-z9:hover,\na.HamburgerItemComponent-module_item__K3-z9:visited:hover {\n  background: var(--ui-button-state-hover, var(--color-primary-5));\n  color: var(--ui-button-state-hover-text-color);\n}\n.HamburgerItemComponent-module_item__K3-z9:active, .HamburgerItemComponent-module_item__K3-z9:visited:active,\na.HamburgerItemComponent-module_item__K3-z9:active,\na.HamburgerItemComponent-module_item__K3-z9:visited:active {\n  background: var(--ui-button-state-active, var(--color-primary-15));\n  color: var(--ui-button-state-active-text-color);\n}\n.HamburgerItemComponent-module_item__K3-z9.HamburgerItemComponent-module_active__9Zhyb,\na.HamburgerItemComponent-module_item__K3-z9.HamburgerItemComponent-module_active__9Zhyb {\n  background-color: var(--ui-button-state-active);\n  color: var(--ui-button-state-active-text-color);\n}\n\n.HamburgerItemComponent-module_button__HtBpc {\n  border: 0;\n  cursor: pointer;\n  width: 100%;\n  background: transparent;\n}\n.HamburgerItemComponent-module_button__HtBpc:hover, .HamburgerItemComponent-module_button__HtBpc:visited:hover {\n  background: var(--ui-button-state-hover, var(--color-primary-5));\n  color: var(--ui-button-state-hover-text-color);\n}\n.HamburgerItemComponent-module_button__HtBpc:active, .HamburgerItemComponent-module_button__HtBpc:visited:active {\n  background: var(--ui-button-state-active, var(--color-primary-15));\n  color: var(--ui-button-state-active-text-color);\n}\n.HamburgerItemComponent-module_button__HtBpc.HamburgerItemComponent-module_active__9Zhyb {\n  background-color: var(--ui-button-state-active);\n}\n\n.HamburgerItemComponent-module_itemSmall__fEGat {\n  height: var(--size-xxl);\n  font-family: \"Hauss\", sans-serif;\n  font-weight: 500;\n  font-size: 1rem;\n  line-height: 1.5rem;\n  letter-spacing: normal;\n}\n\n.HamburgerItemComponent-module_icon__jgUYE {\n  display: flex;\n  margin-inline-end: var(--size-xs);\n}\n\n.HamburgerItemComponent-module_label__f2DDU {\n  position: relative;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}";
var styles$9 = {"item":"HamburgerItemComponent-module_item__K3-z9","active":"HamburgerItemComponent-module_active__9Zhyb","button":"HamburgerItemComponent-module_button__HtBpc","itemSmall":"HamburgerItemComponent-module_itemSmall__fEGat","icon":"HamburgerItemComponent-module_icon__jgUYE","label":"HamburgerItemComponent-module_label__f2DDU"};
styleInject(css_248z$9);

const HamburgerItemComponent = ({ item, size, iconSize = 'md', className, isActive, onClick, onKeyDown, }) => {
    const { url = null, icon_id, target = '_top' } = item;
    const child = (jsxs(Fragment, { children: [icon_id && (jsx("span", Object.assign({ className: styles$9.icon }, { children: jsx(SvgComponent, { name: icon_id, size: iconSize }) }))), jsx("span", Object.assign({ className: styles$9.label }, { children: item.name }))] }));
    if (!url) {
        return (jsx("button", Object.assign({ className: cn(styles$9.item, styles$9.button, {
                [styles$9.itemSmall]: size === 'small',
                [styles$9.active]: isActive,
            }, className, 'hamburger-item-component'), "data-slug": item.slug, onClick: onClick, onKeyDown: onKeyDown }, { children: child })));
    }
    return (jsx("a", Object.assign({ href: url, target: target, className: cn(styles$9.item, {
            [styles$9.itemSmall]: size === 'small',
            [styles$9.active]: isActive,
        }, className, 'hamburger-item-component'), "data-slug": item.slug, onClick: onClick, onKeyDown: onKeyDown }, { children: child })));
};

var css_248z$8 = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.HamburgerSectionHeader-module_container__NLtyS {\n  padding: 0;\n  margin-block-start: var(--size-l);\n}\n.HamburgerSectionHeader-module_container__NLtyS .HamburgerSectionHeader-module_menu__1a46w,\n.HamburgerSectionHeader-module_container__NLtyS .HamburgerSectionHeader-module_menu__1a46w ul,\n.HamburgerSectionHeader-module_container__NLtyS .HamburgerSectionHeader-module_menu__1a46w li {\n  list-style-type: none;\n  padding: 0;\n  margin: 0;\n}";
var styles$8 = {"container":"HamburgerSectionHeader-module_container__NLtyS","menu":"HamburgerSectionHeader-module_menu__1a46w"};
styleInject(css_248z$8);

var css_248z$7 = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.HamburgerSectionHeaderComponent-module_control__T1-dT {\n  display: flex;\n  align-items: center;\n  justify-content: space-between;\n  width: 100%;\n  height: var(--size-l);\n  padding-inline-start: var(--size-xxxl);\n  font-size: var(--font-xs);\n  font-weight: var(--font-semibold);\n  text-transform: uppercase;\n  cursor: default;\n}\n.HamburgerSectionHeaderComponent-module_control__T1-dT::before {\n  content: unset;\n}\n\n.HamburgerSectionHeaderComponent-module_name__eDKua {\n  color: var(--ui-hamburger-section-name-color);\n  text-decoration: none;\n  user-select: none;\n}\n\n.HamburgerSectionHeaderComponent-module_button__WuoQ9 {\n  display: flex;\n  align-items: center;\n  margin: 0;\n  padding: 0;\n  border: none;\n  background: none;\n  color: inherit;\n  font-family: inherit;\n  font-size: inherit;\n  font-weight: inherit;\n  cursor: pointer;\n}\n\n.HamburgerSectionHeaderComponent-module_chevron__mzdso {\n  rotate: 180deg;\n  transition: rotate 0.2s;\n  color: var(--ui-hamburger-section-chevron-color);\n}\n.HamburgerSectionHeaderComponent-module_chevron__mzdso.HamburgerSectionHeaderComponent-module_closed__jgflE {\n  rotate: 0deg;\n}";
var styles$7 = {"control":"HamburgerSectionHeaderComponent-module_control__T1-dT","name":"HamburgerSectionHeaderComponent-module_name__eDKua","button":"HamburgerSectionHeaderComponent-module_button__WuoQ9","chevron":"HamburgerSectionHeaderComponent-module_chevron__mzdso","closed":"HamburgerSectionHeaderComponent-module_closed__jgflE"};
styleInject(css_248z$7);

const handleKeyDownClick = (handler) => (e) => {
    if (['Enter', 'Space'].includes(e.code)) {
        handler(e);
        e.preventDefault();
    }
};

const HamburgerSectionHeaderComponent = ({ isOpen, onClick, title }) => {
    if (!title)
        return null;
    return (jsxs("li", Object.assign({ role: "menuitem", tabIndex: 0, className: styles$7.control, onClick: onClick, onKeyDown: handleKeyDownClick(() => onClick === null || onClick === void 0 ? void 0 : onClick()) }, { children: [jsx("span", Object.assign({ className: styles$7.name }, { children: title })), jsx("button", Object.assign({ tabIndex: -1, className: styles$7.button }, { children: jsx("div", Object.assign({ className: cn(styles$7.chevron, !isOpen && styles$7.closed) }, { children: jsx(SvgComponent, { name: "ChevronSmallDownBoldStroke", size: "md" }) })) }))] })));
};

const HamburgerSectionHeader = ({ title, children, className }) => {
    const [isOpen, setIsOpen] = useState(true);
    return (jsxs("ul", Object.assign({ className: cn(styles$8.container, className) }, { children: [jsx(HamburgerSectionHeaderComponent, { isOpen: isOpen, onClick: () => setIsOpen(!isOpen), title: title }), isOpen ? jsx("ul", Object.assign({ className: styles$8.menu }, { children: children })) : null] })));
};

const HamburgerTenantLinks = () => {
    const links = useCommonTenantLinks();
    if (!links)
        return null;
    return (jsx(Fragment, { children: links.map((item) => {
            if (item.isSection) {
                return (jsx(HamburgerSectionHeader, Object.assign({ title: item.name }, { children: Array.from(item.children.values()).map((child) => (jsx(HamburgerItemComponent, { item: child }, child.name))) }), item.name));
            }
            return jsx(HamburgerItemComponent, { item: item }, item.name);
        }) }));
};

var css_248z$6 = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.SidebarMenuComponent-module_menu__ZSEqc {\n  --base-navigation-inline-start: var(--size-s);\n  --base-navigation-inline-end: var(--size-s);\n  --base-navigation-radius: 0;\n  margin: 0;\n  padding: 0;\n  list-style: none;\n  position: absolute;\n  top: 0;\n  left: calc(100% + 9px);\n  border: 1px solid var(--color-primary);\n  background: var(--ui-left-bar-background-color, var(--uikit-bg-c-default, #ffffff));\n  padding: var(--size-xxs) 0;\n}\n.SidebarMenuComponent-module_menu__ZSEqc::before {\n  content: \"\";\n  position: absolute;\n  top: 0;\n  bottom: 0;\n  left: -10px;\n  width: 10px;\n}\nhtml[dir=rtl] .SidebarMenuComponent-module_menu__ZSEqc {\n  left: auto;\n  right: calc(100% + 4px);\n}\n\n.SidebarMenuComponent-module_item__wIqG6 {\n  line-height: var(--size-l);\n}";
var styles$6 = {"menu":"SidebarMenuComponent-module_menu__ZSEqc","item":"SidebarMenuComponent-module_item__wIqG6"};
styleInject(css_248z$6);

const SidebarMenuComponent = ({ items, onClick, activePath, className }) => {
    if (!items.length)
        return null;
    const handleClick = (evt, item) => {
        onClick === null || onClick === void 0 ? void 0 : onClick(evt, item);
        evt.currentTarget.blur();
    };
    const handleKeyDown = (evt, item) => {
        if (!['Enter', 'Space'].includes(evt.key))
            return;
        onClick === null || onClick === void 0 ? void 0 : onClick(evt, item);
        evt.preventDefault();
    };
    return (jsx("ul", Object.assign({ className: cn(styles$6.menu, className) }, { children: items.map((item) => (jsx("li", Object.assign({ className: styles$6.item }, { children: jsx(HamburgerItemComponent, { item: item, onClick: (evt) => handleClick(evt, item), onKeyDown: (evt) => handleKeyDown(evt, item), size: "small", isActive: activePath === null || activePath === void 0 ? void 0 : activePath.has(item.slug) }, `${item.name}-item`) }), item.name))) })));
};

const SidebarMenu = (_a) => {
    var { children, activePath } = _a, props = __rest(_a, ["children", "activePath"]);
    return (jsxs("div", Object.assign({ className: styles$a.container }, { children: [children, jsx(SidebarMenuComponent, { items: props.items, onClick: props.onItemClick, activePath: activePath, className: styles$a.menu })] })));
};

var css_248z$5 = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.SidebarListItem-module_item__nxtjg {\n  position: relative;\n  display: flex;\n  justify-content: center;\n}\n.SidebarListItem-module_item__nxtjg:not(:first-child) {\n  margin-top: 1px;\n}";
var styles$5 = {"item":"SidebarListItem-module_item__nxtjg"};
styleInject(css_248z$5);

const SidebarListItem = ({ children }) => jsx("li", Object.assign({ className: styles$5.item }, { children: children }));

const DEFAULT_ICON = 'WarningBoldFilled';
const SidebarSectionComponent = ({ item, onClick, withHint, activePath }) => {
    var _a;
    if (!((_a = item.children) === null || _a === void 0 ? void 0 : _a.size)) {
        return null;
    }
    const items = Array.from(item.children.values());
    const previewItem = item.icon_id ? item : items[0];
    return (jsx(SidebarListItem, { children: jsx(SidebarMenu, Object.assign({ items: items, onItemClick: onClick, activePath: activePath }, { children: jsx(SidebarButtonComponent, { iconId: previewItem.icon_id || DEFAULT_ICON, name: previewItem.name, slug: previewItem.slug, url: previewItem.url, withHint: withHint, onClick: (ev) => onClick === null || onClick === void 0 ? void 0 : onClick(ev, previewItem), isActive: activePath === null || activePath === void 0 ? void 0 : activePath.has(previewItem.slug) }) })) }));
};

const SidebarSection = ({ item, withHint }) => {
    const activePath = useSelector(selectActivePath);
    const { onLinkClick } = usePlatformContext();
    return jsx(SidebarSectionComponent, { item: item, activePath: activePath, onClick: onLinkClick, withHint: withHint });
};

const SidebarPanelComponent = ({ slugs }) => {
    const { mode } = usePlatformContext();
    const features = FeaturesMap[mode];
    const activePath = useSelector(selectActivePath);
    const mixedIndex = useSelector(selectMixedIndex);
    const listChild = useMemo(() => {
        return Array.from(slugs)
            .map((slug) => {
            const item = mixedIndex.get(slug);
            if (!item) {
                return null;
            }
            if (item.isSection) {
                if (!item.firstChildSlug) {
                    return null;
                }
                return jsx(SidebarSection, { item: item }, slug);
            }
            return (jsx(SidebarListItem, { children: jsx(SidebarButton, { slug: slug, isActive: activePath.has(slug), withHint: true }, `${slug}-button`) }, slug));
        })
            .filter((item) => !!item);
    }, [activePath, mixedIndex, slugs]);
    return (jsx("aside", Object.assign({ className: cn(styles$b.sidebar, !features.hamburgerVisible && styles$b.noHamburgerMode, 'sidebar-panel') }, { children: jsx("nav", Object.assign({ className: styles$b.nav }, { children: listChild && listChild.length > 0 && (jsx("ul", Object.assign({ className: styles$b.list, role: "menu" }, { children: listChild }))) })) })));
};

const selectSidebarTopLayerSlugs = createSelector(selectLastSidebar, (items) => {
    return Array.from(getCollectionSlugs(items));
});

const HamburgerItem = ({ slug, size, iconSize = 'md' }) => {
    const { onLinkClick } = usePlatformContext();
    const activePath = useSelector(selectActivePath);
    const mixedIndex = useSelector(selectMixedIndex);
    const item = mixedIndex.get(slug);
    const { t } = useTranslation();
    const handleClick = useCallback((ev) => {
        if (!item) {
            return;
        }
        onLinkClick(ev, item);
    }, [onLinkClick, item]);
    if (!item) {
        return null;
    }
    const translatedName = t(item.name);
    return (jsx(HamburgerItemComponent, { item: Object.assign(Object.assign({}, item), { name: translatedName }), size: size, iconSize: iconSize, onClick: handleClick, isActive: activePath.has(slug) }));
};

var css_248z$4 = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.HamburgerMenu-module_list__P8T41 {\n  margin: 0;\n  padding: 0;\n  list-style: none;\n}\n\n.HamburgerMenu-module_itemElement__2dn6A {\n  position: relative;\n}";
var styles$4 = {"list":"HamburgerMenu-module_list__P8T41","itemElement":"HamburgerMenu-module_itemElement__2dn6A"};
styleInject(css_248z$4);

var css_248z$3 = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.HamburgerSectionComponent-module_list__gh7W3.HamburgerSectionComponent-module_closed__qPhfF {\n  display: none;\n}";
var styles$3 = {"list":"HamburgerSectionComponent-module_list__gh7W3"};
styleInject(css_248z$3);

const HamburgerSectionComponent = ({ item }) => {
    if (!item || !item.children) {
        throw new Error('@platform/layout: wrong item object');
    }
    return (jsx(HamburgerSectionHeader, Object.assign({ title: item.name }, { children: jsx(HamburgerMenu, { className: styles$3.list, items: item.children }) })));
};

const HamburgerSection = ({ slug }) => {
    const mixedIndex = useSelector(selectMixedIndex);
    const item = mixedIndex.get(slug);
    if (!item || !item.isSection) {
        return null;
    }
    return jsx(HamburgerSectionComponent, { item: item });
};

const flattenTree = (tree) => {
    return Array.from(tree).reduce((acc, [slug, item]) => {
        if (item.isSection && item.children) {
            return [...acc, ...flattenTree(item.children)];
        }
        return [...acc, item];
    }, []);
};
const HamburgerMenu = ({ items, className }) => {
    const mode = useSelector(selectMode);
    const { hideHamburgerSections } = FeaturesMap[mode];
    const itemsArray = useMemo(() => {
        if (hideHamburgerSections) {
            return flattenTree(items);
        }
        return Array.from(items.values());
    }, [hideHamburgerSections, items]);
    if (itemsArray.length === 0) {
        return null;
    }
    return (jsx("ul", Object.assign({ className: cn(styles$4.list, className) }, { children: itemsArray.map((item) => {
            return (jsxs("li", Object.assign({ className: cn(!item.isSection && styles$4.itemElement) }, { children: [!item.isSection && jsx(HamburgerItem, { slug: item.slug }, `${item.slug}-item`), item.isSection && jsx(HamburgerSection, { slug: item.slug }, `${item.slug}-section`)] }), item.slug));
        }) })));
};

const PortalHamburgerPlugin = () => {
    useGetNavigationStructureQuery();
    const hamburgerTree = useSelector(selectHamburgerTree);
    if (!hamburgerTree) {
        return null;
    }
    return (jsx(HamburgerPlugin, { children: jsx(HamburgerMenu, { items: hamburgerTree }) }));
};

const selectSidebarDefaultSlugs = createSelector(selectSaturatedTree, (tree) => {
    var _a;
    return Array.from((_a = tree === null || tree === void 0 ? void 0 : tree.values()) !== null && _a !== void 0 ? _a : []).map((item) => item.slug);
});

const selectSidebarCollection = createSelector(selectSidebarRoot, (state) => state.collection);

const useSidebarSlugs = () => {
    const { isMobile } = useBreakpoints();
    const { mode } = usePlatformContext();
    const features = FeaturesMap[mode];
    const topLayerSlugs = useSelector(selectSidebarTopLayerSlugs);
    const sidebarDefaultSlugs = useSelector(selectSidebarDefaultSlugs);
    const sidebarCollection = useSelector(selectSidebarCollection);
    const instructorDashboardEnabled = useSelector(selectInstructorDashboardEnabled);
    const slugs = useMemo(() => {
        if (isMobile) {
            return null;
        }
        if (features.isSidebarOptional) {
            // if there is no custom elements, then we have to hide sidebar for learner
            if (topLayerSlugs.length === 0) {
                return null;
            }
            return topLayerSlugs;
        }
        if (sidebarCollection.length !== 0) {
            return topLayerSlugs;
        }
        return instructorDashboardEnabled ? [INSTRUCTOR_DASHBOARD_SLUG, ...sidebarDefaultSlugs] : sidebarDefaultSlugs;
    }, [
        features.isSidebarOptional,
        instructorDashboardEnabled,
        isMobile,
        sidebarCollection.length,
        sidebarDefaultSlugs,
        topLayerSlugs,
    ]);
    return slugs;
};

const SidebarPanel = () => {
    const slugs = useSidebarSlugs();
    if (!slugs) {
        return null;
    }
    return jsx(SidebarPanelComponent, { slugs: slugs });
};

const KeyboardKeys = {
    esc: 'Escape'};

var css_248z$2 = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n@media (max-width: 1278px) {\n  .HamburgerPanel-module_leftPanel__hLWcA {\n    --left-panel--background-color: var(--color-primary-5);\n  }\n}\n\n.HamburgerPanel-module_header__ow-mC {\n  display: flex;\n  align-items: stretch;\n  justify-content: space-between;\n  height: var(--layout-header-height);\n}\n@media (min-width: 1279px) {\n  .HamburgerPanel-module_header__ow-mC {\n    justify-content: start;\n    box-shadow: inset 0 -2px 0 -1px var(--navigation-border-color);\n  }\n}\n\n.HamburgerPanel-module_close__qUiaq {\n  flex: 0 0 auto;\n  display: flex;\n  align-items: center;\n  justify-content: center;\n  padding: var(--size-s);\n}\n\n.HamburgerPanel-module_logo__NuXdj {\n  flex: 0 0 auto;\n  display: flex;\n  margin-inline: var(--layout-inline-gap);\n  align-items: center;\n}\n\n.HamburgerPanel-module_menu__HXyRT {\n  padding: var(--size-xs) 6px;\n}";
var styles$2 = {"leftPanel":"HamburgerPanel-module_leftPanel__hLWcA","header":"HamburgerPanel-module_header__ow-mC","close":"HamburgerPanel-module_close__qUiaq","logo":"HamburgerPanel-module_logo__NuXdj","menu":"HamburgerPanel-module_menu__HXyRT"};
styleInject(css_248z$2);

const HamburgerPanel = () => {
    const dispatch = useDispatch();
    const hamburgerTree = useSelector(selectHamburgerTree);
    const isPanelOpen = useSelector(selectIsHamburgerOpen);
    const { isMobile } = useBreakpoints();
    const handleClose = useCallback(() => {
        dispatch(setHamburgerOpen(false));
    }, [dispatch]);
    useEffect(() => {
        const close = (key) => {
            if (key.code === KeyboardKeys.esc) {
                handleClose();
            }
        };
        window.addEventListener('keydown', close);
        return () => window.removeEventListener('keydown', close);
    }, [handleClose]);
    return (jsxs(LeftPanelComponent, Object.assign({ isOpen: isPanelOpen, onClose: handleClose, className: styles$2.leftPanel }, { children: [jsxs("header", Object.assign({ className: styles$2.header }, { children: [jsx("div", Object.assign({ className: styles$2.close }, { children: jsx(HamburgerCloseButton, { onClick: handleClose }) })), !isMobile && (jsx("div", Object.assign({ className: styles$2.logo }, { children: jsx(TenantLogo, { isHamburger: true }) })))] })), jsx("nav", Object.assign({ className: styles$2.menu }, { children: hamburgerTree && jsx(HamburgerMenu, { items: hamburgerTree }) }))] })));
};

var css_248z$1 = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\nbody {\n  --ui-sidebar-bg-color: var(--ts-ui-sidebar-bg-color, var(--uikit-bg-c-default));\n  --ui-sidebar-scrollbar-thumb-color: var(\n    --ts-ui-sidebar-scrollbar-thumb-color,\n    var(--uikit-dark-fixed-text-new-graphics-only-inverse)\n  );\n  --ui-sidebar-button-text-color: var(--ts-ui-sidebar-button-text-color, var(--uikit-text-new-primary));\n  --ui-sidebar-button-hover-bg-color: var(--ts-ui-sidebar-button-hover-bg-color, var(--uikit-bg-c-default-soft));\n  --ui-sidebar-button-hover-text-color: var(--ts-ui-sidebar-button-hover-text-color, var(--uikit-text-new-primary));\n  --ui-sidebar-button-active-bg-color: var(--ts-ui-sidebar-button-active-bg-color, var(--uikit-bg-c-default-soft));\n  --ui-sidebar-button-active-text-color: var(--ts-ui-sidebar-button-active-text-color, var(--uikit-text-new-primary));\n  --ui-sidebar-button-focus-bg-color: var(--ts-ui-sidebar-button-focus-bg-color, var(--uikit-bg-c-default-pressed));\n  --ui-sidebar-button-focus-text-color: var(--ts-ui-sidebar-button-focus-text-color, var(--uikit-text-new-primary));\n  --ui-left-bar-background-color: var(--ui-sidebar-bg-color);\n  --ui-hamburger-section-name-color: var(--ts-ui-hamburger-section-name-color, #757575);\n  --ui-hamburger-section-chevron-color: var(--ts-ui-hamburger-section-chevron-color, #757575);\n  --ui-hamburger-close-button-bg-color: var(--ts-ui-hamburger-close-button-bg-color, var(--uikit-bg-c-default-soft));\n  --ui-hamburger-close-button-text-color: var(--ts-ui-hamburger-close-button-text-color, var(--uikit-text-new-primary));\n  --ui-hamburger-close-button-hover-bg-color: var(\n    --ts-ui-hamburger-close-button-hover-bg-color,\n    var(--uikit-bg-c-default-pressed)\n  );\n  --ui-hamburger-close-button-hover-text-color: var(\n    --ts-ui-hamburger-close-button-hover-text-color,\n    var(--uikit-text-on-c-default)\n  );\n  --ui-hamburger-close-button-focus-bg-color: var(--ts-ui-hamburger-close-button-focus-bg-color);\n  --ui-hamburger-close-button-focus-text-color: var(--ts-ui-hamburger-close-button-focus-text-color);\n  --ui-hamburger-close-button-active-bg-color: var(\n    --ts-ui-hamburger-close-button-active-bg-color,\n    var(--uikit-bg-c-default-soft)\n  );\n  --ui-hamburger-close-button-active-text-color: var(\n    --ts-ui-hamburger-close-button-active-text-color,\n    var(--uikit-text-new-primary)\n  );\n  --ui-hamburger-open-button-in-sidebar-bg-color: var(--ts-ui-hamburger-open-button-in-sidebar-bg-color, transparent);\n  --ui-hamburger-open-button-in-sidebar-text-color: var(\n    --ts-ui-hamburger-open-button-in-sidebar-text-color,\n    var(--uikit-text-new-primary)\n  );\n  --ui-hamburger-open-button-in-sidebar-hover-bg-color: var(\n    --ts-ui-hamburger-open-button-in-sidebar-hover-bg-color,\n    var(--uikit-bg-c-default-soft)\n  );\n  --ui-hamburger-open-button-in-sidebar-hover-text-color: var(\n    --ts-ui-hamburger-open-button-in-sidebar-hover-text-color,\n    var(--uikit-text-new-primary)\n  );\n  --ui-hamburger-open-button-in-sidebar-focus-bg-color: var(--ts-ui-hamburger-open-button-in-sidebar-focus-bg-color);\n  --ui-hamburger-open-button-in-sidebar-focus-text-color: var(\n    --ts-ui-hamburger-open-button-in-sidebar-focus-text-color\n  );\n  --ui-hamburger-open-button-in-sidebar-active-bg-color: var(\n    --ts-ui-hamburger-open-button-in-sidebar-active-bg-color,\n    var(--uikit-bg-c-default-pressed)\n  );\n  --ui-hamburger-open-button-in-sidebar-active-text-color: var(\n    --ts-ui-hamburger-open-button-in-sidebar-active-text-color,\n    var(--uikit-text-new-primary)\n  );\n  --ui-hamburger-header-border-bottom-color: var(\n    --ts-ui-hamburger-header-border-bottom-color,\n    var(--uikit-bg-c-default-soft)\n  );\n}\nbody .sidebar-panel,\nbody .hamburger-item-component,\nbody .stencil-left-sidebar {\n  --ui-button-state-hover: var(--ui-sidebar-button-hover-bg-color);\n  --ui-button-state-focus: var(--ui-sidebar-button-focus-bg-color);\n  --ui-button-state-active: var(--ui-sidebar-button-active-bg-color);\n  --ui-button-state-hover-text-color: var(--ui-sidebar-button-hover-text-color);\n  --ui-button-state-focus-text-color: var(--ui-sidebar-button-focus-text-color);\n  --ui-button-state-active-text-color: var(--ui-sidebar-button-active-text-color);\n}\n\nbody.acronis-theme {\n  background: var(--uikit-surface-new-10);\n  --ui-breadcrumb-link-color: var(--uikit-bg-c-brand-primary);\n  --ui-breadcrumb-seperator-color: var(--uikit-bg-c-brand-primary);\n  --ui-breadcrumb-seperator-opacity: 0.3;\n  --ui-breadcrumb-link-decoration: none;\n}\nbody.acronis-theme .header-nav {\n  --ui-breadcrumb-link-active-color: var(--uikit-decoration-new-red-70);\n}\nbody.acronis-theme .bottom-sheet-content {\n  --ui-button-state-hover: var(--uikit-bg-c-default-soft-hovered);\n  --ui-button-state-focus: var(--uikit-bg-c-default-soft-pressed);\n  --ui-button-state-active: var(--uikit-text-new-graphics-only);\n}\nbody.acronis-theme #hamburger-header {\n  --ui-hamburger-header-flex-direction: row-reverse;\n}\n\n.PlatformLayout-module_layout__dmvPy {\n  --layout-header-height: var(--l-header-height, 60px);\n  --layout-sidebar-width: var(--l-sidebar-width, 60px);\n  --layout-inline-gap: var(--l-side-gap, var(--size-s));\n  --layout-border-color: var(--l-border-color, rgba(65, 65, 65, 0.1));\n  --layout-header-color: var(--l-background-color, #fff);\n  display: grid;\n  min-height: 100vh;\n  position: relative;\n  grid-template-columns: 1fr;\n  grid-template-rows: var(--layout-header-height) 1fr;\n  grid-template-areas: \"header\" \"main\" \"footer\";\n}\n@media (max-width: 1278px) {\n  .PlatformLayout-module_layout__dmvPy {\n    min-height: -webkit-fill-available;\n  }\n}\n@media (min-width: 1280px) {\n  .PlatformLayout-module_layout__dmvPy.PlatformLayout-module_withSidebar__cP7qF {\n    grid-template-columns: var(--layout-sidebar-width) 1fr;\n    grid-template-rows: var(--layout-header-height) 1fr;\n    grid-template-areas: \"header header\" \"sidebar main\" \"sidebar footer\";\n  }\n}\n\n.PlatformLayout-module_main__Lgi1- {\n  grid-area: main;\n}";
var styles$1 = {"layout":"PlatformLayout-module_layout__dmvPy","withSidebar":"PlatformLayout-module_withSidebar__cP7qF","main":"PlatformLayout-module_main__Lgi1-"};
styleInject(css_248z$1);

const ThemePlugin = () => {
    const colorScheme = useBrandingSettings('colorScheme');
    return jsx(Theme, { colorScheme: colorScheme || 'default' });
};

const PublicThemePlugin = () => {
    const colorScheme = useBrandingSettings('colorScheme', 'public');
    return jsx(Theme, { colorScheme: colorScheme || 'default' });
};

const root = document.documentElement;
const useSetCustomBrandingColors = (accessMode) => {
    const customColors = useBrandingSettings('customColors', accessMode);
    useEffect(() => {
        if (!customColors) {
            return;
        }
        Object.entries(customColors).forEach(([key, value]) => {
            root.style.setProperty(key, value);
        });
    }, [customColors]);
};

const DEFAULT_ACCESS_MODE = 'logged';
const SetCustomBrandingColorsPlugin = ({ accessMode = DEFAULT_ACCESS_MODE, }) => {
    useSetCustomBrandingColors(accessMode);
    return null;
};

var css_248z = "/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `typography` mixin instead\n */\n/**\n * @deprecated\n * use `theme` mixin instead\n */\n\n.NoAccess-module_noAccessWrapper__mj7dn {\n  height: 100%;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n  flex-direction: column;\n}";
var styles = {"noAccessWrapper":"NoAccess-module_noAccessWrapper__mj7dn"};
styleInject(css_248z);

var img = "data:image/png;base64,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";

// You have no access. Please contact the administrator.
const NoAccess = () => {
    const { t } = useTranslation();
    return (jsx("div", Object.assign({ className: styles.noAccessWrapper }, { children: jsx(AccessGuard, { image: jsx("img", { className: styles.img, src: img, alt: "access-guard" }), text: t('noAccessText'), title: "403", testId: "navigation-403" }) })));
};

const PlatformLayout = ({ children, userMenu, chatBottomSheetTitle, }) => {
    const { isNavOpen, footerSlot, tenantSettingsLoading, colorScheme, shouldShowAccessGuard, userLoading } = useNavigationContext();
    const sidebarSlugs = useSidebarSlugs();
    const isPanelOpen = useSelector(selectIsHamburgerOpen);
    useLockBodyScroll(isNavOpen || isPanelOpen);
    if (tenantSettingsLoading || userLoading) {
        return jsx(SplashLoading, {});
    }
    return (jsx(ProvideBreakpoints, { children: jsx(ProctoredProvider, { children: jsxs(ChatProvider, Object.assign({ bottomSheetTitle: chatBottomSheetTitle }, { children: [jsx(ImpersonationListener, {}), jsx(Theme, { colorScheme: colorScheme }), jsx(SetCustomBrandingColorsPlugin, {}), jsxs("div", Object.assign({ className: cn(styles$1.layout, !!sidebarSlugs && styles$1.withSidebar) }, { children: [jsx(Headline, { userMenu: userMenu }), jsx(SidebarPanel, {}), jsx("main", Object.assign({ className: styles$1.main }, { children: shouldShowAccessGuard ? jsx(NoAccess, {}) : children })), jsx(HamburgerPanel, {}), footerSlot] }))] })) }) }));
};

const PortalSidebarPlugin = () => {
    return (jsx(SidebarSlotPlugin, { children: jsx(SidebarPanel, {}) }));
};

const { ToggleButton } = ToggleButtonGroup;
const isItemWithDropdown = (props) => {
    return props.variant === 'dropdown';
};
const SideBarItemComponent = (_a) => {
    var { title, iconId, active, onClick, badge } = _a, props = __rest(_a, ["title", "iconId", "active", "onClick", "badge"]);
    const { fromDesktop } = useBreakpoints();
    if (fromDesktop) {
        const element = (jsx(SidebarButtonComponent, { iconId: iconId, name: title, withHint: props.variant !== 'dropdown', isActive: active, onClick: onClick, badge: badge }));
        if (isItemWithDropdown(props)) {
            return (jsx(SidebarMenu, Object.assign({ items: props.items, onItemClick: props.onItemClick }, { children: element })));
        }
        return element;
    }
    return (jsx(ToggleButton, Object.assign({ "aria-label": title, value: "", selected: active, onClick: onClick }, { children: jsxs(Fragment, { children: [jsx(SvgComponent, { name: iconId, size: "sm" }), " ", badge && jsx(SidebarComponentComponentBadge, { badge: badge })] }) })));
};

const selectLastBreadcrumbs = createDraftSafeSelector(selectBreadcrumbsRoot, (state) => {
    var _a;
    return (_a = state.collection[state.collection.length - 1]) !== null && _a !== void 0 ? _a : null;
});

const ChatPlugin = () => {
    const HeaderChatPortal = usePortal(PORTAL_HEADER_CHAT);
    return jsx(HeaderChatPortal, { children: useIsChatEnabled() && jsx(Chat, {}) });
};

const ImpersonationPlugin = ({ onSignOut, user }) => {
    const ImpersonationPortal = usePortal(PORTAL_IMPERSONATION, document.body);
    return (jsx(ImpersonationPortal, { children: jsx(ImpersonationComponent, { insidePortal: true, onSignOut: onSignOut, user: user }) }));
};

export { ACTION_LOGOUT, ActionsPlugin, ActionsSlotPlugin, AvatarPlugin, BottomToolbarComponent, BottomToolbarPlugin, BreadcrumbItemComponent, BreadcrumbsNarrow, BreadcrumbsPlugin, BreadcrumbsWide, Chat, ChatContext, ChatPlugin, ChatProvider, ChatProviderInner, ChatWindow, ChatWindowIframe, ContentHeaderComponent, ContentHeaderPlugin, ContentHeaderRightSlotPlugin, DropdownItemComponent, HamburgerItemComponent, HamburgerPlugin, HamburgerSectionHeader, HamburgerTenantLinks, HeaderComponent, HeaderMenuRightSlotPlugin, HeaderPlugin, ImpersonationFloatingButton, ImpersonationPlugin, LOCALE_DEFAULT, LOCALE_LABELS, LanguageSubMenuItem, LanguageSwitcherPlugin, LicenseGuard, Logo, LogoComponent, LogoSlotPlugin, NavigationContext, PUBLIC_TENANT_SETTINGS_ENDPOINT, PlatformContextProvider, PlatformLayout, PortalHamburgerPlugin, PortalLogoPlugin, PortalSidebarPlugin, ProctoredContext, ProctoredProvider, ProvideNavigationContext, PublicTenantLogo, PublicThemePlugin, RenderLogo, SetCustomBrandingColorsPlugin, SideBarComponent, SideBarItemComponent, SideBarPlugin, SidebarButtonComponent, SplashLoading, Stencil, StencilComponent, TENANT_COMMON_NAVIGATION, TENANT_LOCALE, TENANT_SETTINGS_ENDPOINT, TENANT_SUPPORTED_LOCALES, TOCPlugin, TOCWideComponent, TenantLogo, ThemePlugin, UnauthUserMenuPlugin, UserMenuBottomSheetSlotPlugin, UserMenuComponent, UserMenuPlugin, UserMenuSlotPlugin, clearAllStorage, getCurrentDomain, getPublicTenantSettingsMap, getTenantSettingsMap, licenseApi, navigationPlatformApi, navigationReducer, navigationSlice, navigationStore, navigationStructureApi, platformUserApi, popActions, popBreadcrumbs, popSidebar, publicTenantSettingsApi, pushActions, pushBreadcrumbs, pushSidebar, selectActionsLimit, selectBreadcrumbsItemsIndex, selectBreadcrumbsSlugs, selectCurrentLocale, selectIsHamburgerOpen, selectIsPublicSettingsGivesNotFound, selectLastActions, selectLastBreadcrumbs, selectLastSidebar, selectLicensedFeatures, selectMixedIndex, selectPublicTenantSettingsMap, selectSaturatedBreadcrumbsItems, selectSaturatedTree, selectTenantCommonNavigation, selectTenantDefaultLocale, selectTenantLocales, selectTenantSettingsMap, selectTreeFirstNode, selectUserPermissions, selectUsersMe, setActions, setActionsLimit, setActiveSlug, setBreadcrumbs, setHamburgerOpen, setMode, setNavOpen, setSidebar, tenantSettingsApi, useBrandingSettings, useChatContext, useCommonTenantLinks, useGetLicenseFeaturesQuery, useGetNavigationStructureQuery, useGetPublicTenantSettingsQuery, useGetTenantSettingsQuery, useGetUsersMeQuery, useGetUsersPermissionsQuery, useHasLicensedFeature, useIsChatEnabled, useLazyGetNavigationStructureQuery, useLazyGetPublicTenantSettingsQuery, useLazyGetTenantSettingsQuery, useLazyGetUsersMeQuery, useLazyGetUsersPermissionsQuery, useLocaleContext, useNavigationContext, usePlatformContext, useProctoredContext, usePublicTenantSettings, useSetActiveSlug, useSubEvent, useTenantSettings, userPermissionsApi };
